import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { CardHoverPreview, canPlayCard } from "@/components/game/ManagerCardHand";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { X, Users, List, Wifi, WifiOff, Loader2 } from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { useGameSocket } from "@/hooks/useGameSocket";
import { resolveActiveModifiers } from "@/utils/activeModifiers";
import { deriveGynIntel } from "../utils/gynIntel";
import { checkInvariants } from "../utils/stateInvariants";
import { derivePitcherCondition } from "../utils/pitcherCondition";
import { shouldReleaseLogEntry } from "@/utils/playLogUtils";

import {
  Scoreboard,
  DiamondField,
  PhaseNav,
  PitchingPhase,
  FieldingPhase,
  RosterPanel,
  VictoryModal,
  PitcherSubDialog,
  BatterSubDialog,
  PlayerDetailsDialog,
  GameStatsModal,
  isGameOver,
  getBatterIndex,
  getBench,
} from "@/components/game";
import HeatMapZone from "@/components/game/HeatMapZone";
import PlayByPlayDrawer from "@/components/game/PlayByPlayDrawer";
import HRBallEffect from "@/components/game/HRBallEffect";
import LightsOutEffect from "@/components/game/LightsOutEffect";
import StreakerEffect from "@/components/game/StreakerEffect";
import GameErrorBoundary from "@/components/game/GameErrorBoundary";
import RundownModal from "@/components/game/RundownModal";
import SwapSpitModal from "@/components/game/SwapSpitModal";
import HiddenBallModal from "@/components/game/HiddenBallModal";
import StreakerInteractiveModal from "@/components/game/StreakerInteractiveModal";
import HalfInningBreakModal from "@/components/game/HalfInningBreakModal";
import BluffValuesModal from "@/components/game/BluffValuesModal";
import StudentOfGameModal from "@/components/game/StudentOfGameModal";
import ManagersDecisionModal from "@/components/game/ManagersDecisionModal";
import StealingSignalsModal from "@/components/game/StealingSignalsModal";
import SubterfugeModal from "@/components/game/SubterfugeModal";
import GameNotificationStack from "@/components/game/GameNotificationStack";
import { CARD_BANNER_MAP } from "@/constants/cardBannerCategories";
import { track } from "@/utils/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Overlay auto-dismiss delays (ms) ─────────────────────────────────────────
const RUNDOWN_CLEAR_MS              = 2200;
// Phase watchdog: if a resolution object stays set this long without clearing,
// request a full server resync to unstick the UI (animation bug / WS drop).
const PHASE_WATCHDOG_MS             = 25000;
// Victory modal: normal delay lets the runner trot animation finish;
// fallback fires if the resolution animation never clears (e.g. mid-play disconnect).
const VICTORY_MODAL_DELAY_MS        = 1500;
const VICTORY_MODAL_FALLBACK_MS     = 6000;

// Pure stat helpers — module-scope so their identity is stable across GameBoard renders.
// This keeps the WS onMessage closure (which references applyChipsDelta / applyPlayDelta)
// out of react-hooks/exhaustive-deps territory.
const clonePlayer = (p) => ({ ...p });
const initPitcher = () => ({ k: 0, bb: 0, h: 0, strikes: 0, balls: 0, outs: 0, runsAllowed: 0 });
const initBatter = () => ({ k: 0, bb: 0, h: 0, ab: 0, singles: 0, doubles: 0, triples: 0, hr: 0, rbi: 0, sb: 0 });

function applyChipsDelta(prev, pitcherId, batterId, chips, pitcherChip = null, batterChip = null) {
  const pMgr = chips?.pMgr || 0, pChip = chips?.pChip || 0;
  const bMgr = chips?.bMgr || 0, bChip = chips?.bChip || 0;
  const updated = {
    ...prev,
    pitcherMgr: prev.pitcherMgr + pMgr,
    pitcherChips: prev.pitcherChips + pChip,
    batterMgr: prev.batterMgr + bMgr,
    batterChips: prev.batterChips + bChip,
  };
  const players = { ...updated.players };
  // Pitcher: tally mgr/chip usage AND the pitch type thrown (FB/CH/CB/SL = chip 1-4).
  // pitchTypes counts are tallied for every pitch this pitcher threw, regardless of
  // whether they used a mod / chip — that makes pitchTypes a true "pitch mix" stat.
  if (pitcherId && (pMgr || pChip || pitcherChip != null)) {
    const p = { ...(players[pitcherId] || initPitcher()) };
    p.mgrUsed = (p.mgrUsed || 0) + pMgr;
    p.chipsUsed = (p.chipsUsed || 0) + pChip;
    if (pitcherChip != null) {
      const pitchTypes = { ...(p.pitchTypes || {}) };
      pitchTypes[pitcherChip] = (pitchTypes[pitcherChip] || 0) + 1;
      p.pitchTypes = pitchTypes;
    }
    players[pitcherId] = p;
  }
  // Batter: same idea — tally each chip the batter GUESSED as a per-type column.
  // Useful for spotting tendencies (e.g. always guesses fastball).
  if (batterId && (bMgr || bChip || batterChip != null)) {
    const b = { ...(players[batterId] || initBatter()) };
    b.mgrUsed = (b.mgrUsed || 0) + bMgr;
    b.chipsUsed = (b.chipsUsed || 0) + bChip;
    if (batterChip != null) {
      const guessTypes = { ...(b.guessTypes || {}) };
      guessTypes[batterChip] = (guessTypes[batterChip] || 0) + 1;
      b.guessTypes = guessTypes;
    }
    players[batterId] = b;
  }
  updated.players = players;
  return updated;
}

function applyPlayDelta(prev, pitcherId, batterId, result) {
  if (!result) return prev;
  const players = { ...prev.players };
  const p = clonePlayer(players[pitcherId] || initPitcher());
  const b = clonePlayer(players[batterId] || initBatter());
  if (result.type === 'strikeout') {
    p.k += 1; p.outs += 1;
    b.k += 1; b.ab += 1;
  } else if (result.type === 'walk') {
    p.bb += 1; b.bb += 1;
    if (result.rbi) { b.rbi += result.rbi; p.runsAllowed += result.rbi; }
  } else if (result.type === 'hit') {
    p.h += 1; b.h += 1; b.ab += 1;
    if (result.hitType === 'single') b.singles += 1;
    else if (result.hitType === 'double') b.doubles += 1;
    else if (result.hitType === 'triple') b.triples += 1;
    else if (result.hitType === 'hr') b.hr += 1;
    if (result.rbi) { b.rbi += result.rbi; p.runsAllowed += result.rbi; }
  } else if (result.type === 'out') {
    b.ab += 1; p.outs += (result.outs || 1);
  } else if (result.type === 'steal') {
    b.sb = (b.sb || 0) + 1;
  } else if (result.type === 'wild_pitch') {
    if (result.runs) p.runsAllowed += result.runs;
  } else if (result.type === 'hbp') {
    p.bb += 1; b.bb += 1;
    if (result.rbi) { b.rbi += result.rbi; p.runsAllowed += result.rbi; }
  }
  players[pitcherId] = p;
  players[batterId] = b;
  return { ...prev, players };
}


// Small tag row shown below a card's description in the hand-refresh modals.
// Displays modifier values, stack ceiling, phase restriction, and persistence.
function CardMeta({ card }) {
  if (!card) return null;
  const tags = [];

  if (card.pitcher_modifier) {
    const sign = card.pitcher_modifier > 0 ? "+" : "";
    tags.push({ text: `${sign}${card.pitcher_modifier} Pitcher`, key: "pm" });
  }
  if (card.batter_modifier) {
    const sign = card.batter_modifier > 0 ? "+" : "";
    tags.push({ text: `${sign}${card.batter_modifier} Batter`, key: "bm" });
  }
  if (card.fielding_modifier) {
    const sign = card.fielding_modifier > 0 ? "+" : "";
    tags.push({ text: `${sign}${card.fielding_modifier} Field`, key: "fm" });
  }
  if (card.max != null && card.max > 0) tags.push({ text: `Max +${card.max}`, key: "max" });
  if (card.persistent) tags.push({ text: "Persistent", key: "pers" });
  // Only show phase when it's something other than the default (pitch)
  if (card.phase && card.phase !== "any" && card.phase !== "pitch") {
    const label = { fielding: "Fielding phase", steal: "Steal phase", batting: "Batting phase" }[card.phase] ?? card.phase;
    tags.push({ text: label, key: "ph" });
  }

  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1 mt-1">
      {tags.map(t => (
        <span key={t.key} className="text-[8px] font-bold uppercase tracking-wider px-1.5 py-px rounded bg-slate-700/60 border border-slate-600/40 text-slate-400">{t.text}</span>
      ))}
    </div>
  );
}

function BaserunnersPanel({ game }) {
  const { state } = game;
  const bases = state.bases || {};
  const battingTeamKey = state.half === "top" ? "away" : "home";
  const battingTeam = game[`${battingTeamKey}_team`];
  const slots = [
    { label: "3rd", runner: bases.third },
    { label: "2nd", runner: bases.second },
    { label: "1st", runner: bases.first },
  ];
  const runnerCount = slots.filter(s => s.runner).length;
  return (
    <div className="h-full flex flex-col gap-2 px-3 py-2">
      <div className="flex items-center justify-between flex-shrink-0">
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-500">On Base</p>
        {battingTeam && <span className="text-[9px] text-slate-600">{battingTeam.name}</span>}
      </div>
      <div className="flex flex-col gap-1.5 flex-1 overflow-y-auto">
        {slots.map(({ label, runner }) => (
          <div key={label} className={`flex items-center gap-2 px-2.5 py-1.5 rounded-lg border transition-all ${
            runner ? "border-amber-500/30 bg-amber-500/5" : "border-slate-800/40 opacity-30"
          }`}>
            <span className={`text-[11px] font-black w-6 text-center flex-shrink-0 ${runner ? "text-amber-400" : "text-slate-600"}`}>
              {label}
            </span>
            <div className="w-px h-3 bg-slate-700 flex-shrink-0" />
            {runner
              ? <span className="text-[11px] font-bold text-white truncate">{runner.name}</span>
              : <span className="text-[10px] text-slate-700 italic">empty</span>
            }
          </div>
        ))}
      </div>
      <p className="text-[9px] text-slate-600 text-center flex-shrink-0">
        {runnerCount === 1 ? "1 runner on base" : `${runnerCount} runners on base`}
      </p>
    </div>
  );
}

// Build a structured pitch record from a raw `pitch_reveal` frame.
//
// Module-level and shared on purpose: this is used both live and when replaying the
// server's reconnect buffer, and the two must produce identical records — the array
// is the evidence base an agent (or a human) rebuilds their Got Your Number model
// from, so a drift between the paths would corrupt the opponent model silently.
function buildPitchRecord(msg) {
  // A taken pitch never runs the chip game, so the server's chip-value fields are
  // left at their 0 default. 0 is NOT a legal Got Your Number value — the sheet is
  // 1/1/2/3 — so ingesting it writes a impossible value into that pitcher's sheet
  // and corrupts the opponent model. A tester hit exactly this.
  //
  // Emit explicit nulls plus a `batterAction` sentinel instead, and never `undefined`
  // (which does not survive a JSON round-trip).
  const took = !!msg.called_ball;
  const orNull = (v) => (v === undefined ? null : v);
  return {
    batterAction: took ? "take" : "swing",
    chipsApplied: !took,

  resolutionId: msg.resolution_id,
  inning: msg.context?.inning ?? null,
  half: msg.context?.half ?? null,
  outs: msg.context?.outs ?? null,
  balls: msg.context?.balls ?? null,
  strikes: msg.context?.strikes ?? null,
  calledBall: !!msg.called_ball,
  pitcherChip: orNull(msg.pitcher_chip ?? null),
  batterChip: orNull(msg.batter_chip ?? null),
  chipMatch: msg.pitcher_chip != null && msg.pitcher_chip === msg.batter_chip,
  chipWinner: orNull(msg.chip_winner ?? null),
  chipBonus: msg.chip_bonus ?? 0,
  // Only ever populated for chips this client is entitled to see.
  // Each side's chip is valued on its OWN current pitcher's secret sheet —
  // by design. The *Source ids say which pitcher, so this is never ambiguous.
  pitcherChipValue: took ? null : (msg.pitcher_chip_value ?? null),
  pitcherChipValueSource: msg.pitcher_chip_value_source ?? null,
  batterChipValue: took ? null : (msg.batter_chip_value ?? null),
  batterChipValueSource: msg.batter_chip_value_source ?? null,
  pitcherRoll: msg.pitcher_d10 ?? null,
  batterRoll: msg.batter_d10 ?? null,
  // Card modifiers kept SEPARATE from the chip bonus on purpose — the prose
  // log folds them together, which made two testers read the wrong chip value
  // off the same line and derive opposite rules for the core mechanic.
  pitcherCardMod: msg.pitcher_card_mod ?? 0,
  batterCardMod: msg.batter_card_mod ?? 0,
  pitcherModBreakdown: msg.pitcher_mod_breakdown ?? null,
  batterModBreakdown: msg.batter_mod_breakdown ?? null,
  pitcherTotal: msg.pitcher_total ?? null,
  batterTotal: msg.batter_total ?? null,
  result: msg.result_type ?? null,
  // result_type stays "in_play" for a home run because that value routes the
  // fielding phase; this is the explicit label for anything counting HRs.
  isHomeRun: !!msg.is_home_run,
  contact: msg.contact ?? 0,
  };
}

export default function GameBoard() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const myRole = searchParams.get("role"); // "home" | "away" | null (null = local/demo)
  const isSpectate = searchParams.get("spectate") === "true";

  const [game, setGame] = useState(null);
  // Phase 6: confirmation modal for deactivating a persistent active card.
  // null = closed; otherwise { id, name } for the card pending deactivation.
  const [deactivateConfirm, setDeactivateConfirm] = useState(null);
  // Banner active-card hover preview ({ card, rect } or null). Reuses the same
  // CardHoverPreview component the hand uses so the popover styling matches.
  const [bannerHover, setBannerHover] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPhase, setCurrentPhase] = useState("pitch");
  // Server-authoritative lock-in status. Each consumer adopts whatever is already
  // here when it MOUNTS without applying it, and only acts on messages that arrive
  // while it is mounted — a phase_sync describes the play that was live when it was
  // built, and FieldingPhase remounts on every ball in play.
  const [phaseSync, setPhaseSync] = useState(null);
  // FieldingPhase's own sub-phase ("select" = owes a roll, "waiting" = submitted).
  const [fieldingSubPhase, setFieldingSubPhase] = useState("select");
  // Machine-readable pitch-by-pitch history for window.__JUNKBALL_STATE__.
  // Kept separate from playLog because that one is display prose.
  //
  // Persisted like playLog: this array is the ENTIRE evidence base for Got Your
  // Number — the only place chip values and their source pitcher ids live. A
  // reconnect used to drop it to [] with the board state intact, which silently
  // degraded that seat to blind guessing behind a completely normal-looking UI.
  const pitchKey = `junkball_pitchHistory_${gameId}`;
  const [pitchHistory, setPitchHistory] = useState(() => {
    try { const v = localStorage.getItem(pitchKey); return v ? JSON.parse(v) : []; }
    catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(pitchKey, JSON.stringify(pitchHistory)); } catch {}
  }, [pitchHistory, pitchKey]);
  // Most recent refused action, machine-readable. Rejections were toast-only, so
  // an automated seat whose action was dropped had no way to see that it had been
  // dropped — a card click that vanished on a remount resolved the pitch with a
  // smaller modifier and nothing anywhere said why. Published on the state blob
  // so a driver can detect the refusal and retry.
  const [lastRejection, setLastRejection] = useState(null);
  const [resyncing, setResyncing] = useState(false);
  const [pitchResult, setPitchResult] = useState(null);
  const [activePositions, setActivePositions] = useState([]);

  const [mobileRosterOpen, setMobileRosterOpen] = useState(false);
  const [mobileLogOpen, setMobileLogOpen] = useState(false);
  // Lock body scroll while either mobile sheet is open. Without this the page behind
  // the fixed-inset sheet stays scrollable, so on iOS / touch the user can scroll both
  // layers at once — visually confusing. Restore on close.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const anyOpen = mobileRosterOpen || mobileLogOpen;
    if (!anyOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [mobileRosterOpen, mobileLogOpen]);

  // Card play rejection state — passed to PitchingPhase so it can clear the card selection
  const [rejectedCardId, setRejectedCardId] = useState(null);

  // Online 1v1 state
  const [onlinePitcherLocked, setOnlinePitcherLocked] = useState(null); // { cards_count }
  const [onlinePitcherReady, setOnlinePitcherReady] = useState(null);   // { cards_count, started_at, duration } — PvP: pitcher committed
  const [onlinePitchReveal, setOnlinePitchReveal] = useState(null);      // { pitcher_chip, batter_chip, dice, result_type, ... }
  const [onlineFieldingLocked, setOnlineFieldingLocked] = useState(null); // { role, cards_count }
  const [onlineFieldingResolution, setOnlineFieldingResolution] = useState(null); // { field_d6, outcome, ... }
  // Steal state — declared by batting team via runner-tile dropdown.
  // active: { base, next_base, runner, batting_role, fielding_role } while a steal is in flight
  // opponentLocked: opponent has submitted their cards
  // resolution: server-broadcast outcome (safe / out + dice + cards)
  const [onlineStealActive, setOnlineStealActive] = useState(null);
  const [onlineStealOpponentLocked, setOnlineStealOpponentLocked] = useState(null);
  const [onlineStealResolution, setOnlineStealResolution] = useState(null);
  const [rundownState, setRundownState] = useState(null);
  const [swapSpitOpen, setSwapSpitOpen] = useState(false);
  const [swapSpitProgress, setSwapSpitProgress] = useState([]);
  const [swapSpitClock, setSwapSpitClock] = useState(null);  // { started_at, duration } | null
  // Streak Yourself mini-game state — open when a streaker_interactive_open
  // event arrives; cleared on streaker_interactive_complete or unmount.
  const [streakerInteractive, setStreakerInteractive] = useState(null); // { initiator, spawn_edge, exit_edge, seed, started_at, duration }
  const [studentOfGameModal, setStudentOfGameModal] = useState(null); // { drawnCards: [id, id] } | null
  const [managersDecisionModal, setManagersDecisionModal] = useState(null); // { cards: [{id, name}] } | null
  const [stealingSignalsModal, setStealingSignalsModal] = useState(null); // { phase: "pick"|"decide", cardCount, revealedCard } | null
  const [subterfugeModal, setSubterfugeModal] = useState(null); // { availableCardIds: [], triggeredBy } | null
  const [hiddenBallPending, setHiddenBallPending] = useState(null); // { occupiedBases: [] }
  // pickoff_select event: { card_id, card_name, threshold, fielding_role, runners_on }
  // Shown to both clients during the multi-runner target-selection step (before the roll).
  const [onlinePickoffSelect, setOnlinePickoffSelect] = useState(null);
  // pickoff_roll event: { roll, threshold, result, card_name, fielding_role, runners_on, target_base }
  // Drives the pick-off dice animation in PitchingPhase.
  const [onlinePickoffRoll, setOnlinePickoffRoll] = useState(null);
  // Safety auto-clear: PitchingPhase normally clears onlinePickoffRoll after a 2s
  // animation, but its timer is gated on pvpPhase === "pickoff_roll" — any phase
  // transition (half-flip, hand-refresh, fielding) cancels it and leaves the red
  // pickoff-target highlight stuck on the runner. This belt-and-suspenders clear
  // fires regardless of what PitchingPhase is doing. Slightly longer than the
  // inner 2s so the normal path wins when it can.
  useEffect(() => {
    if (!onlinePickoffRoll) return;
    const cardId = onlinePickoffRoll.card_id;
    const timer = setTimeout(() => {
      setOnlinePickoffRoll(null);
      if (cardId) setRecentInstantCards(prev => prev.filter(c => c.id !== cardId));
    }, 2500);
    return () => clearTimeout(timer);
  }, [onlinePickoffRoll]);
  const [digItOutOptions, setDigItOutOptions] = useState(null); // { options: [{id, name}] } | null
  const [digItOutTimer, setDigItOutTimer] = useState(null);
  // Track whether the server has confirmed pending_dig_it_out was active. Without this,
  // the auto-close effect fires immediately when the modal opens (digItOutOptions just set)
  // but before the game_state broadcast carrying pending_dig_it_out arrives — causing the
  // modal to close before the player ever sees it.
  const _digPendingSeenRef = useRef(false);
  // Auto-close the dig-it-out modal when the server has resolved it (e.g., a bot
  // handled the pick in bot-vs-bot mode, or via a server-side cleanup). Watches
  // game.state.pending_dig_it_out — if it's gone, the modal should close too.
  useEffect(() => {
    if (!digItOutOptions) { _digPendingSeenRef.current = false; return; }
    const pending = game?.state?.pending_dig_it_out;
    if (pending?.for_role) { _digPendingSeenRef.current = true; return; }
    // Only close once we've confirmed pending was non-null; avoids closing on the first
    // render where game_state with pending_dig_it_out hasn't arrived yet.
    if (_digPendingSeenRef.current) setDigItOutOptions(null);
  }, [game?.state?.pending_dig_it_out, digItOutOptions]);
  // 20-second countdown for dig-it-out; auto-selects first available card on expiry.
  useEffect(() => {
    if (!digItOutOptions) { setDigItOutTimer(null); return; }
    const opts = digItOutOptions;
    setDigItOutTimer(20);
    let fired = false;
    const id = setInterval(() => {
      setDigItOutTimer(prev => {
        if (prev !== null && prev <= 1) {
          if (!fired) {
            fired = true;
            if (opts?.options?.length > 0) {
              const pick = opts.options[Math.floor(Math.random() * opts.options.length)];
              wsSendRef.current?.({ type: "dig_it_out_select", card_id: pick.id });
            }
            setDigItOutOptions(null);
          }
          return 0;
        }
        return prev !== null ? prev - 1 : null;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [digItOutOptions]);
  // HR ball cinematic — `hrTriggerKey` increments to remount HRBallEffect for each HR.
  // The phase components (PitchingPhase auto-HR, FieldingPhase fielded-HR) fire onHrShow
  // with a `power` value (auto-HR contact value, or fielded diff) so the cinematic's
  // distance counter scales — barely-cleared HRs roll the low end of the band, monster
  // swings flatten toward the top.
  const [hrTriggerKey, setHrTriggerKey] = useState(0);
  const [hrPower, setHrPower] = useState(null);
  // Incremented on every server phase_reset so PitchingPhase/FieldingPhase remount
  // with clean internal state (pvpPhase, animation refs, etc.).
  const [phaseResetCounter, setPhaseResetCounter] = useState(0);
  // Incremented whenever the server rejects our pitch_lock_in or reconnect reveals we
  // are not in pending_pitches — triggers PitchingPhase to reset pvpPhase back to
  // batter_select/pitch_select so the player can retry without waiting for the 32s backup timer.
  const [pitchLockInRejectedSeq, setPitchLockInRejectedSeq] = useState(0);
  // Heat Map center-area state: synced from PitchingPhase so GameBoard can render
  // HeatMapZone in the field-area instead of DiamondField during pitch phases.
  const [pitchPvpPhase, setPitchPvpPhase] = useState("pitch_select");
  const [heatMapChip, setHeatMapChip] = useState(null);
  const [heatMapPos, setHeatMapPos] = useState(null);
  // "zone" = strike zone panel (default), "field" = diamond view. Resets each pitch.
  const [heatMapView, setHeatMapView] = useState("zone");
  // True when the field view was triggered automatically (runner anim / steal / pickoff).
  // Lets us auto-restore to zone when the trigger clears, while respecting manual toggles.
  const heatMapAutoFieldRef = useRef(false);
  // Incremented each time the user finalizes a chip placement (pointer-up / tile drop).
  // PitchingPhase watches this to auto-fire pitch_lock_in without a separate button click.
  const [heatMapDropSeq, setHeatMapDropSeq] = useState(0);
  // True from when we detect an HR pitch_reveal until the runner trot animation settles.
  // Keeps the heat map hidden during the full runner circuit even after onlinePitchReveal
  // clears (which releases holdBases and starts the animation).
  const [runnerAnimationActive, setRunnerAnimationActive] = useState(false);
  // Junk wax cosmetic triggers.
  const [lightsOutActive, setLightsOutActive] = useState(false);  // persistent until inning end or X
  const [streakerTriggerKey, setStreakerTriggerKey] = useState(0);
  const [hotdogTrigger, setHotdogTrigger] = useState(null); // { key, role }
  const [devMenuOpen, setDevMenuOpen] = useState(false);
  // ── Unified notification queue (replaces junkWaxBanner / hackTheBoardMessage /
  //    pitchingChangeBanner). Each entry: { id, icon, accent, teamLabel, title,
  //    body, dismissMs, onDismiss? }.  Rendered via portal by GameNotificationStack.
  const [notifQueue, setNotifQueue] = useState([]);
  const notifTimers = useRef(new Map()); // id → setTimeout handle
  const lightsOutNotifId = useRef(null); // tracks the persistent lights_out notification so it can be cleared
  const [hackTheBoardPending, setHackTheBoardPending] = useState(null); // card_id waiting for message input
  const [hackTheBoardDraft, setHackTheBoardDraft] = useState("");
  // `hrSeed` is the resolution_id of the HR-producing roll. HRBallEffect and the
  // phase-tab badge both call rollHrDistance(power, seed) with this value, so the
  // cinematic counter and the badge agree on the same number every HR.
  const [hrSeed, setHrSeed] = useState(null);
  const triggerHrBall = useCallback((power = null, seed = null) => {
    setHrPower(power);
    setHrSeed(seed);
    setHrTriggerKey(k => k + 1);
  }, []);

  // ── Notification queue helpers ───────────────────────────────────────────────
  const dismissNotif = useCallback((id) => {
    const timers = notifTimers.current;
    clearTimeout(timers.get(id));
    timers.delete(id);
    setNotifQueue(q => {
      const entry = q.find(n => n.id === id);
      if (entry?.onDismiss) entry.onDismiss();
      return q.filter(n => n.id !== id);
    });
  }, []);

  const pushNotif = useCallback((notif) => {
    const id = notif.id ?? crypto.randomUUID();
    setNotifQueue(q => {
      const next = [...q, { ...notif, id }];
      // Cap at 3 visible — drop the oldest when a 4th arrives.
      return next.length > 3 ? next.slice(next.length - 3) : next;
    });
    if (notif.dismissMs) {
      notifTimers.current.set(
        id,
        setTimeout(() => dismissNotif(id), notif.dismissMs),
      );
    }
    return id;
  }, [dismissNotif]);
  // Tough Hop target-select mode — set by FieldingPhase when the batter plays
  // tough_hop and there is more than one eligible infielder to choose from.
  // Shape: { eligibleIds: string[], onSelect: (player) => void } | null
  const [toughHopSelectInfo, setToughHopSelectInfo] = useState(null);

  // Directional Hitting target-select mode — set by FieldingPhase when the batter plays
  // directional_hitting and there is more than one eligible outfielder/infielder to choose from.
  const [directionalHittingSelectInfo, setDirectionalHittingSelectInfo] = useState(null);

  // Pitch clock — visible to both players; published from the active phase component (PitchingPhase / FieldingPhase).
  // null = hidden (e.g. during resolution animation). Numeric = seconds remaining.
  const [pitchClock, setPitchClock] = useState(null);
  // Server-synced clock state: { started_at: epoch_ms, duration: seconds }. Both clients share
  // the same start timestamp, so countdowns computed locally are identical (modulo clock skew).
  const [pitchClockSync, setPitchClockSync] = useState(null);
  // Online half-inning hand refresh modal state
  const [onlineHandRefreshOpen, setOnlineHandRefreshOpen] = useState(false);
  const [myHandReady, setMyHandReady] = useState(false);
  const [opponentHandReady, setOpponentHandReady] = useState(false);
  const [onlineHandDiscards, setOnlineHandDiscards] = useState([]);
  // Phase 6: which active (persistent) cards the user has chosen to discard during
  // the current half-inning modal. Burned cards are auto-included server-side
  // regardless. Reset to [] every modal open via the same effect that resets
  // onlineHandDiscards (search the file for `setOnlineHandDiscards([])`).
  const [onlineActiveDiscards, setOnlineActiveDiscards] = useState([]);
  // 30s auto-ready countdown for the modal. On expiry, fires the user's current discard
  // selections (which always include spent cards via pre-fill).
  const [handRefreshClock, setHandRefreshClock] = useState(null);
  const handRefreshReadyRef = useRef(null);
  // Queue for play-log entries that arrive WHILE a resolution animation is playing.
  // The server emits `play_recorded` with `log_desc` immediately on resolve, but the
  // client's animation takes ~6s to play out — appending the log entry instantly
  // spoils the result before the dice/badge has shown it.
  //
  // Held in React state (not a ref) and matched on `resolution_id`. The release
  // effect drains entries only when the result badge is revealed on screen
  // (via onResultRevealed → revealedResolutionIds) or on pitch_clock_start force-flush.
  // Spectators bypass this queue entirely (no animation to sync with).
  const [pendingLogEntries, setPendingLogEntries] = useState([]);
  // Resolution IDs whose RESULT badge has been revealed on screen. The phase
  // components (PitchingPhase / FieldingPhase) call onResultRevealed at their
  // result-reveal stage — earlier than when the resolution prop clears. The
  // release effect drains a pending log entry as soon as its resolution is
  // revealed, so the log lands in sync with the result badge instead of
  // waiting out the full ~3s result-hold + advance.
  const [revealedResolutionIds, setRevealedResolutionIds] = useState(() => new Set());
  const markResolutionRevealed = useCallback((rid) => {
    if (!rid) return;
    if (process.env.NODE_ENV === "development") {
      console.log(`[LOG-BADGE ${performance.now().toFixed(1)}ms] result badge revealed rid=${rid.slice(-8)}`);
    }
    setRevealedResolutionIds(prev => (prev.has(rid) ? prev : new Set(prev).add(rid)));
  }, []);
  // Incremented by pitch_clock_start to force-drain the pending queue in fast/bot mode.
  // When this ticks, the release effect releases ALL pending entries regardless of
  // in-flight status — pitch_clock_start is the server's definitive "new pitch, previous
  // pitch fully resolved" signal, so it's always safe to flush.
  const [_pendingFlushSeq, _setPendingFlushSeq] = useState(0);
  const _pendingFlushSeqRef = useRef(0);
  const handRefreshInflightRef = useRef(false);
  // Tracks whether the hand-refresh modal is currently open in a ref so the WS
  // message handler (stale closure — onlineHandRefreshOpen not in its dep array)
  // can guard against duplicate hand_refresh_complete events without a stale read.
  const handRefreshOpenRef = useRef(false);
  // Tracks the opened_at_ms of the most recently opened modal so Effect 2 only
  // opens once per server-emitted open event. Without this, when the close path
  // flips onlineHandRefreshOpen → false (a dep of Effect 2), the effect would
  // see a stale game.state.hand_refresh.phase==="open" and reopen the modal.
  // Mirrors the dedup pattern in Effect 1's sentAnimationCompleteForRef.
  const lastOpenedAtRef = useRef(null);
  // Bluff-values: pitcher IDs that have already had values submitted this session.
  // Prevents a stale game_state WS broadcast from re-showing the modal after a
  // successful PATCH (race condition where broadcast arrives just before DB commits).
  const bluffValuesSubmittedRef = useRef(new Set());
  // Online bluff-values readiness: tracks whether each team has finished assigning
  // all pitcher values. Initialized from the DB flag (handles reconnects mid-game).
  // Only relevant in online mode — local mode blocks the whole UI sequentially.
  const [homeBluffReady, setHomeBluffReady] = useState(() => !game?.bluff_values || !!game?.home_bluff_ready);
  const [awayBluffReady, setAwayBluffReady] = useState(() => !game?.bluff_values || !!game?.away_bluff_ready);
  // card_play_rejected dedupe: { "<card_id>::<reason>": <last shown timestamp ms> }
  // Suppresses repeat toasts for the same rejection inside a 2.5s window — catches
  // stale-tab auto-retry loops and rapid double-clicks that otherwise spam.
  const _rejectionDedupRef = useRef(new Map());

  // User-resizable bottom phase-controls panel. Default = the prior fixed values
  // (170 mobile / 220 desktop). Persisted per-browser (not per-game) so a user's
  // preferred size sticks across sessions. Min = the default; max = 70% of viewport
  // height to leave room for the field/scoreboard above.
  const PHASE_PANEL_MIN_DESKTOP = 220;
  const PHASE_PANEL_MIN_MOBILE = 170;
  const [phasePanelHeight, setPhasePanelHeight] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("junkball_phasePanelHeight") || "", 10);
      if (!isNaN(v) && v > 0) return v;
    } catch {}
    return null; // null = use the default min for current breakpoint
  });
  useEffect(() => {
    if (phasePanelHeight == null) return;
    try { localStorage.setItem("junkball_phasePanelHeight", String(phasePanelHeight)); } catch {}
  }, [phasePanelHeight]);
  const startPhasePanelResize = useCallback((evt) => {
    evt.preventDefault();
    const isTouch = evt.type === "touchstart";
    const startY = isTouch ? evt.touches[0].clientY : evt.clientY;
    const isDesktop = typeof window !== "undefined" && window.matchMedia("(min-width: 768px)").matches;
    const minH = isDesktop ? PHASE_PANEL_MIN_DESKTOP : PHASE_PANEL_MIN_MOBILE;
    const maxH = Math.max(minH, Math.floor(window.innerHeight * 0.7));
    const startH = phasePanelHeight ?? minH;
    const onMove = (e) => {
      const y = e.touches ? e.touches[0].clientY : e.clientY;
      const delta = startY - y; // drag UP grows panel
      setPhasePanelHeight(Math.max(minH, Math.min(maxH, startH + delta)));
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
  }, [phasePanelHeight]);

  const [pitcherDialogOpen, setPitcherDialogOpen] = useState(false);
  const [batterSubDialog, setBatterSubDialog] = useState({ open: false, player: null, index: null });
  const [fielderSubDialog, setFielderSubDialog] = useState({ open: false, player: null });

  // Hoisted before the stat callbacks so their deps arrays don't TDZ on `isOnline`. The
  // useGameSocket call lower in this file later assigns its `send` function into wsSendRef
  // — using a ref keeps it out of those deps arrays too.
  const isOnline = game?.game_mode === "online";
  const wsSendRef = useRef(null);
  const [playerDetailsDialog, setPlayerDetailsDialog] = useState({ open: false, player: null });
  const [victoryModal, setVictoryModal] = useState({ open: false, winner: null });
  const [statsModalOpen, setStatsModalOpen] = useState(false);

  // PvP — manager cards + hand management
  const [allCards, setAllCards] = useState([]);
  const [homeHand, setHomeHand] = useState([]);
  const [awayHand, setAwayHand] = useState([]);
  const [activeCards, setActiveCards] = useState([]);
  const [recentInstantCards, setRecentInstantCards] = useState([]);
  const [handRefreshPhase, setHandRefreshPhase] = useState(null);
  // Card return pre-selection: set by lockin_reentry; cleared after PitchingPhase consumes it.
  const [lockinReentryCards, setLockinReentryCards] = useState(null);
  // null | "pitching_team" | "cover_between" | "batting_team"
  const [handRefreshSubmitting, setHandRefreshSubmitting] = useState(false);
  const [handDiscards, setHandDiscards] = useState([]);
  const [activeDiscards, setActiveDiscards] = useState([]); // active-card IDs chosen for trade-back in PvP modal
  // Spent cards come from the server — no local tracking needed
  
  // Track per-player game stats — persist in localStorage per game.
  const statsKey = `junkball_gameStats_${gameId}`;
  const [gameStats, setGameStats] = useState(() => {
    try {
      const saved = localStorage.getItem(statsKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Strip the internal dedup-keys field before returning stats to React state.
        const { _appliedKeys: _, ...stats } = parsed;
        return stats;
      }
    } catch {}
    return {
      pitcherMgr: 0, pitcherChips: 0, batterMgr: 0, batterChips: 0,
      players: {},
    };
  });

  useEffect(() => {
    try {
      // Bundle _appliedKeys with gameStats so they're always persisted together.
      // If the bundle is wiped, both clear at once — prevents split-brain double-apply.
      localStorage.setItem(statsKey, JSON.stringify({
        ...gameStats,
        _appliedKeys: [...(appliedPlayKeysRef.current || [])],
      }));
    } catch {}
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameStats, statsKey]);

  // Play-by-play log — persisted in localStorage per game
  const logKey = `junkball_playLog_${gameId}`;
  const [playLog, setPlayLog] = useState(() => {
    try { const s = localStorage.getItem(logKey); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  useEffect(() => {
    try { localStorage.setItem(logKey, JSON.stringify(playLog)); } catch {}
  }, [playLog, logKey]);

  // Pure function to make a log entry from current game / stats — used both for direct local
  // append and for applying a server-broadcast play_recorded event in online mode.
  const buildLogEntry = (gameSnap, _statsSnap, desc) => ({
    id: Date.now(),
    inning: gameSnap.state.inning,
    half: gameSnap.state.half,
    outs: gameSnap.state.outs,
    desc,
  });

  // Latest resolution_id for pitch / fielding / steal — used as the dedup key when
  // forwarding stats events to the server. Sticky: holds the most-recent live id
  // even after the resolution prop clears, so stat callbacks invoked at animation-
  // end (PitchingPhase stage 8 etc.) still attach the right id to record_play.
  // State (not a ref) so the callbacks below close over the live value via deps —
  // structural fix for the same stale-mirror class as the play-log queue race.
  // Hoisted above addLogEntry / updateChips / recordPlay / recordSteal because
  // those callbacks reference it in their closure (TDZ otherwise).
  const [currentResolutionId, setCurrentResolutionId] = useState(null);

  const addLogEntry = useCallback((desc) => {
    if (!game) return;
    const send = wsSendRef.current;
    if (isOnline && send) {
      if (currentResolutionId) {
        send({
          type: "record_play",
          resolution_id: currentResolutionId,
          sub_key: "log",
          log_desc: desc,
          // Snapshot the pre-resolution state so the log entry shows the right inning/half/outs
          // when both clients apply the broadcast (game.state is updated reactively after).
          context: {
            inning: game.state.inning,
            half: game.state.half,
            outs: game.state.outs,
          },
        });
      }
      return; // never apply locally in online mode
    }
    setPlayLog(prev => [...prev, buildLogEntry(game, gameStats, desc)]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game, gameStats, isOnline, currentResolutionId]);

  const clearPlayLog = useCallback(() => setPlayLog([]), []);

  // "rid:sub" keys already applied — loaded from the gameStats bundle so they're always
  // in sync (wiping one wipes both). markPlayApplied writes inline to the bundle so a
  // page refresh immediately after marking still captures the key.
  const appliedPlayKeysRef = useRef(null);
  if (appliedPlayKeysRef.current === null) {
    try {
      const raw = localStorage.getItem(statsKey);
      const parsed = raw ? JSON.parse(raw) : {};
      if (parsed._appliedKeys) {
        appliedPlayKeysRef.current = new Set(parsed._appliedKeys);
      } else {
        // Migration: read the old separate key written by pre-bundle code.
        const legacyRaw = localStorage.getItem(`junkball_appliedKeys_${gameId}`);
        appliedPlayKeysRef.current = new Set(legacyRaw ? JSON.parse(legacyRaw) : []);
      }
    } catch {
      appliedPlayKeysRef.current = new Set();
    }
  }
  const markPlayApplied = useCallback((key) => {
    appliedPlayKeysRef.current.add(key);
    try {
      const raw = localStorage.getItem(statsKey);
      const bundle = raw ? JSON.parse(raw) : {};
      bundle._appliedKeys = [...appliedPlayKeysRef.current];
      localStorage.setItem(statsKey, JSON.stringify(bundle));
    } catch {}
  }, [statsKey]);

  // Callback to update chips when used (now tracks per-player). In online mode, forward to
  // server and let the play_recorded broadcast drive both clients' updates.
  const updateChips = useCallback((pitcherId, batterId, pMgr, pChip, bMgr, bChip) => {
    const send = wsSendRef.current;
    if (isOnline && send) {
      if (currentResolutionId) {
        send({
          type: "record_play",
          resolution_id: currentResolutionId,
          sub_key: "chips",
          pitcher_id: pitcherId,
          batter_id: batterId,
          chips: { pMgr: pMgr || 0, pChip: pChip || 0, bMgr: bMgr || 0, bChip: bChip || 0 },
        });
      }
      return; // never apply locally in online mode
    }
    setGameStats(prev => applyChipsDelta(prev, pitcherId, batterId, { pMgr, pChip, bMgr, bChip }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, currentResolutionId]);

  // Callback to record a play result (immutable — safe under StrictMode).
  const recordPlay = useCallback((pitcherId, batterId, result) => {
    const send = wsSendRef.current;
    if (isOnline && send) {
      if (currentResolutionId) {
        send({
          type: "record_play",
          resolution_id: currentResolutionId,
          sub_key: "play",
          pitcher_id: pitcherId,
          batter_id: batterId,
          play: result,
        });
      }
      return; // never apply locally in online mode
    }
    setGameStats(prev => applyPlayDelta(prev, pitcherId, batterId, result));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, currentResolutionId]);

  // Called by PitchingPhase / FieldingPhase when a half-inning ends.
  // Local PvP uses the existing pass-the-device flow. Online uses a per-player modal that
  // syncs via WS — both players ready up independently before the next half-inning starts.
  const onHalfInningEnd = useCallback(() => {
    if (!game) return;
    if (game.game_mode === "pvp") {
      setHandRefreshPhase("pitching_team");
      setHandDiscards([]);
      setHandRefreshSubmitting(false);
      setActiveCards([]); setRecentInstantCards([]);
      // Spent cards are cleared AFTER the modal closes (not before it opens)
      // so played cards remain visible in the modal.
    }
    // Online mode: NO-OP. Modal lifecycle is server-driven via state.hand_refresh —
    // see the "phase: open" mirror effect below. Phase components (PitchingPhase /
    // FieldingPhase) still call this when they detect a half-end as a legacy hook;
    // ignoring it here is safe because the server has already populated the field.
  }, [game]);

  // Called by PitchingPhase after reveal — updates active card list for UI.
  // Spent tracking is handled reactively by the game?.home_hand / away_hand watchers.
  const onCardsPlayed = useCallback((cards) => {
    setActiveCards(cards);
  }, []);

  // Track instant (one-time) cards played mid-phase so they appear in the banner
  // for both players, even though they never enter the persistent active_cards list.
  // OOT cards (out_of_turn: true) are skipped here — the server's last_oot_play state
  // drives their display, preventing false-positive banners on rejected plays.
  const onInstantCardPlayed = useCallback((id) => {
    const card = allCards.find(c => c.id === id);
    if (!card || card.out_of_turn) return;
    setRecentInstantCards(prev => prev.some(c => c.id === id) ? prev : [...prev, { ...card, _instantRole: myRole || null }]);
  }, [allCards, myRole]);

  // Intercept hack_the_board instant play — open compose dialog instead of sending directly.
  const onHackTheBoardPlay = useCallback((cardId) => {
    setHackTheBoardDraft("");
    setHackTheBoardPending(cardId);
  }, []);

  // Refresh a team's hand: discard selected cards, draw back to 5.
  // `forcedDiscards` = played/spent cards auto-traded back regardless of user selection.
  const doHandRefresh = useCallback(async (team, forcedDiscards = []) => {
    try {
      const allDiscards = [...new Set([...handDiscards, ...forcedDiscards])];
      const res = await axios.post(`${API}/games/${gameId}/hand/${team}/refresh`, {
        discard: allDiscards,
        discard_active: activeDiscards,
      });
      const newIds = res.data.hand;
      const resolved = newIds.map(id => allCards.find(c => c.id === id)).filter(Boolean);
      if (team === "home") { setHomeHand(resolved); setGame(prev => ({ ...prev, home_hand: newIds })); }
      else { setAwayHand(resolved); setGame(prev => ({ ...prev, away_hand: newIds })); }
      // In local (non-online) mode the server's WS broadcast doesn't reach us — emit
      // the discard log entry directly so the play log feed mirrors what online mode sees.
      if (!isOnline && game) {
        const teamLabel = (team === "home" ? game.home_team?.name : game.away_team?.name) || (team[0].toUpperCase() + team.slice(1));
        const allTraded = [...allDiscards, ...activeDiscards];
        const discardNames = allTraded.map(id => (allCards.find(c => c.id === id) || {}).name).filter(Boolean);
        const desc = discardNames.length > 0
          ? `${teamLabel} traded: ${discardNames.join(", ")}`
          : `${teamLabel} kept hand`;
        setPlayLog(prev => [...prev, buildLogEntry(game, gameStats, desc)]);
      }
      setHandDiscards([]);
      setActiveDiscards([]);
    } catch { toast.error("Failed to refresh hand"); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameId, handDiscards, activeDiscards, allCards, isOnline, game, gameStats]);

  const recordSteal = useCallback((runnerId, _resolutionId = null) => {
    if (!runnerId) return;
    // Online: the server credits the stolen base authoritatively inside
    // _apply_steal_outcome (see backend/game_stats.py). Sending a record_play
    // from here would double-count it. Local games still tally client-side.
    if (isOnline) return;
    setGameStats(prev => applyPlayDelta(prev, null, runnerId, { type: "steal" }));
  }, [isOnline]);

  // Online WS connection — only when game_mode==="online" and role is set.
  // `isOnline` is hoisted earlier in this component for the stat callbacks; reuse it here.
  const { send: wsSend, connected: wsConnected, reconnecting: wsReconnecting, homeConnected, awayConnected } = useGameSocket(
    isOnline && myRole ? gameId : null,
    myRole,
    useCallback((msg) => {
      if (msg.type !== "game_state" && msg.type !== "pong") {
        // Timestamp-enhanced WS log. Filter in DevTools by "[WS]" to watch the message stream.
        // Bursts of many messages in a short window are the primary stutter cause — look for
        // multiple messages arriving within ~16ms of each other (one frame).
        // performance.now() is time-since-page-load, not round-trip latency. Printed
        // bare it reads as one — an audit reported chasing a "142-second RTT" that
        // was simply a two-minute-old connection. Label it for what it is, and show
        // the actual gap since the previous frame.
        {
          const _now = performance.now();
          const _gap = _wsLastFrameRef.current == null ? null : _now - _wsLastFrameRef.current;
          _wsLastFrameRef.current = _now;
          console.log(
            `[WS +${(_now / 1000).toFixed(1)}s since load`
            + (_gap == null ? "" : `, ${_gap.toFixed(0)}ms since last frame`)
            + `] ← ${msg.type}`,
            msg,
          );
        }
      }
      if (msg.type === "game_state") {
        setGame(msg.game);
      } else if (msg.type === "phase_snapshot") {
        // Reconnect recovery: server tells us what phase is in flight (steal / pitch / fielding)
        // and the active clock so we land on the right screen instead of pitch_select.
        if (msg.pitch_clock) {
          setPitchClockSync({
            started_at: msg.pitch_clock.started_at,
            duration: msg.pitch_clock.duration,
            kind: msg.pitch_clock.kind || "pitch",
          });
          // Pitcher-selection phase — pitcher hasn't committed yet, so any stale
          // onlinePitcherReady from a previous pitch or a mid-pitch disconnect is wrong.
          if ((msg.pitch_clock.kind || "pitch") === "pitch") {
            setOnlinePitcherReady(null);
          }
        }
        if (msg.steal) {
          setOnlineStealActive({
            base: msg.steal.base,
            next_base: msg.steal.next_base,
            runner: msg.steal.runner,
            batting_role: msg.steal.batting_role,
            fielding_role: msg.steal.fielding_role,
          });
          // If I've already locked in for this steal, surface that so PitchingPhase can show
          // steal_waiting instead of steal_select.
          const myRoleVal = myRole;
          const opponent = myRoleVal === "home" ? "away" : "home";
          if ((msg.steal.pending_roles || []).includes(opponent)) {
            setOnlineStealOpponentLocked({ role: opponent, cards_count: 0 });
          }
        }
        // Pitch lock-in state: the opponent_locked card surfaces "opponent has committed" UI.
        const oppPitchKey = myRole === "home" ? "away" : "home";
        if (msg.pending_pitches && msg.pending_pitches[oppPitchKey]) {
          setOnlinePitcherLocked({
            role: oppPitchKey,
            cards_count: msg.pending_pitches[oppPitchKey].cards_count || 0,
          });
        }
        // Reconnect recovery for stuck waiting_for_opponent. If the server's pending_pitches
        // data is present and my role is absent, my lock-in was dropped (race, disconnect, etc.).
        // Increment so PitchingPhase resets back to the selection phase.
        // PitchingPhase's guard (pvpPhase !== "waiting_for_opponent") prevents spurious resets
        // when we're in any other phase, so over-incrementing is harmless.
        const myRoleInPending = msg.pending_pitches != null && !!msg.pending_pitches[myRole];
        const oppRoleInPending = msg.pending_pitches != null && !!msg.pending_pitches[oppPitchKey];
        if (msg.pending_pitches != null && !myRoleInPending) {
          setPitchLockInRejectedSeq(s => s + 1);
        }
        // Reconnect recovery for batter stuck in waiting_for_pitcher.
        // When the pitch clock is in "pitch_batter" phase (pitcher already committed,
        // batter is now selecting) and the opponent has a pending lock-in but we don't,
        // synthesize a pitcher_ready so PitchingPhase transitions waiting_for_pitcher →
        // batter_select. The `prev ?? …` guard prevents clobbering a real pitcher_ready
        // that may have arrived before or after this snapshot.
        if (oppRoleInPending && !myRoleInPending && msg.pitch_clock?.kind === "pitch_batter") {
          setOnlinePitcherReady(prev => prev ?? {
            recovered: true,
            cards_count: msg.pending_pitches[oppPitchKey]?.cards_count || 0,
          });
        }
        // Fielding lock-in state: same idea for the fielding screen.
        if (msg.pending_fielding && msg.pending_fielding[oppPitchKey]) {
          setOnlineFieldingLocked({
            role: oppPitchKey,
            cards_count: msg.pending_fielding[oppPitchKey].cards_count || 0,
          });
        }
        // Ball-in-play recovery: a pitch resolved to contact but fielding never completed
        // (disconnect, crash). Server persists in_play_pitches so we can rejoin mid-play.
        // Jump straight to fielding phase so the pitch_lock_in guard doesn't silently block.
        if (!isSpectate && msg.in_play?.contact != null) {
          setPitchResult({ contact: msg.in_play.contact });
          setCurrentPhase("fielding");
        }
        // Replay missed stats / log events from the server's recent_plays buffer so a
        // reconnecting client catches up. Each entry is deduped by the same rid:sub key.
        for (const ev of (msg.recent_plays || [])) {
          const key = `${ev.resolution_id}:${ev.sub_key}`;
          if (appliedPlayKeysRef.current.has(key)) continue;
          markPlayApplied(key);
          if (ev.sub_key === "chips" && ev.chips) {
            // Pass pitcher_chip / batter_chip through so the per-type pitch-mix
            // tally accumulates from the replayed events too.
            setGameStats(prev => applyChipsDelta(prev, ev.pitcher_id, ev.batter_id, ev.chips, ev.pitcher_chip, ev.batter_chip));
          } else if ((ev.sub_key === "play" || ev.sub_key === "steal") && ev.play) {
            setGameStats(prev => applyPlayDelta(prev, ev.pitcher_id, ev.batter_id, ev.play));
          }
          if (ev.log_desc) {
            setPlayLog(prev => [...prev, {
              id: Date.now() + Math.random(),
              inning: ev.context?.inning,
              half: ev.context?.half,
              outs: ev.context?.outs,
              desc: ev.log_desc,
              kind: ev.log_kind,
              cards: ev.log_cards || null,
            }]);
          }
        }
      } else if (msg.type === "opponent_locked") {
        setOnlinePitcherLocked(msg);
        // Rebuild the structured pitch history. recent_plays only ever carried
        // play_recorded frames, so a reconnect restored the board but left `pitches`
        // empty — and that array is the entire Got Your Number evidence base, so the
        // seat silently degraded to blind guessing behind a normal-looking UI.
        // Merge rather than overwrite: a locally-persisted copy may already be ahead.
        if (Array.isArray(msg.recent_pitches) && msg.recent_pitches.length) {
          setPitchHistory(prev => {
            const seen = new Set(prev.map(r => r && r.resolutionId).filter(Boolean));
            const restored = msg.recent_pitches
              .filter(r => r && !seen.has(r.resolution_id))
              .map(buildPitchRecord);
            return restored.length ? [...prev, ...restored].slice(-200) : prev;
          });
        }
      } else if (msg.type === "pitcher_ready") {
        setOnlinePitcherReady(msg);
        setPitchClockSync({ started_at: msg.started_at, duration: msg.duration, kind: "pitch_batter", pitch_token: msg.pitch_token });
      } else if (msg.type === "pitch_reveal") {
        // Capture the resolution as DATA before any display concern. Everything an
        // agent needs to play Got Your Number is in this payload; it just never
        // survived into a readable form.
        setPitchHistory(prev => [...prev.slice(-199), buildPitchRecord(msg)]);
        if (isSpectate) {
          // Spectators: PitchingPhase is not mounted, so it can't drive pitchPvpPhase.
          // Store the reveal data and flip to "resolution" manually so the heat map
          // overlay renders chip colors + positions. Cleared on the next pitch_clock_start.
          setOnlinePitchReveal(msg);
          setPitchPvpPhase("resolution");
        } else {
          setOnlinePitchReveal(msg);
          setOnlinePitcherLocked(null);
          setOnlinePitcherReady(null);
          setLightsOutActive(false);
          if (lightsOutNotifId.current) { dismissNotif(lightsOutNotifId.current); lightsOutNotifId.current = null; }
        }
      } else if (msg.type === "phase_sync") {
        // Authoritative lock-in status for THIS client, sent after every
        // request_state (which useGameSocket fires on each reconnect). Lets a
        // reloaded client restore "you're locked in" instead of re-arming its
        // submit button and deadlocking against an opponent who already sent.
        if (!isSpectate) setPhaseSync(msg);
      } else if (msg.type === "opponent_fielding_locked") {
        if (!isSpectate) setOnlineFieldingLocked(msg);
      } else if (msg.type === "fielding_resolution") {
        // Attach the fielding roll to the pitch that produced it. The server
        // already broadcasts field_d6 / fielding_mod / fielding_mod_breakdown /
        // field_total, but pitches[] only ever recorded the PITCH half — so every
        // fielding card (Elite Reaction Time, Expanded Range, Block the Plate)
        // was verifiable only by parsing a play-log STRING. That is the same gap
        // just closed on the pitcher side; this finishes the job.
        //
        // Matched to the most recent in-play pitch rather than by resolution_id:
        // the fielding resolution carries its OWN id, distinct from the pitch's.
        setPitchHistory(prev => {
          if (!prev.length) return prev;
          const i = [...prev].reverse().findIndex(r => r && r.result === "in_play" && !r.fielding);
          if (i === -1) return prev;
          const idx = prev.length - 1 - i;
          const next = prev.slice();
          next[idx] = {
            ...next[idx],
            fielding: {
              resolutionId: msg.resolution_id ?? null,
              roll: msg.field_d6 ?? null,
              originalRoll: msg.original_field_d6 ?? null,
              errorRerollRoll: msg.error_reroll_d6 ?? null,
              mod: msg.fielding_mod ?? 0,
              modBreakdown: msg.fielding_mod_breakdown ?? null,
              total: msg.field_total ?? null,
              contact: msg.contact ?? null,
              // The rule both seats verified against the log, 29/29 with zero
              // contradictions: D6 total > contact is an out, <= is a hit.
              out: (msg.field_total ?? null) != null && (msg.contact ?? null) != null
                ? msg.field_total > msg.contact : null,
            },
          };
          return next;
        });
        if (!isSpectate) {
          setOnlineFieldingResolution(msg);
          setOnlineFieldingLocked(null);
          setLightsOutActive(false);
          if (lightsOutNotifId.current) { dismissNotif(lightsOutNotifId.current); lightsOutNotifId.current = null; }
        }
      } else if (msg.type === "steal_declared") {
        setOnlineStealActive({
          base: msg.base,
          next_base: msg.next_base,
          runner: msg.runner,
          batting_role: msg.batting_role,
          fielding_role: msg.fielding_role,
        });
        setOnlineStealOpponentLocked(null);
        setOnlineStealResolution(null);
      } else if (msg.type === "steal_rejected") {
        const reasonMap = {
          not_batting_team: "Only the batting team can steal",
          batter_locked: "Too late — batter has already committed to the pitch",
          phase_locked: "Can't steal mid-pitch or during fielding",
          steal_in_progress: "A steal is already in progress",
          invalid_base: "Invalid base",
          no_runner: "No runner on that base",
          next_base_occupied: "Next base is occupied",
        };
        toast.error(reasonMap[msg.reason] || "Can't declare steal right now");
      } else if (msg.type === "card_play_rejected") {
        setLastRejection({
          kind: "card_play", cardId: msg.card_id ?? null,
          reason: msg.reason ?? "unknown", ts: Date.now(),
        });
        if (process.env.NODE_ENV === "development") {
          console.warn("[GAME] card_play_rejected", { card: msg.card_id, reason: msg.reason });
        }
        // Server refused an instant manager-card play. Client UI gates this most
        // of the time, but a stale tab or a race against the runner moving could
        // sneak through — surface the reason instead of silently swallowing.
        const reasonMap = {
          not_in_hand: "That card isn't in your hand anymore",
          card_not_in_hand: "That card isn't in your hand anymore",
          unknown_card: "Unknown card",
          not_instant: "That card has to be played at lock-in",
          not_batting_team: "Only the batting team can play that card",
          not_pitching_team: "Only the pitching team can play that card",
          batter_locked: "Too late — batter has already committed to the pitch",
          phase_locked: "Can't play that card mid-pitch or during fielding",
          modal_open: "Can't play that card while the modal is open",
          steal_in_progress: "A steal is already in progress",
          action_in_progress: "Another action is in progress — try again in a moment",
          swap_in_progress: "A swap is already in progress",
          already_active: "That card's effect is already active",
          already_armed: "That listener is already armed",
          already_spent: "That card has already been played this game",
          card_already_spent: "That card has already been played this game",
          card_locked_until_next_inning: "Locked until the next inning",
          no_runner_3b: "No runner on third base",
          no_runner_on_second: "Tipping Pitches only works with a runner on second",
          no_active_pitcher: "No active pitcher on the mound",
          no_pitcher: "No pitcher available",
          no_batter: "No batter available",
          no_advanceable_runner: "No runners eligible to advance",
          no_eligible_cards: "No eligible cards for that effect",
          no_hbp_occurred: "Needs an HBP this at-bat",
          no_prior_walk: "Needs a recent walk to fire",
          no_hr_context: "No HR available to overturn",
          no_hr_to_challenge: "No HR available to challenge",
          no_correct_called_ball: "No correct called-ball to undo",
          no_qualifying_batters: "Batters don't meet the threshold for that card",
          no_pickoff_pending: "No pickoff selection pending",
          no_dig_it_out_pending: "Dig It Out isn't pending",
          no_message: "Please type a message first",
          batter_does_not_qualify: "Batter doesn't qualify for that card",
          batters_do_not_qualify: "Batters don't qualify for that card",
          runner_does_not_qualify: "Runner doesn't qualify for that card",
          condition_not_met: "Card condition isn't met right now",
          opponent_no_swappable_cards: "Opponent has no swappable cards",
          opponent_has_no_active_cards: "Opponent has no active cards to deactivate",
          missing_target_card_id: "Pick a target card first",
          target_card_not_active: "Target card isn't active anymore",
          invalid_base: "Invalid base",
          invalid_card: "Invalid card",
          invalid_guess: "Invalid guess",
          invalid_guesses: "Invalid guesses",
          next_base_occupied: "Next base is occupied",
          lead_runner_cannot_score: "Lead runner can't score from there",
          base_running_card_already_used: "Already used a base-running card this play",
          bases_loaded: "Bases are loaded — can't play that card",
          deck_empty: "Deck is empty",
          discard_empty: "Discard pile is empty",
          continuous_draw_too_soon: "Card must be active for at least one pitch",
          challenge_play: "Challenge Play in progress",
          foul_play: "Foul Play in progress",
          unsupported_effect: "That card isn't supported yet",
        };
        // Dedupe spam — if the same card+reason fires twice within 2.5s, swallow
        // the duplicate. Catches stale-tab auto-retries and double-clicks.
        const _dedupKey = `${msg.card_id || "?"}::${msg.reason || "?"}`;
        const _now = Date.now();
        const _lastShown = _rejectionDedupRef.current.get(_dedupKey);
        if (_lastShown && (_now - _lastShown) < 2500) {
          // Skip — recent identical rejection.
        } else {
          _rejectionDedupRef.current.set(_dedupKey, _now);
          toast.error(reasonMap[msg.reason] || "Can't play that card right now");
          // Signal PitchingPhase to remove this card from myCards selection
          if (msg.card_id) {
            setRejectedCardId(msg.card_id);
          }
        }
      } else if (msg.type === "dig_it_out_options") {
        // Dig It Out: batting team picks one card from the discard pile.
        setDigItOutOptions({ options: msg.options || [] });
      } else if (msg.type === "pickoff_select") {
        // Multi-runner pickoff: server is waiting for the fielder to choose a target.
        // Both clients enter the selection state so the diamond highlights the runners.
        setOnlinePickoffSelect(msg);
        // Show the card in the instant-play badge immediately on card activation.
        if (msg.card_id) {
          const pickoffCard = allCards.find(c => c.id === msg.card_id);
          if (pickoffCard) {
            const badgeRole = msg.fielding_role || myRole;
            setRecentInstantCards(prev =>
              prev.some(c => c.id === msg.card_id) ? prev : [...prev, { ...pickoffCard, _instantRole: badgeRole }]
            );
          }
        }
      } else if (msg.type === "pickoff_roll") {
        // Pickoff roll result (single runner = immediate; multi-runner = after selection).
        // Clear the selection overlay and show the dice animation.
        setOnlinePickoffSelect(null);
        setOnlinePickoffRoll(msg);
        // Badge for single-runner path (no pickoff_select was sent for that case).
        if (msg.card_id && !onlinePickoffSelect) {
          const pickoffCard = allCards.find(c => c.id === msg.card_id);
          if (pickoffCard) {
            const badgeRole = msg.fielding_role || myRole;
            setRecentInstantCards(prev =>
              prev.some(c => c.id === msg.card_id) ? prev : [...prev, { ...pickoffCard, _instantRole: badgeRole }]
            );
          }
        }
      } else if (msg.type === "opponent_steal_locked") {
        setOnlineStealOpponentLocked(msg);
      } else if (msg.type === "steal_resolution") {
        if (!isSpectate) {
          setOnlineStealResolution(msg);
          setOnlineStealActive(null);
          setOnlineStealOpponentLocked(null);
          setLightsOutActive(false);
          if (lightsOutNotifId.current) { dismissNotif(lightsOutNotifId.current); lightsOutNotifId.current = null; }
        }
        if (msg.outcome === "safe" && msg.runner?.id) recordSteal(msg.runner.id, msg.resolution_id);
      } else if (msg.type === "rundown_state") {
        setRundownState(prev => ({ ...(prev || {}), ...msg, roll_result: null }));
      } else if (msg.type === "rundown_roll_result") {
        setRundownState(prev => prev ? { ...prev, step: msg.outcome === "out" || msg.outcome === "safe" ? "resolved" : (msg.actor === "fielder" ? "runner_guess" : "fielder_guess"), roll_result: msg, round: msg.outcome === "continue" && msg.actor === "runner" ? (prev.round || 1) + 1 : prev.round } : null);
        if (msg.outcome === "out" || msg.outcome === "safe") {
          setTimeout(() => setRundownState(null), RUNDOWN_CLEAR_MS);
        }
      } else if (msg.type === "play_recorded") {
        // Server-broadcast stats event. Dedup locally so a client that also fired the
        // record_play (both clients do, server dedups one direction) doesn't double-apply.
        const key = `${msg.resolution_id}:${msg.sub_key}`;
        if (appliedPlayKeysRef.current.has(key)) return;
        markPlayApplied(key);
        if (msg.sub_key === "chips" && msg.chips) {
          // Pass pitcher_chip + batter_chip so the per-pitch-type tally accumulates.
          // Server emits these for every non-called-ball pitch.
          setGameStats(prev => applyChipsDelta(prev, msg.pitcher_id, msg.batter_id, msg.chips, msg.pitcher_chip, msg.batter_chip));
        } else if ((msg.sub_key === "play" || msg.sub_key === "steal") && msg.play) {
          setGameStats(prev => applyPlayDelta(prev, msg.pitcher_id, msg.batter_id, msg.play));
          // HR ball is fired by the phase components when their result badge renders
          // (PitchingPhase for auto-HR contact ≥7, FieldingPhase for fielded HR).
        }
        if (msg.log_desc) {
          const entry = {
            id: Date.now() + Math.random(),
            inning: msg.context?.inning,
            half: msg.context?.half,
            outs: msg.context?.outs,
            desc: msg.log_desc,
            kind: msg.log_kind,
            cards: msg.log_cards || null,
            resolution_id: msg.resolution_id,
          };
          if (isSpectate || msg.log_immediate) {
            // Spectators have no animation to sync with — PitchingPhase isn't mounted.
            // log_immediate entries (instant card effects, no associated roll animation)
            // are added directly so they appear the moment the card resolves.
            setPlayLog(prev => [...prev, entry]);
          } else {
            // Human-play: hold until the animation clears (release effect drains when
            // the resolution_id leaves the in-flight set, or on pitch_clock_start flush).
            setPendingLogEntries(prev => [...prev, entry]);
          }
        }
        // Manager card visual notification — only for card events that skip phases.
        // roll_override cards surface as a persistent badge in PitchingPhase instead.
        // modifier cards use a lightweight Sonner toast.
        if (msg.sub_key === "log" && msg.log_kind === "card" && msg.card_id) {
          const meta = CARD_BANNER_MAP[msg.card_id];
          if (meta) {
            const _cardTeam = msg.card_role === myRole ? "You" : (msg.card_role === "home" ? game?.home_team?.name : game?.away_team?.name) ?? msg.card_role;
            if (meta.category === "modifier") {
              toast.info(msg.log_desc, { duration: meta.dismissMs ?? 2000 });
            } else if (meta.category !== "roll_override") {
              pushNotif({
                type: "manager_card",
                icon: meta.icon,
                accent: meta.accent,
                teamLabel: _cardTeam,
                title: msg.log_desc?.split(" —")[0] ?? msg.card_id,
                body: msg.log_desc ?? "",
                dismissMs: meta.dismissMs,
              });
            }
          }
        }
      } else if (msg.type === "pitch_clock_start") {
        // A new pitch invalidates any lock-in snapshot from the previous play, so it
        // can never be replayed onto this one.
        setPhaseSync(null);
        setPitchClockSync({ started_at: msg.started_at, duration: msg.duration, kind: msg.kind || "pitch", pitch_token: msg.pitch_token });
        if (msg.kind === "pitch" || !msg.kind) {
          // New pitch starting — clear placed chip/position and return to zone view.
          setHeatMapChip(null);
          setHeatMapPos(null);
          setHeatMapView("zone");
          setHeatMapDropSeq(0);
          // Clear instant-card pills from the previous at-bat (e.g. bunt_instrument
          // which resolves the at-bat without a pitch_reveal, so its pill would otherwise
          // linger until phase_reset).
          setRecentInstantCards([]);
          // Signal the release effect to force-drain any pending log entries. This pitch
          // clock means the previous pitch is fully resolved server-side, so it's safe to
          // flush entries that the animation hasn't cleared yet (fast/bot-vs-bot mode).
          _setPendingFlushSeq(c => c + 1);
        }
        if (isSpectate) {
          // New pitch starting — clear the previous reveal so the heat map returns to
          // placement/history mode until the next pitch_reveal arrives.
          setOnlinePitchReveal(null);
          setPitchPvpPhase("pitch_select");
        }
      } else if (msg.type === "pitch_clock_clear") {
        setPitchClockSync(null);
      } else if (msg.type === "pitch_clock_resume") {
        // Re-establish the timer after a pause (e.g. Swap Spit) without
        // triggering pitch_clock_start's side effects (heat map reset,
        // in-flight chip clear) — the underlying pitch is still in progress.
        setPitchClockSync({ started_at: msg.started_at, duration: msg.duration, kind: msg.kind || "pitch", pitch_token: msg.pitch_token });
      } else if (msg.type === "phase_reset") {
        if (process.env.NODE_ENV === "development") {
          console.warn("[GAME] phase_reset → resetting to", msg.to || "pitch");
        }
        // Server is telling us our local phase is stale (e.g. we sent a fielding_lock_in
        // for a play that no longer exists). Unwind out of any in-flight phase UI and
        // settle back to whatever phase the server says is current. Without this the
        // client stays stuck on FieldingPhase, its clock keeps re-arming, and the auto-
        // action keeps re-firing the same stale fielding_lock_in every 30 seconds.
        setCurrentPhase(msg.to || "pitch");
        setOnlineFieldingResolution(null);
        setOnlinePitchReveal(null);
        setOnlineStealResolution(null);
        setOnlineStealActive(null);
        setOnlineStealOpponentLocked(null);
        setOnlineFieldingLocked(null);
        setOnlinePitcherLocked(null);
        setOnlinePitcherReady(null);
        setPitchClockSync(null);
        setPitchResult(null);
        setRunnerAnimationActive(false);
        setActivePositions([]);
        setActiveCards([]); setRecentInstantCards([]);
        // Force-remount PitchingPhase/FieldingPhase so internal state (pvpPhase, etc.)
        // fully resets — avoids getting stuck in "waiting_for_opponent" after a reset.
        setPhaseResetCounter(c => c + 1);
      } else if (msg.type === "action_rejected") {
        setLastRejection({
          kind: "action", action: msg.action ?? null,
          reason: msg.reason ?? "unknown", ts: Date.now(),
        });
        if (msg.reason === "continuous_draw_too_soon") {
          toast.error(msg.message || "Card must be active for at least one pitch before deactivating");
        } else if (msg.action === "pitch_lock_in") {
          // Our lock-in was rejected (e.g. server's auto-complete timer fired just before we sent,
          // or pitcher wasn't ready yet). Increment seq so PitchingPhase resets pvpPhase from
          // waiting_for_opponent back to batter_select/pitch_select so the player can retry.
          setPitchLockInRejectedSeq(s => s + 1);
          wsSendRef.current?.({ type: "request_state" });
        } else {
          // Server rejected an action (e.g. pitch_lock_in while hand-refresh modal is open).
          // Force a state resync so our UI reflects what the server actually expects.
          wsSendRef.current?.({ type: "request_state" });
        }
      } else if (msg.type === "cards_returned") {
        // Only toast to the player whose cards came back (broadcast for WP/HBP/called_ball,
        // private send_to for Foul Play / Challenge Play). Show count + team rather than
        // listing card names so the opponent doesn't read off what's coming back.
        if (isOnline && msg.role === myRole && (msg.card_ids || []).length > 0) {
          const count = (msg.card_ids || []).length;
          const teamKey = msg.role === "home" ? "home_team" : "away_team";
          const teamName = game?.[teamKey]?.name || (msg.role === "home" ? "Home" : "Away");
          const reasonLabel = {
            wild_pitch: "Wild pitch",
            hit_by_pitch: "HBP",
            called_ball: "Called ball",
            foul_play: "Foul Play",
            challenge_play: "Challenge Play",
          }[msg.reason] || "Card returned";
          toast.info(`${reasonLabel} — ${teamName} traded ${count} ${count === 1 ? "card" : "cards"} back`);
        }
      } else if (msg.type === "lockin_reentry") {
        if (isOnline && (msg.preselect_cards || []).length > 0) {
          setLockinReentryCards(msg.preselect_cards);
        }
      } else if (msg.type === "hand_refresh_progress") {
        if (msg.role && msg.role !== myRole) setOpponentHandReady(true);
      } else if (msg.type === "hand_refresh_complete") {
        // Ignore duplicate / stale events. The server already guards against sending two,
        // but the ref is a cheap client-side belt-and-suspenders. Mark consumed immediately
        // (before React flushes state) so a second message in the same event-loop tick is
        // also blocked. handRefreshOpenRef mirrors onlineHandRefreshOpen via a useEffect.
        if (!handRefreshOpenRef.current) return;
        handRefreshOpenRef.current = false;
        // Null out the auto-ready callback so a racing client-side timer can't fire
        // handleReadyUp after the modal closes and inject a stale hand_refresh_done
        // into the next half-inning.
        handRefreshReadyRef.current = null;
        setOnlineHandRefreshOpen(false);
        setMyHandReady(false);
        setOpponentHandReady(false);
        setOnlineHandDiscards([]);
        setOnlineActiveDiscards([]);
        setOnlinePitcherReady(null);
        setPitchClockSync(null);
        // Remount PitchingPhase so pvpPhase resets to pitch_select / waiting_for_pitcher.
        // Without this, both players stay stuck at "Waiting for pitcher..." because pvpPhase
        // from the previous half is never cleared when the modal closes.
        setPhaseResetCounter(c => c + 1);
      } else if (msg.type === "junk_wax_event") {
        // Cosmetic-only easter egg fired by a junk wax card play. variant
        // names map 1:1 to the effect_type suffix on the server side.
        // Broadcast goes to BOTH connected clients so both see the animation.
        const _jwTeam = msg.role === myRole ? "You" : "Opp";
        if (msg.variant === "lights_out") {
          setLightsOutActive(true);
          lightsOutNotifId.current = pushNotif({
            type: "junk_wax", icon: "💡", accent: "slate",
            teamLabel: _jwTeam,
            title: msg.card_name ?? "Lights Out",
            body: "Lights out — play continues in the dark",
            dismissMs: null,
            onDismiss: () => setLightsOutActive(false),
          });
        } else if (msg.variant === "streaker") {
          setStreakerTriggerKey(k => k + 1);
          if (msg.card_name) {
            pushNotif({
              type: "junk_wax", icon: "🏃", accent: "slate",
              teamLabel: _jwTeam,
              title: msg.card_name,
              body: "A streaker hits the field!",
              dismissMs: 5000,
            });
          }
        } else if (msg.variant === "hotdog") {
          setHotdogTrigger({ key: Date.now(), role: msg.role });
          if (msg.card_name) {
            pushNotif({
              type: "junk_wax", icon: "🌭", accent: "slate",
              teamLabel: _jwTeam,
              title: msg.card_name,
              body: "Hot dogs raining down!",
              dismissMs: 3000,
            });
          }
        }
      } else if (msg.type === "hack_the_board_message") {
        pushNotif({
          type: "hack_the_board", icon: "💻", accent: "violet",
          teamLabel: msg.role === myRole ? "You" : "Opp",
          title: "Hack the Board",
          body: msg.message,
          dismissMs: 5000,
        });
      } else if (msg.type === "pitching_change") {
        // Opposing manager swapped pitchers — flash a notification so the
        // batting side can't miss the change mid-at-bat.
        pushNotif({
          type: "pitching_change", icon: "⚾", accent: "sky",
          teamLabel: msg.team === myRole ? "You" : "Opp",
          title: "Pitching Change",
          body: msg.outgoing_name
            ? `${msg.pitcher_name} replaces ${msg.outgoing_name}`
            : `${msg.pitcher_name} takes the mound`,
          dismissMs: 6000,
        });
      } else if (msg.type === "swap_spit_open") {
        // Spectators (incl. bot-vs-bot watchers connected as ?role=home) never
        // get interactive modals — the server / bot loop resolves them. Guard
        // the OPEN handlers; the *_complete / *_resolved closers stay
        // unconditional so any stale modal still clears.
        if (!isSpectate) {
          setSwapSpitOpen(true);
          setSwapSpitProgress([]);
          setSwapSpitClock(
            msg.started_at && msg.duration
              ? { started_at: msg.started_at, duration: msg.duration }
              : null
          );
        }
      } else if (msg.type === "swap_spit_progress") {
        if (msg.role) setSwapSpitProgress(prev => prev.includes(msg.role) ? prev : [...prev, msg.role]);
      } else if (msg.type === "swap_spit_complete") {
        setSwapSpitOpen(false);
        setSwapSpitProgress([]);
        setSwapSpitClock(null);
      } else if (msg.type === "streaker_interactive_open") {
        if (!isSpectate) {
          setStreakerInteractive({
            initiator: msg.initiator,
            spawn_edge: msg.spawn_edge,
            exit_edge: msg.exit_edge,
            seed: msg.seed,
            started_at: msg.started_at,
            duration: msg.duration,
          });
        }
      } else if (msg.type === "streaker_interactive_complete") {
        // Initiator: the modal already manages its own RESULT_HOLD_MS overlay
        // before calling onClose. Closing immediately on the broadcast would
        // race that timer and clip the CAUGHT/ESCAPED message to a flash.
        // Spectator: no local result hold — close as soon as the server says
        // it's done.
        setStreakerInteractive(prev => {
          if (!prev) return null;
          if (prev.initiator === myRole) return prev;
          return null;
        });
      } else if (msg.type === "hidden_ball_trick_target") {
        if (!isSpectate) setHiddenBallPending({ occupiedBases: msg.occupied_bases || [] });
      } else if (msg.type === "hidden_ball_trick_result") {
        setHiddenBallPending(null);
      } else if (msg.type === "student_of_game_choose") {
        if (!isSpectate) setStudentOfGameModal({ drawnCards: msg.drawn_cards || [] });
      } else if (msg.type === "student_of_game_resolved") {
        setStudentOfGameModal(null);
      } else if (msg.type === "managers_decision_choose") {
        if (!isSpectate) setManagersDecisionModal({ cards: msg.cards || [] });
      } else if (msg.type === "managers_decision_applied") {
        setManagersDecisionModal(null);
      } else if (msg.type === "stealing_signals_open") {
        if (!isSpectate) setStealingSignalsModal({ phase: "pick", cardCount: msg.card_count || 1, revealedCard: null });
      } else if (msg.type === "stealing_signals_reveal") {
        setStealingSignalsModal(prev => prev ? { ...prev, phase: "decide", revealedCard: msg.card_id } : null);
      } else if (msg.type === "stealing_signals_resolved") {
        setStealingSignalsModal(null);
      } else if (msg.type === "subterfuge_triggered") {
        // Only show the steal-choose UI to the subterfuge holder on next message
        if (msg.by_role !== myRole) setSubterfugeModal(null); // attacker sees nothing special
      } else if (msg.type === "subterfuge_steal_choose") {
        if (!isSpectate) setSubterfugeModal({ availableCardIds: msg.cards || [], triggeredBy: msg.triggered_by || "" });
      } else if (msg.type === "subterfuge_steal_resolved") {
        setSubterfugeModal(null);
      } else if (msg.type === "bluff_values_ready") {
        if (msg.role === "home") setHomeBluffReady(true);
        else if (msg.role === "away") setAwayBluffReady(true);
      }
    // Deps deliberately minimal: this is the WebSocket message handler. Adding
    // `game` / `allCards` / `isOnline` / `markPlayApplied` / `onlinePickoffSelect`
    // would recreate the handler — and re-subscribe the socket — on every
    // game_state broadcast. The omitted values are either effectively static
    // for the session (`allCards`, `isOnline`), used only for a cosmetic
    // lookup (`game` → team name in a toast), or a stable callback
    // (`markPlayApplied`); the minor staleness is an accepted tradeoff against
    // socket churn. Pre-existing pattern — see the other disables in this file.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myRole, isSpectate, recordSteal])
  );
  // Sync the live `wsSend` into the ref so the hoisted stat callbacks (defined before
  // useGameSocket runs) can call it. Direct assignment during render is intentional —
  // a useEffect would be one render late on first mount.
  wsSendRef.current = wsSend;

  // ── Phase watchdog ───────────────────────────────────────────────────────
  // If a resolution object stays set for > 25 s the animation never cleared it
  // (component unmounted early, WS drop, etc.). Request a full server resync so
  // the game can continue rather than staying frozen on the last play.
  useEffect(() => {
    if (!isOnline) return;
    if (!onlineFieldingResolution && !onlinePitchReveal && !onlineStealResolution) return;
    const t = setTimeout(() => {
      wsSendRef.current?.({ type: "request_state" });
    }, PHASE_WATCHDOG_MS);
    return () => clearTimeout(t);
  }, [isOnline, onlineFieldingResolution, onlinePitchReveal, onlineStealResolution]);

  const fetchGame = useCallback(async () => {
    try {
      // Send our role so the server strips the opponent's secrets BEFORE they hit
      // the wire. The client-side strip below stays as a belt-and-braces fallback
      // for older servers, but on its own it cannot stop someone reading the
      // Network tab.
      const response = await axios.get(
        `${API}/games/${gameId}`,
        myRole ? { params: { role: myRole } } : undefined,
      );
      let data = response.data;
      // Strip opponent's pitch_chip_values from REST response — same as WS broadcast filter.
      // Prevents a player from inspecting network traffic to learn the opponent's secret values.
      if (myRole && data?.bluff_values) {
        const opp = myRole === "home" ? "away" : "home";
        const oppTeamKey = `${opp}_team`;
        if (Array.isArray(data[oppTeamKey]?.players)) {
          data = {
            ...data,
            [oppTeamKey]: {
              ...data[oppTeamKey],
              players: data[oppTeamKey].players.map(p =>
                p && typeof p === "object" ? (({ pitch_chip_values: _, ...rest }) => rest)(p) : p
              ),
            },
          };
        }
      }
      setGame(data);
    }
    catch { toast.error("Couldn't load game"); navigate("/"); }
    finally { setLoading(false); }
  }, [gameId, myRole, navigate]);

  // Instantly update local game state + fire async save — no round-trip GET.
  // resolutionId (optional) makes the PUT idempotent — both online clients can fire safely.
  const onStateUpdate = useCallback((newState, resolutionId = null) => {
    setGame(prev => ({ ...prev, state: newState }));
    try { localStorage.setItem(`junkball_state_${gameId}`, JSON.stringify(newState)); } catch {}
    const url = resolutionId
      ? `${API}/games/${gameId}/state?resolution_id=${encodeURIComponent(resolutionId)}`
      : `${API}/games/${gameId}/state`;
    axios.put(url, newState).catch(() => {
      toast.error("Auto-save failed — check your connection");
    });
  }, [gameId]);

  // Local mode only: mark non-persistent pitch cards as spent in client state.
  // Online mode uses server-side _mark_lockin_spent via the WS lock-in flow.
  const onSpendLocalCards = useCallback((spentCards) => {
    if (!spentCards) return;
    setGame(prev => {
      const updated = { ...prev };
      if (spentCards.home?.length) {
        const existing = new Set(prev.home_spent_cards || []);
        updated.home_spent_cards = [...(prev.home_spent_cards || []), ...spentCards.home.filter(id => !existing.has(id))];
      }
      if (spentCards.away?.length) {
        const existing = new Set(prev.away_spent_cards || []);
        updated.away_spent_cards = [...(prev.away_spent_cards || []), ...spentCards.away.filter(id => !existing.has(id))];
      }
      return updated;
    });
  }, []);

  useEffect(() => { fetchGame(); }, [fetchGame]);

  // Whenever a resolution broadcast arrives, stash its resolution_id so the next stats
  // callbacks (chips / play / log) know which key to forward to the server. Single
  // effect watching all three resolution_ids keeps the sticky semantic.
  useEffect(() => {
    const live = onlinePitchReveal?.resolution_id
      || onlineFieldingResolution?.resolution_id
      || onlineStealResolution?.resolution_id;
    if (live) setCurrentResolutionId(live);
  }, [onlinePitchReveal?.resolution_id, onlineFieldingResolution?.resolution_id, onlineStealResolution?.resolution_id]);

  // Fetch mock cards once when a PvP game loads
  useEffect(() => {
    if (!game || (game.game_mode !== "pvp" && game.game_mode !== "online") || allCards.length > 0) return;
    axios.get(`${API}/cards/mock`).then(res => setAllCards(res.data)).catch(() => {
      console.warn("[GameBoard] failed to fetch manager cards");
    });
  }, [game, allCards.length]);

  // Resolve hand IDs → card objects whenever game hands or allCards change
  useEffect(() => {
    if (!game || !allCards.length) return;
    const resolve = (ids) => (ids || []).map(id => allCards.find(c => c.id === id)).filter(Boolean);
    setHomeHand(resolve(game.home_hand));
    setAwayHand(resolve(game.away_hand));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.home_hand, game?.away_hand, allCards]);

  // Spent cards are server-authoritative — read directly from game document.
  const homeSpentCards = useMemo(() => game?.home_spent_cards ?? [], [game?.home_spent_cards]);
  const awaySpentCards = useMemo(() => game?.away_spent_cards ?? [], [game?.away_spent_cards]);

  // Cards stay in hand permanently; display hands are just the actual server hands.
  const displayHomeHand = homeHand;
  const displayAwayHand = awayHand;

  useEffect(() => {
    const handleVisibilityChange = () => { if (document.visibilityState === 'visible') fetchGame(); };
    window.addEventListener('focus', fetchGame);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => { window.removeEventListener('focus', fetchGame); document.removeEventListener('visibilitychange', handleVisibilityChange); };
  }, [fetchGame]);

  useEffect(() => {
    if (currentPhase === "pitch") {
      setPitchResult(null); setActivePositions([]); setActiveCards([]);
      setOnlinePitcherLocked(null); setOnlinePitchReveal(null);
    }
  }, [currentPhase]);

  // After option A, the server applies state INLINE with the resolution broadcast — meaning
  // game.state.inning / scores / etc. update DURING the resolution animation. Without a gate,
  // the victory modal and the half-inning hand-refresh modal would fire immediately and stomp
  // the animation. While any resolution payload is in flight (or while we're in the fielding
  // phase, which covers both the fielding animation and the HR celebration), defer those
  // modal-firing effects until the user has actually seen the play.
  // Two flags, intentionally separate:
  //
  //   resolutionAnimationPlaying — a dice/result resolution payload is rendering on
  //     screen RIGHT NOW. Used by Effect 1 (sends `hand_refresh_animation_complete`)
  //     so the modal handshake fires the moment the play finishes animating, not
  //     after the modal has come and gone.
  //
  //   isResolutionAnimating — broader "freeze the displayed game state" gate. Stays
  //     true through the resolution animation AND the half-inning modal lifecycle so
  //     the diamond / scoreboard hold on the pre-resolution state until both players
  //     ready up. This is what lets the user see the new defenders only AFTER the
  //     modal closes, not the instant the server flips the half.
  //
  // Conflating them (the previous bug) made Effect 1 wait forever — the freeze gate
  // was true because state.hand_refresh existed, which kept Effect 1 from confirming
  // animation-complete, which kept the modal from opening, which kept state.hand_refresh
  // around forever. Classic deadlock.
  const halfFlipInFlight = !!game?.state?.hand_refresh;
  const resolutionAnimationPlaying = !!(
    onlinePitchReveal || onlineFieldingResolution || onlineStealResolution
  );
  const isResolutionAnimating = !isSpectate && !!(
    resolutionAnimationPlaying
    || (isOnline && currentPhase === "fielding")
    || (isOnline && halfFlipInFlight)
  );

  // While a resolution is in flight, freeze the displayed game state to its pre-resolution
  // snapshot so the scoreboard (scores, inning column, balls/strikes/outs) doesn't reveal
  // the outcome before the animation does. Once the flag releases, the snapshot catches up.
  const frozenStateRef = useRef(null);
  const lastAnimatingRef = useRef(false);
  if (isResolutionAnimating && !lastAnimatingRef.current) {
    // Just entered an animation — capture the pre-resolution state.
    frozenStateRef.current = game?.state || null;
  } else if (!isResolutionAnimating && lastAnimatingRef.current) {
    // Just exited — release the freeze.
    frozenStateRef.current = null;
  }
  lastAnimatingRef.current = isResolutionAnimating;

  // Release effect: drain pendingLogEntries whose result badge has been revealed.
  // Three conditions release an entry:
  //   1. forceFlush — pitch_clock_start guarantees previous pitch is fully resolved,
  //      BUT only when no resolution animation is currently playing. In vs-bot mode
  //      the bot starts the next pitch clock almost instantly after fielding resolves,
  //      so the next pitch_clock_start can arrive before the fielding badge appears.
  //      Deferring the flush (by not advancing _pendingFlushSeqRef) lets the effect
  //      re-run when resolutionAnimationPlaying flips false and then do the flush.
  //   2. !entry.resolution_id — untagged entry, safe to release immediately
  //   3. revealedResolutionIds.has(rid) — phase component confirmed badge is on screen
  //
  // We intentionally do NOT release when the rid merely disappears from the in-flight
  // set (onlinePitchReveal / onlineFieldingResolution / onlineStealResolution).
  // That condition races: play_recorded can arrive before or after pitch_reveal in
  // any render cycle, so "not in flight" can mean "not yet started" just as easily
  // as "already finished" — causing the log to spoil the result mid-animation.
  useEffect(() => {
    if (pendingLogEntries.length === 0) return;
    const forceFlushPending = _pendingFlushSeq !== _pendingFlushSeqRef.current;
    // Hold the force-flush while an animation is actively showing — the badge-reveal
    // mechanism handles timing. Advance the seq ref only when we actually consume the
    // flush so the deferred flush fires when resolutionAnimationPlaying turns false.
    const forceFlush = forceFlushPending && !resolutionAnimationPlaying;
    if (!forceFlushPending || forceFlush) _pendingFlushSeqRef.current = _pendingFlushSeq;
    const releasable = [];
    const stillPending = [];
    for (const entry of pendingLogEntries) {
      if (shouldReleaseLogEntry(entry, revealedResolutionIds, forceFlush)) {
        releasable.push(entry);
        if (process.env.NODE_ENV === "development") {
          const reason = forceFlush ? "force-flush" : !entry.resolution_id ? "untagged" : "badge-revealed";
          console.log(`[LOG-RELEASE ${performance.now().toFixed(1)}ms] "${entry.desc?.slice(0, 40)}" via ${reason} (rid=${entry.resolution_id?.slice(-8) ?? "none"})`);
        }
      } else {
        stillPending.push(entry);
      }
    }
    if (releasable.length === 0) return;
    setPlayLog(prev => [...prev, ...releasable]);
    setPendingLogEntries(stillPending);
    // Prune the revealed set down to resolutions still queued — keeps it from
    // growing unbounded over a long game.
    setRevealedResolutionIds(prev => {
      if (prev.size === 0) return prev;
      const stillNeeded = new Set(
        stillPending.map(e => e.resolution_id).filter(Boolean)
      );
      if (stillNeeded.size === prev.size) return prev;
      return stillNeeded;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingLogEntries, _pendingFlushSeq, revealedResolutionIds, resolutionAnimationPlaying]);
  // Memoised: this used to allocate a fresh object on every render, which made the
  // __JUNKBALL_STATE__ effect below re-run constantly and fire a `junkball:state`
  // event each time — an agent awaiting that event got a flood of duplicates rather
  // than one per actual change.
  const displayGame = useMemo(
    () => (isResolutionAnimating && frozenStateRef.current && game
      ? { ...game, state: frozenStateRef.current }
      : game),
    [isResolutionAnimating, game],
  );

  // Box-score source. The server maintains game.game_stats authoritatively
  // (see backend/game_stats.py); prefer it. Fall back to the legacy client-side
  // reconstruction (`gameStats`) only for games created before that field
  // existed, which never carry a `game_stats.players` map.
  const boxStats = game?.game_stats?.players ? game.game_stats : gameStats;

  // ── window.__JUNKBALL_STATE__ — machine-readable board snapshot ─────────────
  // Published so an automated player can read the game without OCR'ing pixels.
  // It mirrors what this client already holds, so it exposes no hidden
  // information (the opponent's unrevealed chip is not in `game` to begin with).
  //
  // Three placement constraints, each learned the hard way:
  //   * keyed on `game`, not published from the WS handler — there are 13
  //     setGame call sites and the socket handler's `game` closure is stale by
  //     design, so only an effect on the value catches every update;
  //   * above the loading / empty-lineup early returns below, so it keeps
  //     updating on exactly the screens a stuck harness needs to diagnose;
  //   * `server` and `display` are both published because displayGame
  //     deliberately lags during a resolution animation (see `frozen`) — read
  //     `server` for truth, `display` for what is currently on screen.
  // Does this seat still owe Got Your Number assignments? Derived from the game
  // document rather than the modal's own state, which is computed further down the
  // render (below the early returns) and would be in the temporal dead zone here.
  const _gynAssignPending = !!(game?.bluff_values && myRole && (
    ((myRole === "home" ? game.home_team : game.away_team)?.players || [])
      .some(p => p && p.position === "P" && !p.subbed_out && !p.pitch_chip_values)
  ));

  const __stateSeqRef = useRef(0);
  // Previous published snapshot — needed for the monotonic checks (seq, and
  // "knowledge is never forgotten").
  const __prevSnapRef = useRef(null);
  // Last non-null opposing sheet, so intel.opponentPitcher survives the half flip.
  const __lastOppSheetRef = useRef(null);
  // True when I am batting and the pitcher has not committed — so my Lock In is
  // disabled no matter what phase name the client is showing. phase_sync's
  // opponent_pitch_locked is the server's own answer to "has the pitcher committed".
  const _battingAndPitcherNotReady = !!(
    isOnline && myRole && game?.state
    && myRole !== (game.state.half === "top" ? "home" : "away")   // I am batting
    && pitchPvpPhase === "waiting_for_pitcher"
  );

  const __waitStartRef = useRef(null);
  const _wsLastFrameRef = useRef(null);
  useEffect(() => {
    const _totalInnings = game?.innings_total ?? null;
    const pick = (st) => st && {
      // Clamp once the game is over: play stops at innings_total, but the counter
      // had already advanced, so a finished 3-inning game reported inning 4.
      inning: (game?.status === "complete" && _totalInnings)
        ? Math.min(st.inning ?? 1, _totalInnings)
        : st.inning,
      half: st.half, outs: st.outs,
      count: { balls: st.balls, strikes: st.strikes },
      bases: st.bases, home_score: st.home_score, away_score: st.away_score,
    };
    // Derived defensively: any roster shape we don't expect yields null rather
    // than throwing inside an effect that the whole board depends on.
    let batter = null, pitcher = null, battingRole = null, myPitcher = null;
    // Full roster objects, kept separate from the trimmed summaries below.
    // `batter`/`pitcher` are display summaries and deliberately omit `seasons`
    // (a whole career per pitch would bloat the blob). But every stat-gated card
    // condition reads `player.seasons`, so passing a summary to canPlayCard made
    // ALL of them evaluate false — Slap Hit and Discerning Eye reported
    // "conditions-not-met" for Lou Brock, who clears their thresholds easily,
    // while the DOM (which gets the full player) correctly said playable.
    let batterFull = null, pitcherFull = null;
    try {
      const st = game?.state;
      if (st) {
        battingRole = st.half === "top" ? "away" : "home";
        const fieldingRole = battingRole === "away" ? "home" : "away";
        const bt = game[`${battingRole}_team`], ft = game[`${fieldingRole}_team`];
        const order = bt?.batting_order || [];
        const idx = getBatterIndex(st);
        const bid = order[idx % (order.length || 1)];
        const bp = (bt?.players || []).find(pl => pl.id === bid);
        if (bp) { batterFull = bp; batter = { id: bp.id, name: bp.name, position: bp.position, year: bp.year }; }
        const pp = (ft?.players || []).find(pl => pl.position === "P" && !pl.subbed_out)
          || (ft?.players || []).find(pl => pl.position === "P");
        if (pp) {
          pitcherFull = pp;
          pitcher = {
            id: pp.id, name: pp.name, year: pp.year, pitch_chips: pp.pitch_chips,
            // Same treatment for the arm I am facing. Active cards are already
            // public (both sides render them), so this exposes nothing hidden —
            // it just stops every seat re-deriving it from the effects text.
            condition: derivePitcherCondition(game, fieldingRole, pp, allCards),
          };
        }
        // My OWN team's current pitcher, with the chip sheet. This is our own
        // secret (the server strips the opponent's), and it is what a lineup's
        // chips are valued on — previously only recoverable by regexing the DOM.
        const myTeam = myRole === "home" ? game.home_team : game.away_team;
        const myP = (myTeam?.players || []).find(pl => pl?.position === "P" && !pl?.subbed_out);
        if (myP) {
          myPitcher = {
            id: myP.id, name: myP.name, year: myP.year,
            pitch_chips: myP.pitch_chips,
            chipValues: myP.pitch_chip_values ?? null,
            // What is currently TRUE of this arm. The effects line has always
            // rendered this (`P: -1 Worn Down`) while state carried only the
            // identity fields, so a seat driving off state could not see its own
            // pitcher was degraded — the direct input to the bullpen decision.
            // Reports repeatedly mis-filed that gap as a hidden fatigue
            // mechanic, then over-corrected by withdrawing the ticket entirely.
            condition: derivePitcherCondition(game, myRole, myP, allCards),
          };
        }
      }
    } catch { /* leave nulls */ }

    // Computed before the snapshot literal so `actionSelector` can point at the
    // control this action needs, and so a stalled wait can be timestamped.
    const __awaiting = (() => {
        if (isSpectate || !isOnline || !game) return false;
        if (victoryModal.open) return false;                 // terminal
        // ...unless this seat has already readied. The button unmounts and is
        // replaced by "✓ Ready — waiting for <opponent>", so claiming an action
        // is still owed points a driver at a control that no longer exists —
        // and is very likely the "both locked in but frozen" report from the
        // earlier games. Waiting on the opponent is not owing an action.
        if (onlineHandRefreshOpen) return !myHandReady;
        if (_gynAssignPending) return true;                  // assign chips to proceed
        if (swapSpitOpen || studentOfGameModal || managersDecisionModal
            || hiddenBallPending || streakerInteractive) return true;
        // Only true while this seat actually owes a roll. Once submitted the panel
        // shows "YOU'RE LOCKED IN" and removes its control — claiming otherwise
        // told an agent to act with nothing to act on.
        if (currentPhase === "fielding") return fieldingSubPhase === "select";
        if (resolutionAnimationPlaying || halfFlipInFlight) return false;
        // A batter whose pitcher has not committed CANNOT submit — Lock In is
        // disabled and the real gate is the opposing seat. Claiming otherwise made
        // drivers loop on lock-in; the duplicates are what wedged the resolver.
        if (_battingAndPitcherNotReady) return false;
        return ["pitch_select", "batter_select", "steal_select", "pickoff_select"]
          .includes(pitchPvpPhase);
    })();

    const __expected = (() => {
        if (isSpectate || !isOnline || !game) return null;
        if (victoryModal.open) return "game_over";
        if (onlineHandRefreshOpen) return "hand_refresh_ready_up";
        if (_gynAssignPending) return "assign_chip_values";
        if (swapSpitOpen) return "swap_spit";
        if (studentOfGameModal) return "student_of_game_pick";
        if (managersDecisionModal) return "managers_decision";
        if (hiddenBallPending) return "hidden_ball_target";
        if (streakerInteractive) return "streaker_minigame";
        if (currentPhase === "fielding") {
          return fieldingSubPhase === "select" ? "fielding_lock_in" : "wait_opponent";
        }
        if (resolutionAnimationPlaying || halfFlipInFlight) return "wait_animation";
        // Pre-commit: the batter may pick a chip but cannot lock it in yet.
        if (_battingAndPitcherNotReady) return "may_preselect_chip";
        if (pitchPvpPhase === "pitch_select" || pitchPvpPhase === "batter_select") return "select_chip_and_lock";
        if (pitchPvpPhase === "waiting_for_pitcher") return "may_preselect_chip";
        if (pitchPvpPhase === "steal_select") return "steal_lock_in";
        if (pitchPvpPhase === "pickoff_select") return "pickoff_target";
        return "wait_opponent";
      })();
    if (__expected === "wait_opponent" || __expected === "wait_animation") {
      if (__waitStartRef.current == null) __waitStartRef.current = Date.now();
    } else {
      __waitStartRef.current = null;
    }
    const __waitingSince = __waitStartRef.current;

    const snap = {
      schema: 1,
      seq: ++__stateSeqRef.current,
      ts: Date.now(),
      ready: !loading && !!game,
      gameId,
      myRole,
      isSpectate,
      isOnline,
      // NB: NOT plain `isResolutionAnimating`. That gate is true for the WHOLE
      // fielding phase by construction, so it used to report frozen:true at the
      // same moment as awaitingMyAction:true and an enabled Lock In control —
      // two contradictory instructions. An agent obeying "frozen means wait"
      // waits forever and the opposing seat parks in waiting_for_opponent; a
      // playtester traced the unrecoverable stalls in two games to exactly this.
      // frozen now means only "nothing is being asked of you, a resolution is
      // playing" — if you owe an action, you are never frozen.
      frozen: isResolutionAnimating && !__awaiting,
      // The raw animation gate, for anyone who needs the display-freeze signal.
      animating: isResolutionAnimating,
      phase: {
        board: currentPhase,
        pitch: pitchPvpPhase,
        // Derived from displayGame — the SAME source as `display` below — so the
        // whole projection flips atomically. Reading game.state here while display
        // read the frozen snapshot produced states that never existed (role already
        // flipped, outs and bases still the previous half's).
        myRoleThisHalf: !displayGame?.state ? null
          : (myRole === (displayGame.state.half === "top" ? "home" : "away") ? "pitching" : "batting"),
        // True while display intentionally lags server during a reveal.
        settling: isResolutionAnimating,
      },
      // Is a primary control live for ME right now?
      //
      // `phase.pitch` alone cannot answer this: "pitch_select" is the SHARED screen
      // for pitcher and batter, so an agent polling the phase name concluded it was
      // the pitcher's turn and stalled through a whole half-inning of its own at-bats.
      // Derived from the same conditions that enable the buttons.
      // Is a primary control live for ME right now?
      //
      // Ordering is load-bearing and was wrong. `isResolutionAnimating` is defined
      // as `resolutionAnimationPlaying || currentPhase === "fielding" || halfFlipInFlight`
      // — so it is true for the ENTIRE fielding phase, which made the fielding branch
      // below unreachable dead code. Every batted ball reported
      // {expectedAction: "wait_animation", awaitingMyAction: false} while an enabled
      // Lock In sat on screen; a playtest logged 18 such hangs in one game.
      //
      // So: blocking modals first (they own the screen), then the surfaces that owe
      // an action, and only then the genuine animation wait.
      awaitingMyAction: __awaiting,
      // What that action is, so an agent never infers it from a phase name.
      // What that action is, so an agent never infers it from a phase name.
      expectedAction: __expected,

      // Where the expected control actually lives. An audit reported believing it
      // should act while finding no actionable button — `currentPhase` drives both
      // the phase tab and the panel mount so they cannot disagree, but rather than
      // have an agent infer the location at all, name it. Query this selector; if it
      // is absent or disabled, that is a real defect worth reporting.
      actionSelector: (() => {
        const map = {
          fielding_lock_in: "[data-testid='fielding-lock-in']",
          select_chip_and_lock: "[data-testid='lock-in']",
          may_preselect_chip: "[data-testid='chip-row'] [data-testid^='chip-']",
          steal_lock_in: "[data-testid='lock-in'][data-phase='steal_select']",
          // Point at the BUTTON, not the modal container — the container is not
          // clickable and a driver told to act on it has nothing to press.
          hand_refresh_ready_up: "[data-testid='half-inning-ready']",
          assign_chip_values: "[data-testid='gyn-assign-modal']",
          // swap_spit had NO entry, so actionSelector was null while a 30s clock
          // ran and a decision was required — the checker's own
          // "awaitingMyAction with no actionSelector" was the real I2 hit of that
          // game. Point at a card button: selecting one is the action owed, and
          // the Ready control stays disabled until then.
          swap_spit: "[data-testid^='swap-card-']",
        };
        return map[__expected] || null;
      })(),
      // Are keyboard shortcuts live right now, and if not, which overlay owns the
      // keyboard? Reported "shortcuts stopped working mid-game" is most likely a
      // modal flag that never cleared — the handler is bound to `window`, so focus
      // is not the cause. Surfacing this lets a driver fall back to clicking AND
      // name the culprit instead of guessing.
      keyboard: {
        enabled: !(streakerInteractive || swapSpitOpen || _gynAssignPending
                   || hiddenBallPending || studentOfGameModal || managersDecisionModal
                   || onlineHandRefreshOpen || victoryModal.open),
        blockedBy: streakerInteractive ? "streaker"
          : swapSpitOpen ? "swap_spit"
          : _gynAssignPending ? "gyn_assign"
          : hiddenBallPending ? "hidden_ball"
          : studentOfGameModal ? "student_of_game"
          : managersDecisionModal ? "managers_decision"
          : onlineHandRefreshOpen ? "hand_refresh"
          : victoryModal.open ? "victory" : null,
        chips: "1-4 or F/C/B/S", lock: "Enter", takePitch: "T", walk: "W",
      },
      // When this seat entered its current wait, so a stalled opponent is visible
      // instead of silent. With No Timers there is no clock to break a stall, and a
      // seat that stops acting hangs the game until the 10-minute AFK net.
      waitingSince: __waitingSince,
      // The last action the server refused: {kind, action|cardId, reason, ts}.
      // Null until something is rejected. If you submit and nothing changes,
      // check here before assuming the game is stuck.
      lastRejection,
      battingRole,
      batter,
      pitcher,
      myPitcher,
      // ── Got Your Number intel, derived once and correctly ─────────────────
      // Every automated seat was re-deriving this from `pitches[]` by hand and
      // getting it wrong the same ways. Derivation (and its reasoning) lives in
      // utils/gynIntel.js so it is unit-tested rather than trapped in a render.
      intel: (() => {
        try {
          if (!game?.bluff_values || !myRole) return null;
          const _intel = deriveGynIntel(pitchHistory, {
            myRole,
            chipMax: game.chip_max ?? 4,
            // The pitcher facing me only exists while I am batting.
            opponentPitcherId: myRole === battingRole ? (pitcher?.id ?? null) : null,
            opponentPitcherName: myRole === battingRole ? (pitcher?.name ?? null) : null,
            myPitcherId: myPitcher?.id ?? null,
            // Your own sheet is assigned, not learned — seed it so it isn't
            // reported as fully unknown right after you fill in the modal.
            myPitcherChipValues: myPitcher?.chipValues ?? null,
            // Their sheet is permanent knowledge; keep showing it (flagged
            // `stale`) while they're off the mound instead of blanking it.
            lastKnownOpponentPitcher: __lastOppSheetRef.current,
            chipPool: game?.state?.pitcher_chip_pool ?? null,
          });
          const _op = _intel?.opponentPitcher;
          if (_op && !_op.stale) __lastOppSheetRef.current = _op;
          return _intel;
        } catch { return null; }
      })(),
      // My hand, with playability resolved against the CURRENT batter/pitcher.
      //
      // Card conditions are gated on real career stats (25+ SB, 45+ HR, .220 or
      // below) evaluated against the card's printed seasons — not the player's
      // real-life reputation. A playtester finished a game holding two Steals and a
      // Balk that were never legal against a power roster, and lost a planned win
      // condition to it, because the only way to discover a card is dead was to
      // play it and be refused. `playable` answers that before it costs a decision;
      // `blockedReason` says why.
      myHand: (() => {
        try {
          const cards = myRole === "home" ? homeHand : awayHand;
          if (!Array.isArray(cards) || !game?.state) return [];
          const teamRole = myRole === battingRole ? "batter" : "pitcher";
          const spent = new Set(game[`${myRole}_spent_cards`] || []);
          const _myArmedListeners = (game.state?.active_listeners || {})[myRole] || [];
          const _myActiveIds = new Set(
            (game[`${myRole}_active_cards`] || [])
              .filter(e => e && !e.burned)
              .map(e => (typeof e === "string" ? e : e.id))
              .filter(Boolean)
          );
          return cards.filter(Boolean).map(c => {
            const isSpent = spent.has(c.id);
            // Full players, not the display summaries — the summaries have no
            // `seasons`, which silently failed every career-stat gate.
            const ok = !isSpent && canPlayCard(c, {
              phase: currentPhase === "fielding" ? "fielding" : "pitch",
              gameState: game.state,
              currentBatter: batterFull, currentPitcher: pitcherFull, teamRole,
            });
            // Arming state. A playtest tested `myHand[].inPlay` and reported it
            // "never reflects an armed card (0/2)" — the field did not exist, so
            // they were reading `undefined` and correctly seeing it as never
            // true. The tray's ⚡Arm → ⚡Disarm label was the only truth signal,
            // which is not something a state-driven seat can read.
            //   armed  — this listener is live in state.active_listeners
            //   inPlay — this persistent card is in active_cards, not burned
            const _armed = !!c.listener && _myArmedListeners.includes(c.id);
            const _inPlay = _myActiveIds.has(c.id);
            return {
              id: c.id, name: c.name, description: c.description ?? null,
              rarity: c.rarity ?? null, instant: !!c.instant, listener: !!c.listener,
              who: c.who ?? "either", phase: c.phase ?? "any",
              playable: !!ok,
              blockedReason: ok ? null : (isSpent ? "already-used" : "conditions-not-met"),
              armed: _armed,
              inPlay: _inPlay,
              // A listener that is not spent can be armed — this is what the
              // ⚡Arm control's presence actually means.
              armable: !!c.listener && !isSpent && !_armed,
            };
          });
        } catch { return []; }
      })(),
      server: pick(game?.state),
      display: pick(displayGame?.state),
      // Terminal state. Previously nothing in the blob said the game was over —
      // an agent driving from state alone polled forever after the win screen and
      // could not report a result.
      final: {
        over: !!victoryModal.open || game?.status === "complete",
        // Derived, not just mirrored from the victory modal. `over` flips as soon
        // as status is "complete", but the modal's winner arrives a tick later —
        // so an agent reading both on the same tick (as the playbook tells it to)
        // got over:true with winner:null and could not report the result.
        winner: victoryModal.winner ?? (() => {
          if (game?.status !== "complete" || !game?.state) return null;
          const h = game.state.home_score ?? 0, a = game.state.away_score ?? 0;
          if (h === a) return null;   // a tie here means the game is still settling
          return { winner: h > a ? "home" : "away", reason: "regulation", derived: true };
        })(),
        status: game?.status ?? null,
      },
      // Every rule an agent needs to verify a resolution against.
      //
      // The tie toggles were absent, so two separate playtests filed the SAME
      // phantom regression — wild pitch on 0/0 and HBP on 1/1 were reported as
      // "Any Raw Tie -> Ball is broken" when that setting was simply off. An agent
      // that cannot see the ruleset cannot check the engine against it, so it
      // assumes, and assumptions become bug reports.
      settings: game ? {
        no_timers: !!game.no_timers, heat_map: !!game.heat_map,
        bluff_values: !!game.bluff_values, chip_max: game.chip_max,
        innings_total: game.innings_total,
        // Tie resolution — these decide outcomes on identical dice.
        any_raw_tie_is_ball: !!game.any_raw_tie_is_ball,
        same_raw_tie_is_ball: !!game.same_raw_tie_is_ball,
        modifier_tie_is_ball: !!game.modifier_tie_is_ball,
        called_ball_tie_is_strike: !!game.called_ball_tie_is_strike,
        // Other rules that change how a pitch resolves.
        require_chip_match_for_hr: !!game.require_chip_match_for_hr,
        unlimited_stacking: !!game.unlimited_stacking,
        depleting_chips: !!game.depleting_chips,
        continuous_draw: !!game.continuous_draw,
        pitch_mod: game.pitch_mod,
        hand_size: game.hand_size,
      } : null,
      connection: {
        wsConnected, wsReconnecting, homeConnected, awayConnected,
        // A bot never opens a socket, so its peer flag is false for the whole game.
        // Without this it reads as a phantom disconnect that never resolves.
        homeIsBot: game?.bot_role === "home" || game?.bot_role === "both",
        awayIsBot: game?.bot_role === "away" || game?.bot_role === "both",
      },
      log: {
        count: playLog.length,
        last: playLog[playLog.length - 1] || null,
        entries: playLog.slice(-25),
      },
      // Structured resolutions, newest last. This is the one to read for chip
      // deduction — `log.entries` carries only the human-facing prose.
      pitches: pitchHistory,
      lastPitch: pitchHistory[pitchHistory.length - 1] || null,
    };
    // Phase 0: assert the state contract on every emit. Violations ride on the
    // blob so a harness can fail a run on them, and are logged with the seq so a
    // report can point at the exact frame. Never throws — see stateInvariants.js.
    try {
      snap.violations = checkInvariants(snap, __prevSnapRef.current);
      if (snap.violations.length && process.env.NODE_ENV === "development") {
        console.warn("[GAME] state invariant violation", { seq: snap.seq, violations: snap.violations });
      }
    } catch { snap.violations = []; }
    __prevSnapRef.current = snap;
    window.__JUNKBALL_STATE__ = snap;
    // Lets a harness await a change instead of polling on a timer.
    try { window.dispatchEvent(new CustomEvent("junkball:state", { detail: snap })); } catch {}
  }, [game, displayGame, loading, gameId, myRole, isSpectate, isOnline, isResolutionAnimating,
      currentPhase, pitchPvpPhase, wsConnected, wsReconnecting, homeConnected, awayConnected,
      onlineHandRefreshOpen, playLog, pitchHistory, fieldingSubPhase, homeHand, awayHand,
      _gynAssignPending, _battingAndPitcherNotReady, lastRejection, myHandReady, allCards,
      victoryModal.open, victoryModal.winner, swapSpitOpen, studentOfGameModal,
      managersDecisionModal, hiddenBallPending, streakerInteractive,
      resolutionAnimationPlaying, halfFlipInFlight]);

  useEffect(() => () => { try { delete window.__JUNKBALL_STATE__; } catch {} }, []);

  // Heat Map: derive current pitcher's throw history from the game record.
  // Keyed by pitcher_id so switching pitchers automatically shows their history.
  const pitcherHeatHistory = useMemo(() => {
    const half = game?.state?.half || "top";
    const pitchingRole = half === "top" ? "home" : "away";
    const heatMapData = game?.[`${pitchingRole}_heat_map`] || {};
    // current_pitcher_id is null until the first pitch resolves; fall back to
    // the active pitcher from the team roster so history shows from pitch 2+
    // even when navigating back to the page mid-game.
    let pitcherId = game?.state?.current_pitcher_id;
    if (!pitcherId) {
      const pitchingTeam = game?.[`${pitchingRole}_team`];
      const activePitcher = pitchingTeam?.players?.find(
        (p) => p.position === "P" && !p.subbed_out
      );
      pitcherId = activePitcher?.id;
    }
    if (!pitcherId) return [];
    return heatMapData[pitcherId] || [];
  }, [game]);

  // Reset heat map chip/position each time PitchingPhase remounts (new at-bat).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setHeatMapChip(null); setHeatMapPos(null); setHeatMapView("zone"); setHeatMapDropSeq(0); }, [phaseResetCounter]);

  // Auto-switch to field view during runner animations and steal/pickoff phases so players
  // can see the bases. Restores zone view automatically when the trigger clears —
  // unless the player manually chose field, in which case heatMapAutoFieldRef is false.
  useEffect(() => {
    const STEAL_PHASES = ["steal_select","steal_waiting","steal_resolution","pickoff_select","pickoff_roll"];
    if (runnerAnimationActive || STEAL_PHASES.includes(pitchPvpPhase)) {
      heatMapAutoFieldRef.current = true;
      setHeatMapView("field");
    } else if (heatMapAutoFieldRef.current) {
      heatMapAutoFieldRef.current = false;
      setHeatMapView("zone");
    }
  }, [runnerAnimationActive, pitchPvpPhase]);

  useEffect(() => {
    if (game?.state && !victoryModal.open) {
      const gameOverResult = isGameOver(game.state, game.innings_total || 9);
      if (gameOverResult) {
        // Force-clear any transient overlays so they can't outlive the game.
        // The streaker modal in particular has its own 2.4s auto-close timer
        // that can race the burst of game_state broadcasts at end-of-game; if
        // it loses, the CAUGHT/ESCAPED screen lingers under the victory modal.
        setStreakerInteractive(null);
        // Cinematic pause: let the runner trot animation finish (350ms/hop, up to 4 hops on a
        // grand slam walk-off ≈ 1.4s) before the victory modal pops. Without this, the modal
        // appears the same instant runners start moving and steps on the moment.
        // If a resolution animation is still in flight, use a generous fallback delay so the
        // walkoff modal still appears even if the animation hook never clears (e.g. mid-play
        // disconnect). When the animation DOES clear, isResolutionAnimating flips → effect
        // re-fires → old timer cleared → fresh 1500ms timer set from that moment.
        const delay = isResolutionAnimating ? VICTORY_MODAL_FALLBACK_MS : VICTORY_MODAL_DELAY_MS;
        const t = setTimeout(() => setVictoryModal({ open: true, winner: gameOverResult }), delay);
        return () => clearTimeout(t);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.state?.inning, game?.state?.half, game?.state?.home_score, game?.state?.away_score, game?.state?.outs, victoryModal.open, isResolutionAnimating]);

  // Safety clear: if Lights Out somehow survives a half-inning flip, extinguish it then.
  // Primary clear happens on pitch_reveal (next roll resolution).
  useEffect(() => {
    if (lightsOutActive) {
      setLightsOutActive(false);
      if (lightsOutNotifId.current) { dismissNotif(lightsOutNotifId.current); lightsOutNotifId.current = null; }
    }
  // Only re-run when the inning/half actually changes, not on every lightsOutActive toggle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.state?.inning, game?.state?.half]);

  // gameOver gates all timers, auto-actions, and animations once the victory modal is up so
  // players can read the final state without further auto-rolls or expiry triggers firing.
  const gameOver = victoryModal.open;

  // Fire game_started once when the game object first loads.
  const gameStartedTracked = useRef(false);
  useEffect(() => {
    if (!game || gameStartedTracked.current) return;
    gameStartedTracked.current = true;
    track("game_started", {
      game_mode: game.game_mode ?? "unknown",
      innings: game.innings_total ?? 9,
      has_deck_draft: !!game.draft_enabled,
      has_player_draft: !!game.player_draft_enabled,
    });
  }, [game]);

  // Fire game_abandoned when the page unloads while the game is still in progress.
  useEffect(() => {
    if (!game || gameOver) return;
    const handleUnload = () => {
      track("game_abandoned", {
        game_mode: game.game_mode ?? "unknown",
        inning: game.state?.inning ?? 1,
        half: game.state?.half ?? "top",
        home_score: game.state?.home_score ?? 0,
        away_score: game.state?.away_score ?? 0,
      });
    };
    window.addEventListener("beforeunload", handleUnload);
    return () => window.removeEventListener("beforeunload", handleUnload);
  }, [game, gameOver]);

  // Catch-up detector: if the half-inning changes underneath us without going through
  // recordAndAdvance (e.g. the WS dropped during the at-bat and we missed the pitch_reveal
  // broadcast), open the hand-refresh modal anyway so both clients converge instead of one
  // being stuck on the previous half-inning's pitch select while the other waits in the modal.
  // Also gated on isResolutionAnimating so the modal doesn't pop while the user is still
  // watching the play that ended the half-inning. Also skip when the play ended the GAME —
  // gameOver flag is delayed by the 1.5s cinematic pause, so check isGameOver directly here.
  // ─────────────────────────────────────────────────────────────────────────────
  // Half-inning hand-refresh modal lifecycle (Phase 2 — server-driven).
  //
  // Single source of truth: state.hand_refresh = { phase, ready, opened_at_ms }.
  // Server populates the field on every half-flip in _persist_and_broadcast or
  // _apply_steal_outcome. Lifecycle:
  //
  //    null           — no modal activity
  //    phase: pending — half flipped, waiting for both clients to confirm their
  //                     resolution animation has played out (or 8s grace)
  //    phase: open    — both clients should render the modal
  //
  // Old whack-a-mole infra (catch-up effect, pendingHalfFlipRef, runnersSettledTick,
  // onHalfInningEnd→online branch) replaced by two effects below: one sends
  // `hand_refresh_animation_complete` when local animation finishes, the other
  // mirrors the modal flag from state.hand_refresh.phase.
  // ─────────────────────────────────────────────────────────────────────────────

  // Effect 1: when state.hand_refresh.phase === "pending" and our local resolution
  // animation has finished, tell the server we're ready for the modal. Send once
  // per half-flip (deduped by opened_at_ms). Server flips phase to "open" once both
  // sides confirm OR the 8s open-grace timer fires.
  const sentAnimationCompleteForRef = useRef(null);
  useEffect(() => {
    if (!isOnline) return;
    const hr = game?.state?.hand_refresh;
    if (!hr) {
      sentAnimationCompleteForRef.current = null;
      return;
    }
    if (hr.phase !== "pending") return;
    // Use the NARROWER `resolutionAnimationPlaying` flag here, NOT the broader
    // `isResolutionAnimating`. The latter stays true through state.hand_refresh's
    // entire lifecycle for freeze purposes — gating animation-complete on it would
    // create a deadlock (modal never opens because we never confirm because the
    // freeze is held because the modal hasn't opened…).
    if (resolutionAnimationPlaying) return;
    // Also wait for FieldingPhase to fully unmount before confirming. While we're
    // visually still in fielding (HR celebration, applyResult timeout in flight),
    // the play isn't "done" from the user's perspective.
    if (isOnline && currentPhase === "fielding") return;
    // Walk-off / game-end short-circuit: if the half-flip ended the game, victory
    // takes over and the modal should never open. Don't confirm.
    if (gameOver || isGameOver(game.state, game.innings_total || 9)) return;
    if (sentAnimationCompleteForRef.current === hr.opened_at_ms) return;

    const send = wsSendRef.current;
    if (send) send({ type: "hand_refresh_animation_complete" });
    sentAnimationCompleteForRef.current = hr.opened_at_ms;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.state?.hand_refresh, isOnline, resolutionAnimationPlaying, currentPhase, gameOver]);

  // Effect 2: mirror the modal-open flag from state.hand_refresh.phase. When the
  // server flips phase to "open", set up the modal locally (mySpent auto-discards,
  // ready-flags reset, active-cards reset). When the field clears (modal closing
  // path), nothing to do here — `hand_refresh_complete` WS event already drives
  // setOnlineHandRefreshOpen(false).
  useEffect(() => {
    if (!isOnline) return;
    const hr = game?.state?.hand_refresh;
    if (!hr || hr.phase !== "open") return;
    // Open exactly once per server-emitted open event (keyed by opened_at_ms).
    // This is the load-bearing guard — replaces the old `if (onlineHandRefreshOpen) return`
    // check, which couldn't tell a fresh open from a re-fire after close.
    if (lastOpenedAtRef.current === hr.opened_at_ms) return;
    if (gameOver || isGameOver(game.state, game.innings_total || 9)) return;
    lastOpenedAtRef.current = hr.opened_at_ms;
    setOnlineHandRefreshOpen(true);
    setMyHandReady(false);
    setOpponentHandReady(false);
    setActiveCards([]);
    // Pre-select dead cards for trade-back so players don't have to manually pick them.
    // "Dead" covers two categories:
    //   1. Spent instant cards — played this half-inning, no further use.
    //   2. Deactivated persistent cards — burned (manually deactivated) or expired
    //      (inning duration elapsed). Server force-discards these regardless, but
    //      pre-queuing them keeps the count badge and button text accurate.
    const teamHand = (game?.[`${myRole}_hand`] ?? []);
    const teamSpent = (game?.[`${myRole}_spent_cards`] ?? []);
    const teamActive = (game?.[`${myRole}_active_cards`] ?? []);
    const currentInning = game?.state?.inning ?? 1;

    const autoDiscardIds = teamSpent.filter(id => teamHand.includes(id));
    const autoActiveDiscardIds = teamActive
      .filter(a => {
        if (!a?.id) return false;
        if (!teamHand.includes(a.id)) return false;
        if (a.burned) return true;
        if (a.expires_after_inning != null && currentInning > a.expires_after_inning) return true;
        return false;
      })
      .map(a => a.id);

    setOnlineHandDiscards(autoDiscardIds);
    setOnlineActiveDiscards(autoActiveDiscardIds);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game?.state?.hand_refresh, isOnline, gameOver, myRole]);

  // Keep handRefreshOpenRef current so the stale WS handler closure can guard
  // against duplicate hand_refresh_complete events.
  useEffect(() => {
    handRefreshOpenRef.current = onlineHandRefreshOpen;
  }, [onlineHandRefreshOpen]);

  // Close all dismissible modals when the hand-refresh modal opens or when the
  // game phase changes. Prevents modals like the deactivate confirm or pitcher
  // sub dialog from getting stuck on top of the half-inning modal.
  useEffect(() => {
    setDeactivateConfirm(null);
    setStatsModalOpen(false);
    setPitcherDialogOpen(false);
    setBatterSubDialog({ open: false, player: null, index: null });
    setFielderSubDialog({ open: false, player: null });
    setPlayerDetailsDialog({ open: false, player: null });
    setMobileRosterOpen(false);
    setMobileLogOpen(false);
  }, [onlineHandRefreshOpen, currentPhase]);

  // Capture runs_scored from each fielding resolution so DiamondField can correctly
  // route scoring runners vs. force-outs in the trot animation.
  //
  // Timing contract:
  //   SET  — when fielding_resolution arrives (before holdBases releases)
  //   CLEAR — when the trot animation settles (onRunnerAnimationSettled), NOT when
  //            the resolution state clears. If we cleared on resolution-clear we would
  //            null out the ref in the same React batch that releases holdBases, so the
  //            trot effect would see null and fall back to a stale score delta of 0.
  //
  // The stale-ref-from-prior-out problem (runs_scored=0 persisting into the next
  // pitch-based play) is solved by clearing in the settled callback: by the time a
  // walk/HBP changes basesHash the trot from the previous at-bat will have already
  // settled and cleared the ref.
  const fieldingRunsScoredRef = useRef(null);
  // Outcome string ("hr", "double_play", "single", etc.) persisted across the same
  // resolution-clear boundary as fieldingRunsScoredRef — both are set when the
  // fielding_resolution arrives and cleared when the trot settles.
  const fieldingOutcomeRef = useRef(null);
  useEffect(() => {
    if (onlineFieldingResolution?.runs_scored != null) {
      let animRuns = onlineFieldingResolution.runs_scored;
      // Bases-loaded DP: the 3B runner is forced out AT home plate — they physically run
      // there even though no run scores. Routing them to pos=4 (home) in the trot animation
      // makes this clear. Only applies when all three bases were occupied (the only case
      // where the 3B runner is force-out at home; other DP configs don't involve home).
      if (
        onlineFieldingResolution.outcome === "double_play" &&
        onlineFieldingResolution.pre_bases_occupied?.third &&
        onlineFieldingResolution.pre_bases_occupied?.second &&
        onlineFieldingResolution.pre_bases_occupied?.first
      ) {
        animRuns = 1;
      }
      fieldingRunsScoredRef.current = animRuns;
      fieldingOutcomeRef.current = onlineFieldingResolution.outcome ?? null;
    }
  }, [onlineFieldingResolution]);

  // Auto-HR (contact ≥ 7): server skips fielding entirely, so onlineFieldingResolution is
  // never set. Mirror the same ref-setting here so DiamondField animates the batter
  // circling the bases when holdBases releases after the pitch-reveal animation finishes.
  useEffect(() => {
    if (
      onlinePitchReveal?.result_type === "in_play" &&
      (onlinePitchReveal?.contact || 0) >= 7
    ) {
      const preBases = onlinePitchReveal.pre_bases || {};
      const runnersOnBase = Object.values(preBases).filter(Boolean).length;
      fieldingRunsScoredRef.current = runnersOnBase + 1; // all runners + batter
      fieldingOutcomeRef.current = "hr";
      setRunnerAnimationActive(true);
    }
  }, [onlinePitchReveal]);

  const handleRunnerAnimationSettled = useCallback(() => {
    fieldingRunsScoredRef.current = null;
    fieldingOutcomeRef.current = null;
    setRunnerAnimationActive(false);
  }, []);

  // (Legacy `hand_refresh_open` WS-send removed in Phase 2. The server now reschedules
  // the close timer in `_flip_hand_refresh_to_open` at the moment phase actually flips
  // to "open" — so the player always gets the full close duration regardless of how
  // long the resolution animation took. The server-side `hand_refresh_open` message
  // handler is still wired up for backward compat but no client sends it.)

  // Hand-refresh modal 30s auto-ready countdown. Resets every time the modal opens (per
  // half-inning), pauses once I've readied up. On expiry, fires whatever's currently in
  // `onlineHandDiscards` — which always includes already-played cards (auto-discard) plus
  // anything the user manually selected before the timer ran out.
  useEffect(() => {
    if (!onlineHandRefreshOpen || myHandReady || gameOver) {
      setHandRefreshClock(null);
      return;
    }
    // Match the server's hand-refresh duration: 10s in fast mode, 30s otherwise. Keeping
    // these in sync means the modal's "Auto-ready in Xs" badge always matches when the
    // server's force-complete fires. No countdown in no_timers mode — player must act manually.
    if (game?.no_timers) return;
    const totalSeconds = game?.fast_mode ? 10 : 30;
    const deadline = Date.now() + totalSeconds * 1000;
    setHandRefreshClock(totalSeconds);
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setHandRefreshClock(remaining);
      if (remaining <= 0) {
        clearInterval(id);
        if (handRefreshReadyRef.current) handRefreshReadyRef.current();
      }
    };
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlineHandRefreshOpen, myHandReady, gameOver, game?.fast_mode, game?.no_timers]);

  const handlePitcherSub = async (pitcher) => {
    if (!pitcher?.id) { toast.error("Pitcher missing id — can't substitute"); return; }
    const teamType = game.state.half === "top" ? "home" : "away";
    try {
      await axios.put(`${API}/games/${gameId}/teams/${teamType}/pitcher/${pitcher.id}`);
      toast.success(`${pitcher.name} is now pitching!`);
      fetchGame();
      setPitcherDialogOpen(false);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "unknown error";
      console.error("[pitcher_sub] failed", { pitcher, teamType, detail, err });
      toast.error(`Failed to substitute pitcher: ${detail}`);
    }
  };

  const handleTogglePitcherSub = async (pitcherId) => {
    const teamType = game.state.half === "top" ? "home" : "away";
    try { await axios.put(`${API}/games/${gameId}/teams/${teamType}/players/${pitcherId}/toggle-sub`); fetchGame(); } catch { toast.error("Failed"); }
  };

  // Stable handler refs so memoized children (Scoreboard / DiamondField / RosterPanel)
  // can actually skip re-renders. These were inline closures in JSX before, which were
  // recreated every GameBoard render and defeated React.memo.
  const handleStatsClick = useCallback(() => setStatsModalOpen(true), []);
  const handlePitcherClick = useCallback(() => setPitcherDialogOpen(true), []);
  // Resolve a possibly-partial player (e.g. a baserunner stored on state.bases as
  // `{name, id}`) to the full player record from the team roster — so the player-card
  // dialog reads the same source whether you click a runner on the diamond or a name
  // in the roster panel. Without this, runner cards show no seasons / stats / mods
  // because state.bases entries are intentionally lightweight.
  const resolveFullPlayer = useCallback((player) => {
    if (!player || !game) return player;
    // If it already has the markers of a full record (seasons array OR a populated
    // stats object), trust it. The runner record is just `{name, id}`.
    if (Array.isArray(player.seasons) || (player.stats && Object.keys(player.stats).length)) {
      return player;
    }
    const allPlayers = [
      ...(game.home_team?.players || []),
      ...(game.away_team?.players || []),
    ];
    const match = allPlayers.find(p => p.id === player.id);
    return match || player;
  }, [game]);
  const handleFielderAction = useCallback((action, player) => {
    if (action === 'details') setPlayerDetailsDialog({ open: true, player: resolveFullPlayer(player) });
    else if (action === 'substitute' && player) setFielderSubDialog({ open: true, player });
  }, [resolveFullPlayer]);
  const handleDeclareSteal = useCallback((base) => {
    const send = wsSendRef.current;
    if (!isOnline || !send) return;
    const sent = send({ type: "steal_declare", base });
    if (!sent) {
      toast.error("Connection lost — try again");
      return;
    }
    // Confirm submission so the user doesn't sit on a quiet UI wondering if it
    // registered. Server may still reject (steal_rejected) — that path keeps its
    // own error toast. This is the optimistic "ack received" beat.
    toast.info("Steal declared — rolling…");
  }, [isOnline]);
  const handleSubPlayerFromRoster = useCallback(async (player, idx) => {
    await fetchGame();
    setBatterSubDialog({ open: true, player, index: idx });
  }, [fetchGame]);
  const handlePlayerDetails = useCallback((player) => {
    setPlayerDetailsDialog({ open: true, player: resolveFullPlayer(player) });
  }, [resolveFullPlayer]);

  // Ephemeral carry-over state pills — instant cards that set state flags rather
  // than living in active_cards. Synthesized from game.state so both players
  // always see what's hanging over the current at-bat. Resolver lives in
  // utils/activeModifiers.js so the active-manager-bar and any future spectator
  // overlays share one source of truth. Declared up here (above the early returns
  // for loading / empty-lineup states) so rules-of-hooks isn't violated.
  const ephemeralPills = useMemo(
    () => resolveActiveModifiers(game, myRole, allCards),
    [game, myRole, allCards]
  );

  const handleFielderSubstitute = async (newPlayer) => {
    const teamType = game.state.half === "top" ? "home" : "away";
    const oldPlayer = fielderSubDialog.player;
    if (!oldPlayer || !newPlayer) return;
    try {
      await axios.put(`${API}/games/${gameId}/teams/${teamType}/fielder-swap`, { out_id: oldPlayer.id, in_id: newPlayer.id });
      toast.success(`${newPlayer.name} in for ${oldPlayer.name} at ${oldPlayer.position}`);
      fetchGame();
      setFielderSubDialog({ open: false, player: null });
    } catch { toast.error("Failed to substitute fielder"); }
  };

  const handleBatterSubstitute = async (newPlayer) => {
    const teamType = game.state.half === "top" ? "away" : "home";
    const oldPlayer = batterSubDialog.player;
    try {
      // Mark old player as subbed out (permanent)
      await axios.put(`${API}/games/${gameId}/teams/${teamType}/players/${oldPlayer.id}/toggle-sub`);
      // Replace old player's ID with new player's ID in batting_order
      const team = game.state.half === "top" ? game.away_team : game.home_team;
      const newOrder = (team.batting_order || []).map(id => id === oldPlayer.id ? newPlayer.id : id);
      await axios.put(`${API}/games/${gameId}/teams/${teamType}/batting-order`, newOrder);
      toast.success(`${newPlayer.name} in for ${oldPlayer.name}`); fetchGame();
      setBatterSubDialog({ open: false, player: null, index: null });
    } catch { toast.error("Failed to substitute"); }
  };

  if (loading || !game) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-10 h-10 border-3 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  // ── Roleless online game: claim a side before anything renders ──────────────
  // `isOnline` comes from the fetched document but `myRole` comes from ?role=, and
  // ONLY myRole gates the online phase machine. Open an online game without the
  // param and the result is not a broken board — it is a silently WRONG one: no
  // socket is ever opened (useGameSocket bails on a null role), pvpPhase stays at
  // its "pitcher_select" initial value which no online branch matches, and the
  // component falls through to the local hot-seat resolution screen — manual
  // "Roll D10s" / "Call Ball" inputs for both players. Anything done there is
  // discarded, because the online guards make the local handlers no-ops.
  //
  // A playtester hit exactly this while recovering by URL and lost a batted ball to
  // it. Refuse to render the board without a side.
  if (isOnline && !myRole && !isSpectate) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
        <div className="bg-slate-900 border border-amber-500/40 rounded-xl p-8 max-w-sm w-full text-center space-y-4">
          <p className="text-white font-black text-lg">Which side are you?</p>
          <p className="text-xs text-slate-400">
            This is an online game, so it needs to know who you are before it can
            connect. Pick the side you were playing.
          </p>
          <div className="flex gap-2">
            {["away", "home"].map(r => (
              <button
                key={r}
                type="button"
                data-testid={`claim-role-${r}`}
                onClick={() => navigate(`/game/${gameId}?role=${r}`, { replace: true })}
                className="flex-1 px-3 py-2 rounded-lg bg-amber-500 hover:bg-amber-400 text-black font-black text-sm transition-colors"
              >
                {(r === "home" ? game.home_team?.name : game.away_team?.name) || r}
                <span className="block text-[10px] font-bold opacity-70 uppercase">{r}</span>
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="claim-role-spectate"
            onClick={() => navigate(`/game/${gameId}?role=home&spectate=true`, { replace: true })}
            className="text-[11px] font-bold text-slate-500 hover:text-slate-300 transition-colors"
          >
            Just watching
          </button>
        </div>
      </div>
    );
  }

  const battingTeamCheck = game.state.half === "top" ? game.away_team : game.home_team;
  const activeLineupCheck = battingTeamCheck.players.filter(p => p.position !== "P" && !p.subbed_out);
  if (activeLineupCheck.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-4">
        <div className="bg-slate-900 border border-red-500/40 rounded-xl p-8 max-w-sm w-full text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-red-500/10 border border-red-500/30 flex items-center justify-center mx-auto">
            <span className="text-red-400 text-xl font-black">!</span>
          </div>
          <div>
            <p className="text-white font-black text-lg">Empty Lineup</p>
            <p className="text-slate-400 text-sm mt-1">
              <span className="text-amber-400 font-bold">{battingTeamCheck.name}</span> has no active batters. Add at least 9 position players to continue.
            </p>
          </div>
          <button
            onClick={() => navigate(`/setup/${gameId}`)}
            className="w-full px-4 py-2.5 bg-amber-500 hover:bg-amber-400 text-black font-black text-sm rounded-lg transition-colors"
          >
            Return to Roster
          </button>
        </div>
      </div>
    );
  }

  const fieldingTeam = game.state.half === "top" ? game.home_team : game.away_team;
  const pitchers = fieldingTeam.players.filter(p => p.position === "P");
  const currentPitcher = pitchers.find(p => !p.subbed_out) || pitchers[0];

  // Bluff Values modal: block until every pitcher on this client's team(s) has assigned
  // secret chip values — ALL pitchers upfront at game start, not just the active one.
  // Online: only show for my own team's pitchers. Local: home all → away all.
  // bluffValuesSubmittedRef guards against a stale WS broadcast re-showing after PATCH.
  let _bluffModalRole = null;
  let _bluffModalPitcher = null;
  let _bluffModalPitchers = []; // all pitchers on the active role's team (for progress count)
  // Spectators (including bot-vs-bot watchers, who connect as ?role=home&spectate=true)
  // never assign bluff values — the server auto-assigns for bot pitchers via
  // _auto_assign_bluff_values. Without the !isSpectate guard, a bvb spectator
  // gets prompted to set "Got Your Number" values for a team that isn't theirs.
  if (game.bluff_values && !isSpectate) {
    if (isOnline && myRole) {
      const allMyPitchers = game[`${myRole}_team`]?.players?.filter(p => p.position === "P") || [];
      const _p = allMyPitchers.find(p => !p.pitch_chip_values && !bluffValuesSubmittedRef.current.has(p.id));
      if (_p) { _bluffModalRole = myRole; _bluffModalPitcher = _p; _bluffModalPitchers = allMyPitchers; }
    } else if (!isOnline) {
      // Local: all home pitchers first, then all away pitchers
      const homePitchers = game.home_team?.players?.filter(p => p.position === "P") || [];
      const awayPitchers = game.away_team?.players?.filter(p => p.position === "P") || [];
      const _hp = homePitchers.find(p => !p.pitch_chip_values && !bluffValuesSubmittedRef.current.has(p.id));
      const _ap = awayPitchers.find(p => !p.pitch_chip_values && !bluffValuesSubmittedRef.current.has(p.id));
      if (_hp) { _bluffModalRole = "home"; _bluffModalPitcher = _hp; _bluffModalPitchers = homePitchers; }
      else if (_ap) { _bluffModalRole = "away"; _bluffModalPitcher = _ap; _bluffModalPitchers = awayPitchers; }
    }
  }
  const showBluffValuesModal = !!_bluffModalPitcher;
  // Online-only: block gameplay until the opponent has also finished assigning values.
  // Local mode handles both teams sequentially so this is always true there.
  const opponentBluffReady = !isOnline || !game?.bluff_values
    ? true
    : myRole === "home" ? awayBluffReady : homeBluffReady;
  const handleBluffValuesDone = (chipValues) => {
    const role = _bluffModalRole;
    const pitcherId = _bluffModalPitcher?.id;
    if (pitcherId) bluffValuesSubmittedRef.current.add(pitcherId);
    setGame(prev => {
      if (!prev || !role) return prev;
      const teamKey = `${role}_team`;
      const team = prev[teamKey];
      const idx = team.players.findIndex(p => p.id === pitcherId);
      if (idx === -1) return prev;
      const players = [...team.players];
      players[idx] = { ...players[idx], pitch_chip_values: chipValues };
      return { ...prev, [teamKey]: { ...team, players } };
    });
  };

  // Current batter (for active-card dormant-state evaluation in the banner).
  // Same lookup the phase components do — non-pitcher in the batting team's
  // lineup at the current batter index.
  const battingTeamForBanner = game.state.half === "top" ? game.away_team : game.home_team;
  const battingLineupForBanner = (battingTeamForBanner.batting_order || [])
    .map(id => battingTeamForBanner.players.find(p => p.id === id))
    .filter(p => p && !p.subbed_out);
  const currentBatterIdxForBanner = (game.state.half === "top"
    ? game.state.away_batter_index
    : game.state.home_batter_index) ?? 0;
  const currentBatter = battingLineupForBanner.length > 0
    ? battingLineupForBanner[currentBatterIdxForBanner % battingLineupForBanner.length]
    : null;

  // Phase 5/6: persistent active cards in play, per side. Visible to both
  // players (opponent's are read-only). Mine include a Deactivate button — when
  // pressed, the card is "burned" server-side: no longer modifies rolls, can't
  // be re-activated, must be discarded at the next half-inning modal.
  //
  // Filter: remove burned entries (manually deactivated — they no longer affect
  // rolls and are just noise) and entries whose inning expiry has already passed
  // (server cleans these at the next hand-refresh modal, but hide them now).
  const _currentInning = game?.state?.inning || 1;
  const _filterActive = (arr) => (arr || []).filter(entry =>
    !entry.burned && !(entry.expires_after_inning != null && _currentInning > entry.expires_after_inning)
  );
  const oppRoleVal = myRole === "home" ? "away" : "home";
  const myActiveServer = isOnline && myRole && game ? _filterActive(game[`${myRole}_active_cards`]) : [];
  const oppActiveServer = isOnline && myRole && game ? _filterActive(game[`${oppRoleVal}_active_cards`]) : [];
  // Banner data for both online and local modes (not gated on myRole)
  const homeActiveBanner = _filterActive(game?.home_active_cards);
  const awayActiveBanner = _filterActive(game?.away_active_cards);

  const cardsById = (() => {
    const m = {};
    (allCards || []).forEach(c => { m[c.id] = c; });
    return m;
  })();
  // Plain function (not useCallback) — declared after early-return blocks above,
  // so a hook here would violate rules-of-hooks. Identity churn each render is
  // fine here; the button consuming it doesn't memoize on prop identity.
  const handleDeactivate = async (cardId) => {
    if (!cardId) return;
    const send = wsSendRef.current;
    if (send) {
      send({ type: "card_deactivate", card_id: cardId });
    } else {
      try {
        const res = await axios.post(`${API}/games/${gameId}/deactivate-card`, { card_id: cardId });
        if (res.data?.game) setGame(res.data.game);
      } catch (err) {
        console.error("[deactivateCard] failed", err);
      }
    }
  };
  // Phase 6: opens the confirmation modal instead of firing deactivate directly.
  // Hand-card ✕ button + banner ✕ both route through here so the warning is
  // shown once, consistently. Looks the card name up so the modal can show it.
  const requestDeactivate = (cardId) => {
    if (!cardId) return;
    // continuous_draw: block deactivation if card hasn't survived at least one pitch.
    if (game?.continuous_draw && myRole) {
      const activeList = game[`${myRole}_active_cards`] || [];
      const entry = activeList.find(a => a.id === cardId && !a.burned);
      if (entry && entry.pitches_at_activation != null) {
        const currentPitchCount = game?.state?.pitch_count ?? 0;
        if (currentPitchCount <= entry.pitches_at_activation) {
          toast.error("Card must be active for at least one pitch before deactivating");
          return;
        }
      }
    }
    const card = cardsById[cardId];
    setDeactivateConfirm({ id: cardId, name: card?.name || "Card" });
  };
  const confirmDeactivate = () => {
    if (deactivateConfirm?.id) handleDeactivate(deactivateConfirm.id);
    setDeactivateConfirm(null);
  };

  const toggleListener = async (cardId, role) => {
    if (!gameId || !cardId || !role) return;
    try {
      const res = await axios.post(`${API}/games/${gameId}/toggle-listener`, { role, card_id: cardId });
      // Immediately apply the returned active_listeners to local game state so that
      // subsequent onStateUpdate calls (which spread game.state) carry the arm forward.
      // In online mode the WS game_state broadcast also handles this; the two are idempotent.
      if (res.data?.active_listeners) {
        setGame(prev => ({
          ...prev,
          state: { ...(prev?.state || {}), active_listeners: res.data.active_listeners },
        }));
      }
    } catch (err) {
      console.error("[toggleListener] failed", err);
    }
  };

  // Active card bar helpers — shared by all four pill blocks in the "In Play" strip.
  const _myArmedCount = isOnline && myRole
    ? (game?.state?.active_listeners?.[myRole] || []).length
    : 0;
  const _dormancyReason = (card, teamRole) => {
    const ph = card.phase;
    if (ph === "pitch")    return `Dormant — pitching phase only (currently ${teamRole === "pitcher" ? "fielding" : "batting"})`;
    if (ph === "fielding") return "Dormant — fielding phase only";
    if (ph === "steal")    return "Dormant — steal phase only";
    return "Dormant — conditions not currently met";
  };

  // Single resolver: turns one raw active-card entry into a view-model the
  // banner can render directly. Returns null if the entry should be hidden
  // (unknown card id, or armed listener seen by the opponent in online mode).
  // This is the source of truth — both the local and online render paths now
  // share one derivation instead of duplicating it across four blocks.
  const _resolveActiveCard = (entry, ownerRole) => {
    const card = cardsById[entry.id];
    if (!card) return null;
    const isMine = isOnline && myRole ? ownerRole === myRole : false;
    if (isOnline && !isMine) {
      const oppListenerIds = new Set(game?.state?.active_listeners?.[ownerRole] || []);
      if (oppListenerIds.has(entry.id)) return null;
    }
    const battingRole = game?.state?.half === "bottom" ? "home" : "away";
    const teamRole = ownerRole === battingRole ? "batter" : "pitcher";
    const ctx = { phase: card.phase || "any", gameState: { ...(game?.state || {}), total_innings: game?.innings_total ?? 9, _discard_count: (game?.discard || []).length }, currentBatter, currentPitcher, teamRole };
    const effective = canPlayCard(card, ctx);
    const dormant = !effective;
    // In online mode, hide opponent's armed cards until their condition is actually met.
    if (isOnline && !isMine && dormant) return null;
    return {
      entry, card, ownerRole, isMine, teamRole, dormant,
      dormancyReason: dormant ? _dormancyReason(card, teamRole) : null,
      pinnedBatter: entry.pinned_batter_name || null,
      ttlInning: entry.expires_after_inning ?? null,
    };
  };

  // Team abbreviation badge — derived from team name (first letter of each word,
  // up to 3 chars). Falls back to HOM/AWY when no team data is loaded.
  const _teamAbbr = (role) => {
    const team = role === "home" ? game?.home_team : game?.away_team;
    const name = team?.name;
    if (!name) return role === "home" ? "HOM" : "AWY";
    const words = name.split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0] + (words[2]?.[0] || "")).toUpperCase().slice(0, 3);
    return name.slice(0, 3).toUpperCase();
  };

  const _activePills = (() => {
    if (isOnline && myRole) {
      return [
        ...myActiveServer.map(e => _resolveActiveCard(e, myRole)),
        ...oppActiveServer.map(e => _resolveActiveCard(e, oppRoleVal)),
      ].filter(Boolean);
    }
    return [
      ...homeActiveBanner.map(e => _resolveActiveCard(e, "home")),
      ...awayActiveBanner.map(e => _resolveActiveCard(e, "away")),
    ].filter(Boolean);
  })();

  return (
    <div data-testid="game-board" className="h-screen flex flex-col overflow-hidden bg-slate-950">
      {/* HR ball cinematic overlay — fixed-position, ducks behind the scoreboard nav (z-50)
          but appears over everything else (z-40). See HRBallEffect.jsx + effects.js. */}
      <HRBallEffect triggerKey={hrTriggerKey} power={hrPower} seed={hrSeed} />
      <LightsOutEffect active={lightsOutActive} />
      <StreakerEffect triggerKey={streakerTriggerKey} />

      {/* Scoreboard IS the top nav. While a resolution is animating, freeze the displayed
          state to its pre-resolution snapshot so scores / inning column / count don't update
          mid-animation and spoil the play. The server has already applied the new state to
          game.state under the hood — once the animation flag releases, the scoreboard catches up.
          z-[500] keeps it above the HR ball cinematic (z-[400]) so the ball flies
          "behind" the nav. HR ball was raised to z-[400] to clear baserunner tokens
          (z-100/200 in DiamondField) — without that bump, the ball animated UNDER
          a runner standing on second. */}
      <div className="relative z-[500]">
        <Scoreboard game={displayGame} gameId={gameId} onUpdate={fetchGame} />
        {/* Logo sits in the empty left space of the nav bar. Not a link — just branding.
            The last 'L' in BALL is a hidden trigger for the dev tools dropdown.
            No cursor change so it stays invisible. */}
        <div className="absolute left-2 top-0 h-full flex items-center pointer-events-none">
          <div className="relative pointer-events-auto flex items-center gap-1.5 font-black text-sm tracking-tight text-white select-none">
            <img src="/favicons32x32.png" alt="" className="h-5 w-5 pointer-events-none" draggable={false} />
            JUNK <span className="text-amber-400">BALL</span>
            {/* Invisible trigger on the last L — right edge of the logo */}
            <button
              onClick={() => setDevMenuOpen(o => !o)}
              className="absolute right-0 top-0 w-4 h-full cursor-default focus:outline-none"
              tabIndex={-1}
              aria-hidden="true"
            />
            {devMenuOpen && (
              <>
                {/* Click-away backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setDevMenuOpen(false)}
                />
                <div className="absolute top-full left-0 mt-1 z-50 bg-slate-900 border border-slate-700 rounded-lg shadow-2xl overflow-hidden min-w-[160px]">
                  <p className="text-[9px] text-slate-600 uppercase tracking-widest px-3 py-1.5 border-b border-slate-800 select-none">
                    Dev Tools
                  </p>
                  <div className="flex flex-col py-1">
                    <button
                      onClick={() => { setHrTriggerKey(k => k + 1); setDevMenuOpen(false); }}
                      className="flex items-center gap-2 px-3 py-1.5 text-left text-[11px] text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    >
                      <span>✨</span> HR Ball
                    </button>
                    <button
                      onClick={() => { setStreakerTriggerKey(k => k + 1); setDevMenuOpen(false); }}
                      className="flex items-center gap-2 px-3 py-1.5 text-left text-[11px] text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    >
                      <span>🏃</span> Streaker (Sim)
                    </button>
                    <button
                      onClick={() => {
                        // Dev tool: open the interactive Streak Yourself modal
                        // directly without going through the card/server flow.
                        // Random edge + seed each click so testing covers all
                        // four spawn directions over time. `myRole` becomes the
                        // initiator; if it's null (spectator / pre-join), fall
                        // back to "home" so the modal renders in initiator mode.
                        const edges = ["top", "bottom", "left", "right"];
                        const spawn = edges[Math.floor(Math.random() * 4)];
                        const opposite = { top: "bottom", bottom: "top", left: "right", right: "left" };
                        setStreakerInteractive({
                          initiator: myRole || "home",
                          spawn_edge: spawn,
                          exit_edge: opposite[spawn],
                          seed: Math.floor(Math.random() * 2_000_000_000),
                          started_at: Date.now(),
                          duration: 20,
                        });
                        setDevMenuOpen(false);
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-left text-[11px] text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    >
                      <span>🏃‍♂️</span> Streak Yourself
                    </button>
                    <button
                      onClick={() => { setLightsOutActive(true); setDevMenuOpen(false); }}
                      className="flex items-center gap-2 px-3 py-1.5 text-left text-[11px] text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    >
                      <span>💡</span> Lights Out
                    </button>
                    <button
                      onClick={() => { setHotdogTrigger({ key: Date.now(), role: myRole || (Math.random() < 0.5 ? "home" : "away") }); setDevMenuOpen(false); }}
                      className="flex items-center gap-2 px-3 py-1.5 text-left text-[11px] text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    >
                      <span>🌭</span> Hotdog
                    </button>
                    <button
                      onClick={() => {
                        pushNotif({ type: "hack_the_board", icon: "💻", accent: "violet", teamLabel: "You", title: "Hack the Board", body: "Hack the Board!", dismissMs: 5000 });
                        setDevMenuOpen(false);
                      }}
                      className="flex items-center gap-2 px-3 py-1.5 text-left text-[11px] text-slate-300 hover:bg-slate-800 hover:text-white transition-colors"
                    >
                      <span>📡</span> Hack the Board
                    </button>
                    <div className="border-t border-slate-800 my-1" />
                    <button
                      onClick={async () => {
                        const next = !game.fast_mode;
                        try {
                          await axios.put(`${API}/games/${gameId}/fast-mode`, { fast_mode: next });
                          setGame(prev => ({ ...prev, fast_mode: next }));
                          toast.success(next ? "Fast mode ON" : "Fast mode OFF");
                        } catch { toast.error("Failed to toggle fast mode"); }
                        setDevMenuOpen(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 text-left text-[11px] transition-colors ${game.fast_mode ? "text-amber-400 hover:bg-slate-800" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}
                    >
                      <span>⚡</span> Fast Mode {game.fast_mode ? "(ON)" : "(OFF)"}
                    </button>
                    <button
                      onClick={async () => {
                        const next = !game.no_timers;
                        try {
                          await axios.patch(`${API}/games/${gameId}/settings`, { no_timers: next });
                          setGame(prev => ({ ...prev, no_timers: next }));
                          toast.success(next ? "No Timers ON" : "No Timers OFF");
                        } catch { toast.error("Failed to toggle no timers"); }
                        setDevMenuOpen(false);
                      }}
                      className={`flex items-center gap-2 px-3 py-1.5 text-left text-[11px] transition-colors ${game.no_timers ? "text-sky-400 hover:bg-slate-800" : "text-slate-300 hover:bg-slate-800 hover:text-white"}`}
                    >
                      <span>∞</span> No Timers {game.no_timers ? "(ON)" : "(OFF)"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1 flex overflow-hidden">
        {/* Left: Play-by-Play Drawer + Field + Phase Controls */}
        <div className="flex-1 flex overflow-hidden">
          {/* Play-by-Play Drawer — desktop only. relative z-[460] keeps the
              drawer and its toggle button above the Lights Out overlay (z-[450]). */}
          <div className="hidden md:flex relative z-[460]">
            <PlayByPlayDrawer log={playLog} onClear={clearPlayLog} />
          </div>
          
          {/* Field + Phase Controls */}
          <div className="flex-1 flex flex-col overflow-hidden">
          {/* Diamond Field Area - fills remaining space */}
          <div data-testid="field-area" className="flex-1 relative min-h-0">
            {/* Top-right: online connectivity badge */}
            {isOnline && (
              <div className="absolute top-2 right-2 z-20 flex items-center gap-1 px-2 py-1 rounded-md bg-slate-800/60 border border-slate-700 text-[10px] font-bold">
                {wsReconnecting
                  ? <Loader2 size={10} className="text-amber-400 animate-spin" />
                  : wsConnected
                    ? <Wifi size={10} className="text-emerald-400" />
                    : <WifiOff size={10} className="text-red-400" />}
                <span className={wsReconnecting ? "text-amber-400" : wsConnected ? "text-emerald-400" : "text-red-400"}>
                  {wsReconnecting ? "Reconnecting…" : myRole === "home" ? "Home" : "Away"}
                </span>
                {!wsReconnecting && <><span className="text-slate-600">·</span>
                <span className={homeConnected ? "text-emerald-400" : "text-slate-600"}>H</span>
                <span className={awayConnected ? "text-emerald-400" : "text-slate-600"}>A</span></>}
                <span className="text-slate-600">·</span>
                <button
                  type="button"
                  data-testid="resync"
                  title="Re-request game state from the server"
                  aria-label="Resync game state"
                  disabled={resyncing}
                  onClick={() => {
                    setResyncing(true);
                    const ok = wsSendRef.current?.({ type: "request_state" });
                    if (ok === false) toast.error("Not connected — reconnecting…");
                    setTimeout(() => setResyncing(false), 1200);
                  }}
                  className="text-[10px] font-bold text-slate-400 hover:text-amber-400 transition-colors disabled:opacity-40"
                >
                  {resyncing ? "…" : "Resync"}
                </button>
              </div>
            )}

            {(() => {
              // Heat Map: replace diamond with strike-zone during pitcher/batter pitch phases.
              // Steal, pickoff, and fielding phases still show the diamond.
              // Auto-HR (contact ≥ 7) skips fielding entirely so currentPhase never flips to
              // "fielding" — suppress the heat map so the diamond is visible for the runner trot.
              const _STEAL_PHASES = new Set(["steal_select","steal_waiting","steal_resolution","pickoff_select","pickoff_roll"]);
              const _heatMapOn = !!displayGame?.heat_map;
              const _hrRunnerAnim = !!(
                onlinePitchReveal?.result_type === "in_play" &&
                (onlinePitchReveal?.contact || 0) >= 7
              );
              // All or Nothing active: show diamond center so the phase-nav animation is visible
              const _allOrNothing = !!displayGame?.state?.pending_all_or_nothing;
              const _showHeatMapCenter = _heatMapOn && currentPhase === "pitch" && !_STEAL_PHASES.has(pitchPvpPhase) && !_hrRunnerAnim && !_allOrNothing && !runnerAnimationActive;
              const _hmRevealMode = pitchPvpPhase === "resolution" || pitchPvpPhase === "reveal";
              const _hmReveal = onlinePitchReveal;
              const _hmPitcherPos = _hmRevealMode && _hmReveal?.heatmap_pitcher_x != null
                ? { x: _hmReveal.heatmap_pitcher_x, y: _hmReveal.heatmap_pitcher_y } : null;
              const _hmBatterPos = _hmRevealMode && _hmReveal?.heatmap_batter_x != null
                ? { x: _hmReveal.heatmap_batter_x, y: _hmReveal.heatmap_batter_y } : null;
              const _hmFieldingTeam = displayGame?.state?.half === "top" ? displayGame?.home_team : displayGame?.away_team;
              const _hmCurrentPitcher = _hmFieldingTeam?.players?.find(p => p.position === "P" && !p.subbed_out);
              const _hmChipCount = displayGame?.chip_arsenal ? 4 : (_hmCurrentPitcher?.pitch_chips || 4);
              const _hmBattingTeamKey = displayGame?.state?.half === "top" ? "away" : "home";
              const _hmRole = isOnline && myRole === _hmBattingTeamKey ? "batter" : "pitcher";
              // Pitch Arsenal: batters have unlimited chips and must not learn the
              // pitcher's remaining inventory. Detect batter viewers across both
              // online H2H and bot games (human-as-batter).
              // Compute depleted chip set for heat map dock (depleting_chips only —
              // pitch_inventory was removed; chip_arsenal exhaustion is handled by
              // its own _caSupplyDefaults flow inside PitchingPhase).
              const _hmDepletedChips = (() => {
                const d = new Set();
                if (displayGame?.depleting_chips) {
                  const pool = displayGame?.state?.pitcher_chip_pool;
                  if (pool) for (let n = 1; n <= _hmChipCount; n++) { if (!pool.includes(n)) d.add(n); }
                }
                return d.size > 0 ? d : null;
              })();

              // Shared diamond render (used both in the default path and as the
              // "field" tab when heat map is active).
              const fieldingTeamKey = game.state.half === "top" ? "home" : "away";
              const battingTeamKey = fieldingTeamKey === "home" ? "away" : "home";
              const canEditFielding = !isOnline || myRole === fieldingTeamKey;
              const canEditBatting = !isOnline || myRole === battingTeamKey;
              const _isBotVsBot = game?.bot_role === "both";
              const _diamondNode = (
                <DiamondField
                  game={displayGame}
                  currentPhase={currentPhase}
                  activePositions={activePositions}
                  gameStats={boxStats}
                  // No Timers must mean no countdown anywhere. DiamondField renders
                  // whenever this is non-null and has no guard of its own, which is how
                  // a FIELD CLOCK appeared in a no_timers game, counted to 0 and
                  // resolved nothing. Gating here covers pitch / batting / fielding /
                  // steal in one place.
                  pitchClock={(_showHeatMapCenter || game?.no_timers) ? null : pitchClock}
                  pitchClockKind={pitchClockSync?.kind || "pitch"}
                  holdBases={_isBotVsBot ? false : resolutionAnimationPlaying}
                  stepDuration={_isBotVsBot ? 120 : 350}
                  liveBases={game?.state?.bases}
                  fieldingRunsScored={fieldingRunsScoredRef.current}
                  fieldingOutcome={fieldingOutcomeRef.current}
                  canSteal={canEditBatting && isOnline && currentPhase === "pitch" && !!pitchClockSync && pitchClockSync.kind === "pitch" && !onlineHandRefreshOpen && !onlineStealActive && !onlineStealResolution}
                  canEditFielding={canEditFielding}
                  canEditBatter={canEditBatting}
                  onStatsClick={handleStatsClick}
                  onPitcherClick={handlePitcherClick}
                  onFielderAction={handleFielderAction}
                  onDeclareSteal={handleDeclareSteal}
                  onRunnerAnimationSettled={handleRunnerAnimationSettled}
                  hotdogTrigger={hotdogTrigger}
                  pickoffSelectMode={!!onlinePickoffSelect}
                  pickoffSelectedBase={onlinePickoffRoll?.target_base ?? null}
                  onPickoffSelectRunner={(() => {
                    if (!onlinePickoffSelect || !wsSend) return null;
                    const fieldingRole = game?.state?.half === "top" ? "home" : "away";
                    if (myRole !== fieldingRole) return null;
                    return (base) => wsSend({ type: "pickoff_target_select", base });
                  })()}
                  toughHopSelectMode={!!toughHopSelectInfo}
                  toughHopEligibleIds={toughHopSelectInfo?.eligibleIds ?? []}
                  toughHopSelectedId={toughHopSelectInfo?.selectedId ?? null}
                  toughHopColorHex={toughHopSelectInfo?.colorHex ?? null}
                  onToughHopSelect={toughHopSelectInfo?.onSelect ?? null}
                  directionalHittingSelectMode={!!directionalHittingSelectInfo}
                  directionalHittingEligibleIds={directionalHittingSelectInfo?.eligibleIds ?? []}
                  directionalHittingSelectedId={directionalHittingSelectInfo?.selectedId ?? null}
                  directionalHittingColorHex={directionalHittingSelectInfo?.colorHex ?? null}
                  onDirectionalHittingSelect={directionalHittingSelectInfo?.onSelect ?? null}
                />
              );

              if (_showHeatMapCenter) {
                const _hmClockKind = pitchClockSync?.kind || "pitch";
                // During reveal always stay on zone — chip positions must be visible.
                const _viewIsZone = _hmRevealMode || heatMapView === "zone";

                return (
                  <div className="relative h-full flex items-center justify-center p-2 pt-0">
                    {/* Main view — zone or field */}
                    <div className="h-full flex items-center justify-center w-full">
                      {_viewIsZone ? (
                        <HeatMapZone
                          role={_hmRole}
                          chipCount={_hmChipCount}
                          selectedChip={_hmRevealMode ? null : heatMapChip}
                          onChipSelect={_hmRevealMode ? undefined : setHeatMapChip}
                          placedPosition={_hmRevealMode ? null : heatMapPos}
                          onPlace={_hmRevealMode ? undefined : setHeatMapPos}
                          onPlaceFinal={_hmRevealMode ? undefined : (pos) => {
                            setHeatMapPos(pos);
                            setHeatMapDropSeq(n => n + 1);
                          }}
                          locked={pitchPvpPhase === "waiting_for_opponent"}
                          revealMode={_hmRevealMode}
                          pitcherPosition={_hmPitcherPos}
                          batterPosition={_hmBatterPos}
                          pitcherChip={_hmRevealMode ? _hmReveal?.pitcher_chip : null}
                          batterChip={_hmRevealMode ? _hmReveal?.batter_chip : null}
                          positionalOverlap={_hmRevealMode ? (_hmReveal?.heatmap_positional_overlap ?? null) : null}
                          typeMatch={_hmRevealMode ? (_hmReveal?.heatmap_type_match ?? null) : null}
                          pitchHistory={pitcherHeatHistory}
                          depletedChips={_hmDepletedChips}
                        />
                      ) : (
                        <div className="h-full flex items-center justify-center w-full">
                          {_diamondNode}
                        </div>
                      )}
                    </div>

                    {/* Zone/Field toggle — bottom-right, below connection pill */}
                    {!_hmRevealMode && (
                      <div className="absolute bottom-2 right-2 flex items-center gap-1 z-10">
                        <button
                          onClick={() => { heatMapAutoFieldRef.current = false; setHeatMapView("zone"); }}
                          className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold transition-colors ${heatMapView === "zone" ? "bg-amber-500/20 border border-amber-500/60 text-amber-300" : "bg-slate-800/60 border border-slate-700/50 text-slate-500 hover:text-slate-300 hover:border-slate-500"}`}
                        >
                          <span>◎</span> Zone
                        </button>
                        <button
                          onClick={() => { heatMapAutoFieldRef.current = false; setHeatMapView("field"); }}
                          className={`flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold transition-colors ${heatMapView === "field" ? "bg-amber-500/20 border border-amber-500/60 text-amber-300" : "bg-slate-800/60 border border-slate-700/50 text-slate-500 hover:text-slate-300 hover:border-slate-500"}`}
                        >
                          <span>⬨</span> Field
                        </button>
                      </div>
                    )}

                    {/* Pitch clock — same position as DiamondField (bottom-16 left-2) */}
                    {pitchClock !== null && !game?.no_timers && (
                      <div className={`absolute bottom-16 left-2 z-[460] bg-black/60 backdrop-blur-sm rounded-lg p-2 border min-w-[80px] text-center ${pitchClock <= 10 ? "border-red-500/60" : "border-slate-700/50"}`}>
                        <div className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">
                          {_hmClockKind === "pitch_batter" ? "BATTING TIMER" : "PITCH CLOCK"}
                        </div>
                        <div className={`font-mono font-black tabular-nums text-2xl ${pitchClock <= 10 ? "text-red-400 animate-pulse" : "text-slate-200"}`}>
                          {pitchClock}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }

              // Default: diamond field view (non-heat-map games or steal/HR/AoN phases).
              return (
                <div className="h-full flex items-center justify-center p-2 pt-10">
                  {_diamondNode}
                </div>
              );
            })()}
          </div>

          {/* Phase 5/6: Active (persistent) cards strip — anchored above phase controls so both
              teams see it while playing. Owner is called out by a team-abbreviation badge
              (HOM / AWY or first letters of the team name) on every pill. Pill colors:
                home owner → emerald (full when effective, faded when dormant)
                away owner → amber  (full when effective, faded when dormant)
              All pill state derives from `_resolveActiveCard` so it can't drift across modes. */}
          {(homeActiveBanner.length > 0 || awayActiveBanner.length > 0 || ephemeralPills.length > 0 || activeCards.some(c => !c.persistent) || recentInstantCards.length > 0 || game?.state?.last_oot_play) && (
            <div className="relative z-[460] bg-slate-900/80 px-3 py-1.5 flex items-center gap-3 text-[11px] flex-shrink-0 overflow-hidden">
              <span className="text-[9px] font-black uppercase tracking-widest text-slate-500 flex-shrink-0">
                In Play
                {_myArmedCount > 0 && (
                  <span className="ml-1 text-teal-400 normal-case tracking-normal font-bold">
                    ({_myArmedCount} armed)
                  </span>
                )}
              </span>
              <div className="flex-1 flex flex-wrap items-center gap-1.5">
                {_activePills.map(({ entry, card, ownerRole, isMine, dormant, dormancyReason, pinnedBatter, ttlInning }) => {
                  const isHome = ownerRole === "home";
                  const colorClass = isHome
                    ? (dormant ? "border-emerald-500/25 bg-emerald-500/5 text-emerald-400/60" : "border-emerald-500/40 bg-emerald-500/10 text-emerald-300")
                    : (dormant ? "border-amber-500/20 bg-amber-500/5 text-amber-400/50" : "border-amber-500/30 bg-amber-500/5 text-amber-300/80");
                  const rarityDot = { common: "bg-slate-400", rare: "bg-violet-400", iconic: "bg-yellow-400", exclusive: "bg-orange-400", junk_wax: "bg-sky-400" }[card.rarity] || "bg-slate-500";
                  const maxVal = card.max != null && card.max !== 0 ? (card.max > 0 ? `+${card.max}` : String(card.max)) : null;
                  // Online: only the viewer can deactivate their own cards. Local: either side can deactivate.
                  const canDeactivate = isOnline ? isMine : true;
                  const xHoverClass = isHome
                    ? "hover:bg-emerald-600/40 text-emerald-400/50 hover:text-emerald-200"
                    : "hover:bg-amber-600/40 text-amber-400/50 hover:text-amber-200";
                  const tooltip = dormant ? dormancyReason : (isMine ? "Your active card" : isOnline ? "Opponent's active card" : `${isHome ? "Home" : "Away"} card`);
                  const badgeColor = isHome ? "text-emerald-300/80" : "text-amber-300/80";
                  return (
                    <span
                      key={`${ownerRole}-${entry.id}`}
                      onMouseEnter={(e) => setBannerHover({ card, rect: e.currentTarget.getBoundingClientRect() })}
                      onMouseLeave={() => setBannerHover(null)}
                      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${colorClass}`}
                      title={tooltip}
                    >
                      <span className={`text-[8px] font-black uppercase tracking-wider ${badgeColor}`}>{_teamAbbr(ownerRole)}</span>
                      <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${rarityDot}`} />
                      <span className="font-bold">{card.name}</span>
                      {pinnedBatter && <span className="text-[7px] uppercase tracking-wider opacity-50">vs {pinnedBatter}</span>}
                      {!displayGame?.unlimited_stacking && maxVal && <span className="text-[8px] font-mono opacity-60">{maxVal}</span>}
                      {dormant
                        ? <span className="text-[8px] uppercase tracking-wider opacity-70">Dormant</span>
                        : ttlInning != null
                          ? <span className="text-[8px] uppercase tracking-wider opacity-70">Thru Inn {ttlInning}</span>
                          : null}
                      {canDeactivate && (
                        <button
                          onClick={(e) => { e.stopPropagation(); requestDeactivate(entry.id); }}
                          className={`ml-0.5 rounded-full p-0.5 transition-colors flex-shrink-0 ${xHoverClass}`}
                          title="Deactivate card"
                        >
                          <X size={8} />
                        </button>
                      )}
                    </span>
                  );
                })}
                {/* Ephemeral state-flag pills — instant cards whose effects are pending
                    (Plunk Them carry-over, Tensions High window, Lineup Protection buff,
                    BOGO Walk / HBP windows). Hoverable for card preview, no ✕ button. */}
                {ephemeralPills.map((pill) => {
                  const card = cardsById[pill.cardId];
                  const isMine = pill.owner === "mine";
                  const isWindow = pill.key === "bogo_walk" || pill.key === "hbp_window";
                  const isListenerPill = !!pill.isListener;
                  // Window pills and listener pills are hidden from the opponent in online
                  // play — both telegraph which card the opponent is holding.
                  if (isOnline && myRole && !isMine && (isWindow || isListenerPill)) return null;
                  const cls = isListenerPill
                    ? (isMine ? "border-teal-500/50 bg-teal-500/10 text-teal-300" : "border-teal-500/30 bg-teal-500/5 text-teal-300/70 opacity-70")
                    : isWindow
                    ? (isMine ? "border-sky-500/50 bg-sky-500/10 text-sky-300 animate-pulse" : "border-sky-500/30 bg-sky-500/5 text-sky-300/70 opacity-70 animate-pulse")
                    : (isMine ? "border-violet-500/40 bg-violet-500/10 text-violet-300" : "border-violet-400/25 bg-violet-500/5 text-violet-300/70 opacity-70");
                  return (
                    <span key={pill.key} onMouseEnter={(e) => card && setBannerHover({ card, rect: e.currentTarget.getBoundingClientRect() })} onMouseLeave={() => setBannerHover(null)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${cls}`} title={pill.description}>
                      {!isMine && <span className="text-[8px] uppercase tracking-wider opacity-70">Opp</span>}
                      <span className="font-bold">{pill.label}</span>
                      <span className="text-[8px] uppercase tracking-wider opacity-60">{pill.sublabel}</span>
                    </span>
                  );
                })}
                {/* Instant/one-roll cards played this pitch — non-persistent, orange to
                    signal they are ephemeral (gone after this roll). Both players see both
                    teams' cards so everyone knows what happened. Also includes cards tracked
                    via onInstantCardPlayed (mid-phase plays that bypass the reveal flow).
                    Cards already shown as an at-bat/inning pill are excluded to avoid
                    double-badging (e.g. Plunk Them showing both "THIS AT-BAT" and "THIS PITCH"). */}
                {(() => {
                  const _atBatPillIds = new Set(
                    ephemeralPills
                      .filter(p => p.sublabel === "this at-bat" || p.sublabel === "this inning")
                      .map(p => p.cardId)
                  );
                  return [
                    ...activeCards.filter(c => !c.persistent && !_atBatPillIds.has(c.id)),
                    ...recentInstantCards.filter(rc => !activeCards.some(a => a.id === rc.id) && !_atBatPillIds.has(rc.id)),
                  ];
                })().map(card => {
                  const battingRole = game?.state?.half === "bottom" ? "home" : "away";
                  const pitchingRole = battingRole === "home" ? "away" : "home";
                  const cardTeam = card._instantRole || (card.playedBy === "pitcher" ? pitchingRole : battingRole);
                  const isMine = isOnline && myRole ? cardTeam === myRole : null;
                  const teamLabel = isOnline && myRole
                    ? (isMine ? null : "Opp")
                    : (cardTeam === "home" ? "Home" : "Away");
                  const cls = isOnline && myRole
                    ? (isMine
                        ? "border-orange-500/40 bg-orange-500/10 text-orange-300"
                        : "border-orange-500/25 bg-orange-500/5 text-orange-300/70 opacity-70")
                    : (cardTeam === "home"
                        ? "border-orange-500/40 bg-orange-500/10 text-orange-300"
                        : "border-orange-400/30 bg-orange-500/5 text-orange-300/80 opacity-80");
                  return (
                    <span key={`instant-${card.id}`} onMouseEnter={(e) => setBannerHover({ card, rect: e.currentTarget.getBoundingClientRect() })} onMouseLeave={() => setBannerHover(null)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${cls}`} title={`${card.name} — played this pitch`}>
                      {teamLabel && <span className="text-[8px] uppercase tracking-wider opacity-70">{teamLabel}</span>}
                      <span className="font-bold">{card.name}</span>
                      <span className="text-[8px] uppercase tracking-wider opacity-60">This pitch</span>
                    </span>
                  );
                })}
                {/* Out-of-turn instant cards (Foul Play, Old Dog, Error Card, etc.) —
                    played between pitch phases so they never enter activeCards. Shown
                    until cleared by the next pitch/fielding resolution. */}
                {(() => {
                  const ootPlay = game?.state?.last_oot_play;
                  if (!ootPlay) return null;
                  const card = cardsById[ootPlay.card_id];
                  if (!card) return null;
                  const isMine = isOnline && myRole ? ootPlay.role === myRole : null;
                  const teamLabel = isOnline && myRole
                    ? (isMine ? null : "Opp")
                    : (ootPlay.role === "home" ? "Home" : "Away");
                  const cls = isOnline && myRole
                    ? (isMine
                        ? "border-orange-500/40 bg-orange-500/10 text-orange-300"
                        : "border-orange-500/25 bg-orange-500/5 text-orange-300/70 opacity-70")
                    : (ootPlay.role === "home"
                        ? "border-orange-500/40 bg-orange-500/10 text-orange-300"
                        : "border-orange-400/30 bg-orange-500/5 text-orange-300/80 opacity-80");
                  return (
                    <span key={`oot-${ootPlay.card_id}`} onMouseEnter={(e) => setBannerHover({ card, rect: e.currentTarget.getBoundingClientRect() })} onMouseLeave={() => setBannerHover(null)} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border ${cls}`} title={`${card.name} — played out of turn`}>
                      {teamLabel && <span className="text-[8px] uppercase tracking-wider opacity-70">{teamLabel}</span>}
                      <span className="font-bold">{card.name}</span>
                      <span className="text-[8px] uppercase tracking-wider opacity-60">Played</span>
                    </span>
                  );
                })()}
              </div>
            </div>
          )}
          {/* Banner hover preview portal — floats above any clipping ancestor. */}
          {bannerHover && typeof document !== "undefined" && createPortal(
            <CardHoverPreview card={bannerHover.card} rect={bannerHover.rect} isSpent={false} playable={true} unlimitedStacking={!!displayGame?.unlimited_stacking} />,
            document.body
          )}

          {/* Unified game notification stack — rendered at body level so it floats
              above all game UI without z-index conflicts with the card bar. */}
          {typeof document !== "undefined" && createPortal(
            <GameNotificationStack notifications={notifQueue} onDismiss={dismissNotif} />,
            document.body
          )}

          {/* Phase Controls — user-resizable bottom panel. Default min = 170 mobile / 220 desktop.
              relative z-[460]: keeps phase tabs, card hand, and phase badge above the
              Lights Out overlay (z-[450]) so they stay visible and fully interactive. */}
          <div
            className="flex-shrink-0 border-t border-slate-800 px-2 py-1 flex flex-col relative z-[460]"
            style={
              phasePanelHeight != null
                ? { height: phasePanelHeight, minHeight: phasePanelHeight }
                : undefined
            }
            data-default-height
          >
            {/* Resize handle — drag up to grow, down to shrink (clamped to min/max). */}
            <div
              role="separator"
              aria-orientation="horizontal"
              title="Drag to resize"
              onMouseDown={startPhasePanelResize}
              onTouchStart={startPhasePanelResize}
              className="absolute -top-2 left-0 right-0 h-5 md:h-2 z-30 cursor-row-resize group"
            >
              <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-10 h-1 rounded-full bg-slate-700 group-hover:bg-amber-500/60 transition-colors" />
            </div>
            {/* Tailwind h-[170px] / md:h-[220px] mirrors the *default* min when no custom
                height has been set yet. Once the user drags, the inline style above wins. */}
            <div className={phasePanelHeight == null ? "h-[170px] md:h-[220px] flex flex-col" : "flex-1 flex flex-col min-h-0"}>
            <PhaseNav
              currentPhase={currentPhase}
              setCurrentPhase={setCurrentPhase}
              viewerIsBatting={isOnline && myRole && myRole === (game.state.half === "top" ? "away" : "home")}
              // Fielding is only meaningful once a pitch has resolved in-play. Disable the
              // tab until either we're already on it (auto-advanced from PitchingPhase),
              // a pitchResult exists (in-play contact ready to field), or a fielding
              // resolution is animating.
              disabledPhaseIds={[
                ...(currentPhase === "fielding" || pitchResult?.contact != null || onlineFieldingResolution ? [] : ["fielding"]),
                ...(game.state.bases?.first || game.state.bases?.second || game.state.bases?.third ? [] : ["bases"]),
              ]}
            />
            <div className="flex-1 mt-2 min-h-0">
              {/* Spectator mode: bots drive the game — show a live-watch banner instead of interactive panels */}
              {isSpectate && (
                <div className="flex flex-col items-center justify-center h-full gap-3 px-4 text-center">
                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/30">
                    <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                    <span className="text-xs font-bold text-emerald-400 tracking-widest uppercase">Bot vs Bot — Watching Live</span>
                  </div>
                  <p className="text-xs text-slate-500">Play-by-play updates in real time. Open the log drawer to follow along.</p>
                </div>
              )}
              {/* Use displayGame (frozen state) so the pitch panel doesn't reveal the
                  NEXT inning's pitcher / batter / chip count while the diamond is still
                  holding the pre-resolution view. The freeze releases when the half-
                  refresh modal closes — at which point the panel updates in sync with
                  the diamond and scoreboard. Server resolution data still flows via
                  onlinePitchReveal / pitchResult etc. (those bypass the freeze). */}
              {!isSpectate && currentPhase === "pitch" && (
                <GameErrorBoundary>
                  <PitchingPhase key={`pitch-${phaseResetCounter}`} game={displayGame} gameId={gameId} onUpdate={onStateUpdate} setCurrentPhase={setCurrentPhase} setPitchResult={setPitchResult} onChipsUsed={updateChips} onRecordPlay={recordPlay} onLogPlay={addLogEntry}
                    isPvP={game.game_mode === "pvp" || isOnline}
                    phaseSync={phaseSync}
                    keyboardBlocked={!!(streakerInteractive || swapSpitOpen || showBluffValuesModal
                      || hiddenBallPending || studentOfGameModal || managersDecisionModal
                      || onlineHandRefreshOpen || victoryModal.open)}
                    isOnline={isOnline}
                    myRole={myRole}
                    wsSend={wsSend}
                    wsConnected={wsConnected}
                    onlinePitcherLocked={onlinePitcherLocked}
                    onlinePitcherReady={onlinePitcherReady}
                    onlinePitchReveal={onlinePitchReveal}
                    onClearPitchReveal={() => setOnlinePitchReveal(null)}
                    onResultRevealed={markResolutionRevealed}
                    onlineStealActive={onlineStealActive}
                    onlineStealOpponentLocked={onlineStealOpponentLocked}
                    onlineStealResolution={onlineStealResolution}
                    onClearStealResolution={() => setOnlineStealResolution(null)}
                    onlinePickoffSelect={onlinePickoffSelect}
                    onlinePickoffRoll={onlinePickoffRoll}
                    onClearPickoffRoll={() => {
                      const cardId = onlinePickoffRoll?.card_id;
                      if (cardId) setRecentInstantCards(prev => prev.filter(c => c.id !== cardId));
                      setOnlinePickoffRoll(null);
                    }}
                    onPitchClockUpdate={setPitchClock}
                    pitchClockSync={pitchClockSync}
                    handRefreshActive={onlineHandRefreshOpen}
                    gameOver={gameOver}
                    homeHand={displayHomeHand} awayHand={displayAwayHand} allCards={allCards}
                    homeSpentCards={homeSpentCards} awaySpentCards={awaySpentCards}
                    homeActiveCards={game.home_active_cards || []}
                    awayActiveCards={game.away_active_cards || []}
                    onDeactivateCard={requestDeactivate}
                    onCardsPlayed={onCardsPlayed} onHalfInningEnd={onHalfInningEnd}
                    onInstantCardPlayed={onInstantCardPlayed}
                    onHackTheBoardPlay={onHackTheBoardPlay}
                    onHrShow={triggerHrBall}
                    activeListeners={game.state?.active_listeners || {}}
                    onToggleListener={toggleListener}
                    onSpendLocalCards={onSpendLocalCards}
                    chipArsenal={!!displayGame?.chip_arsenal}
                    chipArsenalInfinite={displayGame?.chip_arsenal_infinite ?? 1}
                    chipArsenalModifiers={displayGame?.chip_arsenal_modifiers ?? [1, 1, 1, 1]}
                    heatMap={!!displayGame?.heat_map}
                    pitcherHeatHistory={pitcherHeatHistory}
                    heatMapChip={heatMapChip}
                    onHeatMapChipChange={setHeatMapChip}
                    heatMapPos={heatMapPos}
                    onHeatMapPosChange={setHeatMapPos}
                    heatMapDropSeq={heatMapDropSeq}
                    heatMapOverhangThreshold={displayGame?.heat_map_overhang_threshold ?? 20}
                    heatMapZoneWidthPct={displayGame?.heat_map_zone_width_pct ?? 100}
                    onPvpPhaseChange={setPitchPvpPhase}
                    bluffModalOpen={showBluffValuesModal}
                    opponentBluffReady={opponentBluffReady}
                    lockinReentry={lockinReentryCards}
                    onLockinReentryConsumed={() => setLockinReentryCards(null)}
                    rejectedCardId={rejectedCardId}
                    onRejectedCardClear={() => setRejectedCardId(null)}
                    pitchLockInRejectedSeq={pitchLockInRejectedSeq}
                  />
                </GameErrorBoundary>
              )}
              {/* Same freeze as PitchingPhase — defenders / batter token / bases on the
                  panel's local UI stay on the pre-resolution snapshot through the modal. */}
              {!isSpectate && currentPhase === "fielding" && (
                <GameErrorBoundary>
                  <FieldingPhase key={`field-${phaseResetCounter}`} game={displayGame} gameId={gameId} pitchResult={pitchResult} setCurrentPhase={setCurrentPhase} setActivePositions={setActivePositions} onUpdate={onStateUpdate} onRecordPlay={recordPlay} onLogPlay={addLogEntry}
                    activeCards={activeCards} onHalfInningEnd={onHalfInningEnd}
                    phaseSync={phaseSync}
                    onFieldingPhaseChange={setFieldingSubPhase}
                    keyboardBlocked={!!(streakerInteractive || swapSpitOpen || showBluffValuesModal
                      || hiddenBallPending || studentOfGameModal || managersDecisionModal
                      || onlineHandRefreshOpen || victoryModal.open)}
                    isOnline={isOnline}
                    myRole={myRole}
                    wsSend={wsSend}
                    wsConnected={wsConnected}
                    onlineFieldingLocked={onlineFieldingLocked}
                    onlineFieldingResolution={onlineFieldingResolution}
                    onClearFieldingResolution={() => setOnlineFieldingResolution(null)}
                    onResultRevealed={markResolutionRevealed}
                    homeHand={displayHomeHand} awayHand={displayAwayHand} allCards={allCards}
                    homeSpentCards={homeSpentCards} awaySpentCards={awaySpentCards}
                    onCardsPlayed={onCardsPlayed}
                    onInstantCardPlayed={onInstantCardPlayed}
                    onHackTheBoardPlay={onHackTheBoardPlay}
                    onPitchClockUpdate={setPitchClock}
                    pitchClockSync={pitchClockSync}
                    gameOver={gameOver}
                    onHrShow={triggerHrBall}
                    handRefreshActive={onlineHandRefreshOpen}
                    homeActiveCards={game.home_active_cards || []}
                    awayActiveCards={game.away_active_cards || []}
                    onDeactivateCard={requestDeactivate}
                    onToughHopModeChange={setToughHopSelectInfo}
                    onDirectionalHittingModeChange={setDirectionalHittingSelectInfo}
                    activeListeners={game.state?.active_listeners || {}}
                    onToggleListener={toggleListener}
                  />
                </GameErrorBoundary>
              )}
              {currentPhase === "bases" && <BaserunnersPanel game={displayGame} />}
            </div>
            </div>
          </div>

          {/* Mobile bottom bar */}
          <div className="md:hidden flex-shrink-0 flex items-center justify-around border-t border-slate-800 bg-slate-950 h-11 px-4">
            <button onClick={() => setMobileLogOpen(true)} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-white font-bold transition-colors">
              <List size={14} /> Log {playLog.length > 0 && <span className="text-amber-400 ml-0.5">({playLog.length})</span>}
            </button>
          </div>
          </div>
        </div>

        {/* Right: Roster Panel — desktop only. relative z-[460] keeps the
            roster slide-out above the Lights Out overlay (z-[450]). */}
        <div className="hidden md:block relative z-[460]">
          {(() => {
            const fieldingTeamKey = game.state.half === "top" ? "home" : "away";
            const battingTeamKey = fieldingTeamKey === "home" ? "away" : "home";
            const canEditFielding = !isOnline || myRole === fieldingTeamKey;
            const canEditBatting = !isOnline || myRole === battingTeamKey;
            return (
              <RosterPanel
                game={displayGame}
                onPitcherClick={handlePitcherClick}
                onSubPlayer={handleSubPlayerFromRoster}
                onPlayerDetails={handlePlayerDetails}
                onPitcherSubstitute={handlePitcherClick}
                canEditPitcher={canEditFielding}
                canEditBatters={canEditBatting}
              />
            );
          })()}
        </div>
      </main>

      {/* Mobile: Roster modal */}
      {mobileRosterOpen && (
        <div className="md:hidden fixed inset-0 z-[1000] bg-slate-950 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 flex-shrink-0">
            <span className="font-black text-sm">Roster</span>
            <button onClick={() => setMobileRosterOpen(false)} className="text-slate-400 hover:text-white transition-colors"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {(() => {
              const fieldingTeamKey = game.state.half === "top" ? "home" : "away";
              const battingTeamKey = fieldingTeamKey === "home" ? "away" : "home";
              const canEditFielding = !isOnline || myRole === fieldingTeamKey;
              const canEditBatting = !isOnline || myRole === battingTeamKey;
              return (
                <RosterPanel
                  game={displayGame}
                  onPitcherClick={() => { setPitcherDialogOpen(true); setMobileRosterOpen(false); }}
                  onSubPlayer={async (player, idx) => { await fetchGame(); setBatterSubDialog({ open: true, player, index: idx }); setMobileRosterOpen(false); }}
                  onPlayerDetails={(player) => { setPlayerDetailsDialog({ open: true, player: resolveFullPlayer(player) }); setMobileRosterOpen(false); }}
                  onPitcherSubstitute={() => { setPitcherDialogOpen(true); setMobileRosterOpen(false); }}
                  canEditPitcher={canEditFielding}
                  canEditBatters={canEditBatting}
                />
              );
            })()}
          </div>
        </div>
      )}

      {/* Mobile: Log modal */}
      {mobileLogOpen && (
        <div className="md:hidden fixed inset-0 z-[1000] bg-slate-950 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800 flex-shrink-0">
            <span className="font-black text-sm">Play-by-Play</span>
            <button onClick={() => setMobileLogOpen(false)} className="text-slate-400 hover:text-white transition-colors"><X size={18} /></button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-2">
            {playLog.length === 0 ? (
              <p className="text-slate-600 text-xs text-center py-8">No plays yet</p>
            ) : (
              <div className="flex flex-col gap-1">
                {[...playLog].reverse().map((entry) => (
                  <div key={entry.id} className="text-xs py-2 border-b border-slate-800/50 flex items-start gap-2">
                    <span className="text-slate-500 flex-shrink-0">{entry.half === 'top' ? '▲' : '▼'}{entry.inning}</span>
                    <span className="text-slate-300">{entry.desc}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          {playLog.length > 0 && (
            <div className="border-t border-slate-800 p-3 flex-shrink-0">
              <button onClick={() => { clearPlayLog(); setMobileLogOpen(false); }} className="w-full text-xs text-slate-500 hover:text-red-400 transition-colors py-1">Clear Log</button>
            </div>
          )}
        </div>
      )}

      {/* Online — Half-Inning Break Modal (continuous_draw mode: roster break, no card management) */}
      {onlineHandRefreshOpen && isOnline && myRole && !isSpectate &&
        game?.state?.hand_refresh?.mode === "break" && (
        <HalfInningBreakModal
          game={game}
          myRole={myRole}
          isOnline={isOnline}
          myReady={myHandReady}
          opponentReady={opponentHandReady}
          clock={handRefreshClock}
          onReady={() => {
            if (myHandReady) return;
            if (handRefreshInflightRef.current) return;
            handRefreshInflightRef.current = true;
            setMyHandReady(true);
            const sent = wsSend({ type: "hand_refresh_done" });
            if (!sent) {
              setMyHandReady(false);
              toast.error("Connection lost — try ready again in a sec");
            }
            handRefreshInflightRef.current = false;
          }}
          onRequestPitcherSub={() => setPitcherDialogOpen(true)}
          onRequestBatterSub={() => setBatterSubDialog({ open: true, player: null, index: null })}
        />
      )}

      {/* Online — Hand Refresh Modal (shown to BOTH players after each half-inning) */}
      {onlineHandRefreshOpen && isOnline && myRole && !isSpectate &&
        game?.state?.hand_refresh?.mode !== "break" && (() => {
        const myTeamKey = myRole;
        const myHand = myTeamKey === "home" ? homeHand : awayHand;
        const mySpent = myTeamKey === "home" ? homeSpentCards : awaySpentCards;
        // All cards from this half-inning: current hand + consumed played cards
        const myDisplayHand = myTeamKey === "home" ? displayHomeHand : displayAwayHand;
        const myActiveCardsList = game?.[`${myTeamKey}_active_cards`] || [];
        const activeCardIds = new Set(myActiveCardsList.filter(a => !a.burned).map(a => a.id).filter(Boolean));
        const burnedActiveIds = new Set(myActiveCardsList.filter(a => a.burned).map(a => a.id).filter(Boolean));

        const handleToggle = (cardId) => {
          setOnlineHandDiscards(prev =>
            prev.includes(cardId) ? prev.filter(id => id !== cardId) : [...prev, cardId]
          );
        };

        const handleReadyUp = async () => {
          if (myHandReady) return;
          if (handRefreshInflightRef.current) return;
          handRefreshInflightRef.current = true;
          try {
            const res = await axios.post(`${API}/games/${gameId}/hand/${myTeamKey}/refresh`, {
              discard: onlineHandDiscards,
              discard_active: onlineActiveDiscards,
            });
            const newIds = res.data.hand;
            const resolved = newIds.map(id => allCards.find(c => c.id === id)).filter(Boolean);
            if (myTeamKey === "home") { setHomeHand(resolved); setGame(prev => ({ ...prev, home_hand: newIds })); }
            else { setAwayHand(resolved); setGame(prev => ({ ...prev, away_hand: newIds })); }
            setMyHandReady(true);
            const sent = wsSend({ type: "hand_refresh_done" });
            if (!sent) {
              setMyHandReady(false);
              toast.error("Connection lost — try ready again in a sec");
            }
          } catch (err) {
            const msg = err?.response?.status === 429
              ? "Server's catching up — try again in a moment"
              : "Failed to refresh hand";
            toast.error(msg);
          } finally {
            handRefreshInflightRef.current = false;
          }
        };
        handRefreshReadyRef.current = handleReadyUp;

        const myTeamName = myTeamKey === "home" ? game.home_team.name : game.away_team.name;
        const opponentTeamName = myTeamKey === "home" ? game.away_team.name : game.home_team.name;
        const discardCount = onlineHandDiscards.length + onlineActiveDiscards.length;
        // `state.half` has already flipped to the upcoming half by the time this modal renders.
        // Compute the half/inning that JUST ENDED:
        //   - state.half="bottom" → top of THIS inning just ended
        //   - state.half="top"    → bottom of the PREVIOUS inning just ended
        const justEndedHalf = game.state.half === "bottom" ? "Top" : "Bottom";
        const justEndedInning = game.state.half === "bottom" ? game.state.inning : Math.max(1, (game.state.inning || 1) - 1);
        const inningOver = `${justEndedHalf} of ${justEndedInning}`;

        return (
          <div className="fixed inset-0 z-[1000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onKeyDown={(e) => { if (e.key === "Enter" && !myHandReady) handleReadyUp(); }}
            tabIndex={-1}
          >
            <div className="bg-slate-950 border border-slate-700 rounded-2xl shadow-2xl flex flex-col w-full max-w-md max-h-[85vh] overflow-hidden">
              <div className="flex items-center justify-between px-5 py-3 border-b border-slate-800 flex-shrink-0">
                <div>
                  <p className="text-[10px] font-bold text-amber-400 uppercase tracking-widest">Half-Inning Over</p>
                  <p className="font-black text-white">{myTeamName} — Your Cards</p>
                  <p className="text-[10px] text-slate-500 mt-0.5">{inningOver} complete · tap any card to trade back</p>
                </div>
                <div className="text-right">
                  {discardCount > 0 && <p className="text-[10px] text-red-400">Trading back {discardCount}</p>}
                  {!myHandReady && handRefreshClock !== null && handRefreshClock > 0 && (
                    <p className={`text-[10px] tabular-nums mt-0.5 font-bold ${handRefreshClock <= 5 ? "text-red-400 animate-pulse" : "text-slate-400"}`}>
                      Auto-ready in {handRefreshClock}s
                    </p>
                  )}
                  {opponentHandReady && !myHandReady && (
                    <p className="text-[10px] text-amber-400 mt-0.5 font-bold">{opponentTeamName} ready ✓</p>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-5 space-y-2 min-h-0">
                {myDisplayHand.length === 0 && (
                  <p className="text-slate-600 text-sm text-center py-4">No cards this half-inning.</p>
                )}
                {myDisplayHand.map(card => {
                  const isSpentCard = mySpent.includes(card.id);
                  const isBurnedActive = burnedActiveIds.has(card.id);
                  const isActive = activeCardIds.has(card.id);
                  const isTrading = onlineHandDiscards.includes(card.id);
                  const isTradingActive = onlineActiveDiscards.includes(card.id);
                  // A spent card that isn't currently active or burned-active is a played
                  // instant — it can't be kept. Render as non-interactive auto-trade.
                  const isPlayedCard = isSpentCard && !isActive && !isBurnedActive;

                  if (isBurnedActive) {
                    return (
                      <div key={card.id} className="w-full text-left rounded-xl border border-red-500/20 bg-red-500/5 p-4 opacity-60 cursor-default">
                      data-testid={`refresh-card-${card.id}`}
                      data-card-id={card.id}
                      data-interactive="false"
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-black text-sm text-slate-400">{card.name}</p>
                          <span className="text-[10px] font-bold text-red-400/70 uppercase">Burned — Auto-trading back</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{card.description}</p>
                        <CardMeta card={card} />
                      </div>
                    );
                  }

                  if (isPlayedCard) {
                    return (
                      <div key={card.id} className="w-full text-left rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 opacity-55 cursor-default">
                      data-testid={`refresh-card-${card.id}`}
                      data-card-id={card.id}
                      data-interactive="false"
                        <div className="flex items-center justify-between gap-2">
                          <p className="font-black text-sm text-slate-400">{card.name}</p>
                          <span className="text-[10px] font-bold text-slate-500 uppercase">Played — Auto-trading back</span>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">{card.description}</p>
                        <CardMeta card={card} />
                      </div>
                    );
                  }

                  if (isActive) {
                    return (
                      <button
                        key={card.id}
                        data-testid={`refresh-card-${card.id}`}
                        data-card-id={card.id}
                        data-interactive="true"
                        onClick={() => !myHandReady && setOnlineActiveDiscards(prev =>
                          isTradingActive ? prev.filter(id => id !== card.id) : [...prev, card.id]
                        )}
                        disabled={myHandReady}
                        className={`w-full text-left rounded-xl border p-4 transition-all ${
                          isTradingActive ? "border-red-500/50 bg-red-500/10 opacity-70"
                          : "border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-400/50"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <p className="font-black text-sm text-white">{card.name}</p>
                            {!isTradingActive && <span className="text-[8px] font-black px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 uppercase tracking-wider">Active</span>}
                          </div>
                          {isTradingActive && <span className="text-[10px] font-bold text-red-400 uppercase">Trade Back</span>}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{card.description}</p>
                        <CardMeta card={card} />
                      </button>
                    );
                  }

                  return (
                    <button
                      key={card.id}
                      data-testid={`refresh-card-${card.id}`}
                      data-card-id={card.id}
                      data-interactive="true"
                      onClick={() => !myHandReady && handleToggle(card.id)}
                      disabled={myHandReady}
                      className={`w-full text-left rounded-xl border p-4 transition-all ${
                        isTrading ? "border-red-500/50 bg-red-500/10 opacity-70"
                        : "border-slate-700 bg-slate-900/60 hover:border-slate-500"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-black text-sm text-white">{card.name}</p>
                        {isTrading && isSpentCard && <span className="text-[10px] font-bold text-amber-400 uppercase">Used · Auto Trade</span>}
                        {isTrading && !isSpentCard && <span className="text-[10px] font-bold text-red-400 uppercase">Trade Back</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{card.description}</p>
                      <CardMeta card={card} />
                    </button>
                  );
                })}
              </div>

              <div className="border-t border-slate-800 px-5 py-3 flex-shrink-0">
                {myHandReady ? (
                  <div className="space-y-2 text-center">
                    <p className="text-emerald-400 font-black text-sm">✓ Ready — waiting for {opponentTeamName}…</p>
                    <div className="w-5 h-5 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin mx-auto" />
                  </div>
                ) : (
                  /* data-testid: actionSelector for hand_refresh_ready_up names
                     this testid. It existed only on HalfInningBreakModal's
                     button, but the control actually on screen during a hand
                     refresh is THIS one — so the selector resolved to nothing
                     and the state invariant I2 ("actionSelector matches no
                     element") fired twice an inning against a real defect. */
                  <button
                    onClick={handleReadyUp}
                    data-testid="half-inning-ready"
                    className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-sm transition-all"
                  >
                    {discardCount > 0 ? `Trade Back ${discardCount} · Ready Up →` : "Ready Up →"}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* PvP — Hand Refresh Modal */}
      {handRefreshPhase && (() => {
        const isPitchingTeamPhase = handRefreshPhase === "pitching_team";
        const isCoverPhase = handRefreshPhase === "cover_between";
        const isBattingTeamPhase = handRefreshPhase === "batting_team";

        // After inning switch, game.state.half is already updated
        const pitchingTeamKey = game.state.half === "top" ? "home" : "away";
        const battingTeamKey = pitchingTeamKey === "home" ? "away" : "home";
        const pitchingTeamName = (pitchingTeamKey === "home" ? game.home_team : game.away_team).name;
        const battingTeamName = (battingTeamKey === "home" ? game.home_team : game.away_team).name;

        const activeTeamKey = isPitchingTeamPhase ? pitchingTeamKey : battingTeamKey;
        const activeTeamName = isPitchingTeamPhase ? pitchingTeamName : battingTeamName;
        const activeHand = activeTeamKey === "home" ? homeHand : awayHand;
        // All cards from this half-inning (current hand + consumed played cards)
        const pvpDisplayHand = activeTeamKey === "home" ? displayHomeHand : displayAwayHand;
        const pvpActiveCardsList = game?.[`${activeTeamKey}_active_cards`] || [];
        const pvpActiveCardIds = new Set(pvpActiveCardsList.filter(a => !a.burned).map(a => a.id).filter(Boolean));
        const pvpBurnedActiveIds = new Set(pvpActiveCardsList.filter(a => a.burned).map(a => a.id).filter(Boolean));
        const pvpSpentIds = new Set(activeTeamKey === "home" ? homeSpentCards : awaySpentCards);
        // Played cards that are still in the hand but can't be kept — auto-traded back.
        const pvpForcedDiscards = pvpDisplayHand
          .filter(c => pvpSpentIds.has(c.id) && !pvpActiveCardIds.has(c.id) && !pvpBurnedActiveIds.has(c.id))
          .map(c => c.id);

        if (isCoverPhase) {
          return (
            <div className="fixed inset-0 z-[1000] bg-slate-950 flex flex-col items-center justify-center gap-6 px-6">
              <div className="w-16 h-16 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center">
                <X size={24} className="text-slate-500" />
              </div>
              <div className="text-center">
                <p className="text-xl font-black text-white">Pass the device</p>
                <p className="text-slate-400 text-sm mt-1">Hand off to <span className="text-amber-400 font-bold">{battingTeamName}</span> to manage their hand.</p>
              </div>
              <button
                onClick={() => { setHandRefreshPhase("batting_team"); setHandDiscards([]); setActiveDiscards([]); }}
                className="px-8 py-3 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-sm transition-all"
              >
                I'm Ready →
              </button>
            </div>
          );
        }

        return (
          <div className="fixed inset-0 z-[1000] bg-slate-950 flex flex-col overflow-y-auto">
            <div className="px-6 py-4 border-b border-slate-800 flex-shrink-0">
              <p className="text-[10px] font-bold text-amber-400 uppercase tracking-widest">Half-Inning Over</p>
              <p className="font-black text-white">{activeTeamName} — Your Cards</p>
              <p className="text-[11px] text-slate-500 mt-0.5">Tap any card to trade back · replacements drawn for each</p>
            </div>
            <div className="flex-1 p-6 space-y-3 max-w-md mx-auto w-full">
              {pvpDisplayHand.length === 0 && <p className="text-slate-600 text-sm text-center py-4">No cards this half-inning.</p>}
              {pvpDisplayHand.map(card => {
                const isConsumed = !activeHand.find(h => h.id === card.id);
                const isBurnedActive = pvpBurnedActiveIds.has(card.id);
                const isActive = pvpActiveCardIds.has(card.id);
                const isPlayedCard = pvpSpentIds.has(card.id) && !isActive && !isBurnedActive && !isConsumed;
                const isTrading = handDiscards.includes(card.id);
                const isTradingActive = activeDiscards.includes(card.id);

                if (isConsumed) {
                  return (
                    <div key={card.id} className="w-full text-left rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 opacity-50 cursor-default">
                    data-testid={`refresh-card-${card.id}`}
                    data-card-id={card.id}
                    data-interactive="false"
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-black text-sm text-slate-400">{card.name}</p>
                        <span className="text-[10px] font-bold text-slate-500 uppercase">Used — Traded Back</span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{card.description}</p>
                      <CardMeta card={card} />
                    </div>
                  );
                }

                if (isBurnedActive) {
                  return (
                    <div key={card.id} className="w-full text-left rounded-xl border border-red-500/20 bg-red-500/5 p-4 opacity-60 cursor-default">
                    data-testid={`refresh-card-${card.id}`}
                    data-card-id={card.id}
                    data-interactive="false"
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-black text-sm text-slate-400">{card.name}</p>
                        <span className="text-[10px] font-bold text-red-400/70 uppercase">Burned — Auto-trading back</span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{card.description}</p>
                      <CardMeta card={card} />
                    </div>
                  );
                }

                if (isPlayedCard) {
                  return (
                    <div key={card.id} className="w-full text-left rounded-xl border border-slate-700/40 bg-slate-900/30 p-4 opacity-55 cursor-default">
                    data-testid={`refresh-card-${card.id}`}
                    data-card-id={card.id}
                    data-interactive="false"
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-black text-sm text-slate-400">{card.name}</p>
                        <span className="text-[10px] font-bold text-slate-500 uppercase">Played — Auto-trading back</span>
                      </div>
                      <p className="text-xs text-slate-600 mt-1">{card.description}</p>
                      <CardMeta card={card} />
                    </div>
                  );
                }

                if (isActive) {
                  return (
                    <button
                      key={card.id}
                      data-testid={`refresh-card-${card.id}`}
                      data-card-id={card.id}
                      data-interactive="true"
                      onClick={() => setActiveDiscards(prev => isTradingActive ? prev.filter(id => id !== card.id) : [...prev, card.id])}
                      className={`w-full text-left rounded-xl border p-4 transition-all ${
                        isTradingActive ? "border-red-500/50 bg-red-500/10 opacity-60"
                        : "border-emerald-500/30 bg-emerald-500/5 hover:border-emerald-400/50"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <p className="font-black text-sm text-white">{card.name}</p>
                          {!isTradingActive && <span className="text-[8px] font-black px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 uppercase tracking-wider">Active</span>}
                        </div>
                        {isTradingActive && <span className="text-[10px] font-bold text-red-400 uppercase">Trade Back</span>}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">{card.description}</p>
                      <CardMeta card={card} />
                    </button>
                  );
                }

                return (
                  <button
                    key={card.id}
                    data-testid={`refresh-card-${card.id}`}
                    data-card-id={card.id}
                    data-interactive="true"
                    onClick={() => setHandDiscards(prev => isTrading ? prev.filter(id => id !== card.id) : [...prev, card.id])}
                    className={`w-full text-left rounded-xl border p-4 transition-all ${
                      isTrading ? "border-red-500/50 bg-red-500/10 opacity-60"
                      : "border-slate-700 bg-slate-900/60 hover:border-slate-500"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-black text-sm text-white">{card.name}</p>
                      {isTrading && <span className="text-[10px] font-bold text-red-400 uppercase">Trade Back</span>}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">{card.description}</p>
                    <CardMeta card={card} />
                  </button>
                );
              })}
            </div>
            <div className="border-t border-slate-800 px-6 py-4 flex-shrink-0">
              <button
                disabled={handRefreshSubmitting}
                onClick={async () => {
                  if (handRefreshSubmitting) return;
                  setHandRefreshSubmitting(true);
                  try {
                    await doHandRefresh(activeTeamKey, pvpForcedDiscards);
                    if (isPitchingTeamPhase) {
                      setHandRefreshPhase("cover_between");
                    } else {
                      setHandRefreshPhase(null);
                    }
                  } finally {
                    setHandRefreshSubmitting(false);
                  }
                }}
                className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed text-black font-black text-sm transition-all"
              >
                {handRefreshSubmitting ? "Saving…" : (handDiscards.length + activeDiscards.length) > 0 ? `Trade Back ${handDiscards.length + activeDiscards.length} & Continue` : "Continue →"}
              </button>
            </div>
          </div>
        );
      })()}

      {/* Bluff Values — first-action modal, blocks until pitcher assigns chip values */}
      {showBluffValuesModal && _bluffModalPitcher && (
        <BluffValuesModal
          key={_bluffModalPitcher.id}
          gameId={gameId}
          role={_bluffModalRole}
          pitcher={_bluffModalPitcher}
          pitcherIndex={_bluffModalPitchers.findIndex(p => p.id === _bluffModalPitcher.id) + 1}
          totalPitchers={_bluffModalPitchers.length}
          teamName={!isOnline && _bluffModalRole ? game[`${_bluffModalRole}_team`]?.name : null}
          noTimers={!!game?.no_timers}
          timeoutSecs={game?.card_modal_secs}
          onDone={handleBluffValuesDone}
        />
      )}

      {/* Bluff Values — waiting for opponent to finish assigning (online only) */}
      {isOnline && game?.bluff_values && !showBluffValuesModal && !opponentBluffReady && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70 pointer-events-none">
          <div className="bg-slate-900 border border-amber-500/40 rounded-xl px-8 py-6 text-center max-w-xs shadow-2xl">
            <p className="text-amber-400 font-black text-sm uppercase tracking-widest mb-1">Got Your Number</p>
            <p className="text-slate-300 text-xs">Waiting for opponent to assign pitcher values…</p>
          </div>
        </div>
      )}

      {/* Manager card interactive modals — all share `card_modal_secs` */}
      {studentOfGameModal && (
        <StudentOfGameModal
          drawnCards={studentOfGameModal.drawnCards}
          allCards={allCards}
          timeoutSecs={game?.card_modal_secs}
          onPick={(cardId) => {
            setStudentOfGameModal(null);
            wsSendRef.current?.({ type: "student_of_game_pick", card_id: cardId });
          }}
        />
      )}
      {managersDecisionModal && (
        <ManagersDecisionModal
          eligibleCards={managersDecisionModal.cards}
          timeoutSecs={game?.card_modal_secs}
          onPick={(cardId) => {
            setManagersDecisionModal(null);
            wsSendRef.current?.({ type: "managers_decision_target", card_id: cardId });
          }}
        />
      )}
      {stealingSignalsModal && (
        <StealingSignalsModal
          phase={stealingSignalsModal.phase}
          cardCount={stealingSignalsModal.cardCount}
          revealedCard={stealingSignalsModal.revealedCard}
          allCards={allCards}
          timeoutSecs={game?.card_modal_secs}
          onPick={() => wsSendRef.current?.({ type: "stealing_signals_pick" })}
          onDecide={(action) => {
            setStealingSignalsModal(null);
            wsSendRef.current?.({ type: "stealing_signals_decide", action });
          }}
        />
      )}
      {subterfugeModal && (
        <SubterfugeModal
          availableCardIds={subterfugeModal.availableCardIds}
          allCards={allCards}
          triggeredBy={subterfugeModal.triggeredBy}
          timeoutSecs={game?.card_modal_secs}
          onPick={(cardId) => {
            setSubterfugeModal(null);
            wsSendRef.current?.({ type: "subterfuge_steal_pick", card_id: cardId });
          }}
        />
      )}

      {/* Dialogs */}
      <PitcherSubDialog open={pitcherDialogOpen} onClose={() => setPitcherDialogOpen(false)} pitchers={pitchers} currentPitcher={currentPitcher} onSelect={handlePitcherSub}
        onViewStats={(player) => setPlayerDetailsDialog({ open: true, player: resolveFullPlayer(player) })} />
      <BatterSubDialog open={batterSubDialog.open} onClose={() => setBatterSubDialog({ open: false, player: null, index: null })} playerOut={batterSubDialog.player}
        benchPlayers={(() => { const battingTeam = game.state.half === "top" ? game.away_team : game.home_team; return getBench(battingTeam); })()}
        onSubstitute={handleBatterSubstitute}
        onViewStats={(player) => setPlayerDetailsDialog({ open: true, player: resolveFullPlayer(player) })} />
      <BatterSubDialog open={fielderSubDialog.open} onClose={() => setFielderSubDialog({ open: false, player: null })} playerOut={fielderSubDialog.player}
        benchPlayers={(() => { const fieldingTeam = game.state.half === "top" ? game.home_team : game.away_team; return getBench(fieldingTeam); })()}
        onSubstitute={handleFielderSubstitute} title="Substitute Fielder"
        onViewStats={(player) => setPlayerDetailsDialog({ open: true, player: resolveFullPlayer(player) })} />
      <PlayerDetailsDialog open={playerDetailsDialog.open} onClose={() => setPlayerDetailsDialog({ open: false, player: null })} player={playerDetailsDialog.player} gameStats={boxStats} />
      <VictoryModal open={victoryModal.open} winner={victoryModal.winner} game={game} gameId={gameId} gameStats={boxStats} myRole={myRole} />
      <GameStatsModal open={statsModalOpen} onClose={() => setStatsModalOpen(false)} game={game} gameStats={boxStats} />
      {rundownState && (
        <RundownModal
          rundownState={rundownState}
          myRole={myRole}
          onSelectTarget={(base) => wsSendRef.current?.({ type: "rundown_select_target", base })}
          onFielderGuess={(guesses) => wsSendRef.current?.({ type: "rundown_fielder_guess", guesses })}
          onRunnerGuess={(guess) => wsSendRef.current?.({ type: "rundown_runner_guess", guess })}
        />
      )}
      {/* Render whenever the viewer plays a side (online H2H or human-vs-bot).
          Spectators / bot-vs-bot get no modal — the server picks both sides
          automatically via the bot loop or the 20 s auto-complete timer. */}
      {swapSpitOpen && game && (myRole === "home" || myRole === "away") && (
        <SwapSpitModal
          game={game}
          myRole={myRole}
          allCards={allCards}
          onReady={(cardId) => wsSendRef.current?.({ type: "swap_spit_ready", card_id: cardId })}
          swapProgress={swapSpitProgress}
          clock={swapSpitClock}
        />
      )}
      {streakerInteractive && game && (myRole === "home" || myRole === "away") && (
        <StreakerInteractiveModal
          isInitiator={streakerInteractive.initiator === myRole}
          spawn_edge={streakerInteractive.spawn_edge}
          exit_edge={streakerInteractive.exit_edge}
          seed={streakerInteractive.seed}
          startedAt={streakerInteractive.started_at}
          duration={streakerInteractive.duration}
          initiatorName={(() => {
            const r = streakerInteractive.initiator;
            return (game?.[`${r}_team`] || {}).name || (r ? r.toUpperCase() : "Opponent");
          })()}
          onReportResult={(outcome, meta = {}) => wsSendRef.current?.({ type: "streaker_interactive_result", outcome, elapsed_ms: meta.elapsed_ms })}
          onClose={() => setStreakerInteractive(null)}
        />
      )}
      {hiddenBallPending && isOnline && (
        <HiddenBallModal
          occupiedBases={hiddenBallPending.occupiedBases}
          myRole={myRole}
          fieldingRole={game?.state?.half === "top" ? "home" : "away"}
          timeoutSecs={game?.card_modal_secs}
          onSelectTarget={(base) => {
            setHiddenBallPending(null);
            wsSendRef.current?.({ type: "hidden_ball_target_select", target_base: base });
          }}
        />
      )}
      {/* Phase 6: deactivate confirmation modal. Single instance shared by both
          the hand-card ✕ and any future deactivate entry points. Burns the card
          (no longer modifies rolls, can't be reactivated). The card stays in the
          hand until the next half-inning modal where it's force-discarded. */}
      {deactivateConfirm && (
        <div className="fixed inset-0 z-[1100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDeactivateConfirm(null)}>
          <div className="bg-slate-950 border border-red-500/40 rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-[10px] font-black tracking-widest text-red-400 uppercase mb-2">Deactivate Card</p>
            <p className="font-black text-white text-base mb-1">{deactivateConfirm.name}</p>
            <p className="text-sm text-slate-400 mb-5">
              Deactivating a card will cancel any active effects. It cannot be replayed and will be discarded at the next half-inning.
            </p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setDeactivateConfirm(null)}
                className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 font-bold text-xs transition-all"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeactivate}
                className="px-4 py-2 rounded-lg bg-red-500 hover:bg-red-400 text-black font-black text-xs transition-all"
                data-testid="confirm-deactivate"
              >
                Deactivate
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Dig It Out — discard pile picker */}
      {digItOutOptions && (
        <div className="fixed inset-0 z-[1100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setDigItOutOptions(null)}>
          <div className="bg-slate-950 border border-amber-500/40 rounded-2xl shadow-2xl max-w-md w-full p-5 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <p className="text-[10px] font-black tracking-widest text-amber-400 uppercase">Dig It Out</p>
              {digItOutTimer !== null && (
                <span className={`text-xs font-black tabular-nums ${digItOutTimer <= 5 ? "text-red-400 animate-pulse" : "text-slate-400"}`}>
                  {digItOutTimer}s
                </span>
              )}
            </div>
            <p className="text-sm text-slate-400 mb-3">Pick any one card from the shared discard pile to take into your hand.</p>
            {digItOutOptions.options.length === 0 ? (
              <p className="text-sm text-slate-500 italic text-center py-4">The discard pile is empty.</p>
            ) : (
              <div className="overflow-y-auto flex-1 space-y-1.5 pr-1">
                {digItOutOptions.options.map((opt, i) => (
                  <button
                    key={`${opt.id}-${i}`}
                    onClick={() => {
                      if (!wsSend) return;
                      wsSend({ type: "dig_it_out_select", card_id: opt.id });
                      setDigItOutOptions(null);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg border border-slate-700 bg-slate-900 hover:bg-slate-800 hover:border-amber-500/60 text-white text-sm font-medium transition-all"
                  >
                    {opt.name}
                  </button>
                ))}
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button
                onClick={() => setDigItOutOptions(null)}
                className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 font-bold text-xs transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Hack the Board — compose dialog */}
      {hackTheBoardPending && (
        <div className="fixed inset-0 z-[1100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => { setHackTheBoardPending(null); setHackTheBoardDraft(""); }}>
          <div className="bg-slate-950 border border-violet-500/40 rounded-2xl shadow-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()}>
            <p className="text-[10px] font-black tracking-widest text-violet-400 uppercase mb-1">Hack the Board</p>
            <p className="text-sm text-slate-400 mb-3">Send a nice, little message to your opponent.</p>
            <textarea
              autoFocus
              maxLength={180}
              value={hackTheBoardDraft}
              onChange={(e) => setHackTheBoardDraft(e.target.value)}
              placeholder="Type your message…"
              className="w-full bg-slate-900 border border-slate-700 rounded-lg text-white text-sm px-3 py-2 resize-none h-24 focus:outline-none focus:border-violet-500 placeholder-slate-600"
            />
            <p className="text-[10px] text-slate-600 text-right mt-1 mb-4">{hackTheBoardDraft.length}/180</p>
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => { setHackTheBoardPending(null); setHackTheBoardDraft(""); }}
                className="px-4 py-2 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 font-bold text-xs transition-all"
              >
                Cancel
              </button>
              <button
                disabled={!hackTheBoardDraft.trim()}
                onClick={() => {
                  if (!hackTheBoardDraft.trim() || !wsSend) return;
                  const sent = wsSend({ type: "play_manager_card", card_id: hackTheBoardPending, message: hackTheBoardDraft.trim() });
                  if (!sent) { toast.error("Connection lost — try again"); return; }
                  onInstantCardPlayed(hackTheBoardPending);
                  setHackTheBoardPending(null);
                  setHackTheBoardDraft("");
                }}
                className="px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 disabled:opacity-40 text-white font-black text-xs transition-all"
              >
                Send →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
